    import ReactDOM from 'react-dom';
    import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
// --- Imports do Material UI ---
import { 
    Box, AppBar as MuiAppBar, Toolbar, IconButton, Drawer as MuiDrawer, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, CssBaseline,
    ThemeProvider, createTheme, styled, alpha, Tooltip, Avatar,
    Paper, Typography, Button, CircularProgress, Container, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions 
} from '@mui/material';

// --- Imports de Ícones ---
import { 
    CompareArrows, Visibility, CheckCircle, Description, InfoOutlined, ArrowForward 
} from '@mui/icons-material';
    import ReactFlow, {
        Controls,
        Background,
        ReactFlowProvider,
        useReactFlow,
        MarkerType,
        applyNodeChanges,
        applyEdgeChanges,
        BackgroundVariant,
    } from 'reactflow';
    import ELK from 'elkjs/lib/elk.bundled.js';
 import { 
    ShieldCheck, 
    Users, 
    Wand2, 
    Shapes, 
    FilePenLine, 
    Trash2, 
    Plus, 
    Building, 
    Upload, 
    CheckCircle2, 
    AlertTriangle, 
    RefreshCw, 
    CloudCheck, 
    UserCheck, 
    Briefcase, 
    Puzzle, 
    Building2,
    TrendingUp,    // Novo
    TrendingDown,  // Novo
    Minus,         // Novo
    BarChart3      // Novo
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
    import { SketchPicker } from 'react-color';
    import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
    import 'reactflow/dist/style.css';

    import ManagerEvaluationsModal from '../components/ManagerEvaluationsModal';
    import ManualsModal from '../components/ManualsModal';
import Avaliacao360Workspace from '../components/avaliacao360/Avaliacao360Workspace';
import { build360People } from '../components/avaliacao360/avaliacao360Model';

    // Importando o hook de autenticação centralizado
    import { useAuth } from '../context/AuthContext';

/*
 * Integrado ao index_corrigido.js e à migracao_colaboradores_manuais.sql.
 * Nominais: PUT /organograma/:nodeId/evaluations, com nominalOccupantId retornado
 * pela API. Cadastrados: PUT /users/:userId/evaluations.
 * A identidade da ocupação impede gravar notas de uma pessoa em outra após troca.
 */

// Nome nominal identifica o ocupante no card; não é um ID da tabela de usuários.
const getRegisteredUserId = (data = {}) => {
    const id = data.userId !== undefined ? data.userId : data.user_id;
    if (id == null || id === false || String(id).trim() === '' || String(id) === '0' || /^custom-/i.test(String(id))) {
        return null;
    }
    return id;
};

const getCustomCollaboratorName = (data = {}) => {
    if (getRegisteredUserId(data) !== null) return '';
    if (typeof data.nome_customizado === 'string') return data.nome_customizado.trim();
    // Compatibilidade com cards antigos que ainda tenham o ID fictício em memória.
    const oldId = data.userId !== undefined ? data.userId : data.user_id;
    if (/^custom-/i.test(String(oldId))) {
        const oldName = data.nome || data.name;
        return typeof oldName === 'string' && !['Vaga Aberta', 'Vaga Pendente'].includes(oldName)
            ? oldName.trim() : '';
    }
    return '';
};

const hasAssignedCollaborator = (data = {}) => (
    getRegisteredUserId(data) !== null || getCustomCollaboratorName(data) !== ''
);

const normalizeCollaboratorData = (data = {}) => {
    const userId = getRegisteredUserId(data);
    const customName = getCustomCollaboratorName(data);
    const occupied = userId !== null || customName !== '';
    const name = customName || (occupied ? (data.nome || data.name || '') : 'Vaga Aberta');
    return {
        ...data,
        userId,
        user_id: userId,
        nome_customizado: customName || null,
        nominalOccupantId: customName ? (data.nominalOccupantId ?? data.nominal_evaluation_data?.occupantId ?? null) : null,
        isCustom: customName !== '',
        name,
        nome: occupied ? name : null,
        role: data.cargo ?? data.role,
    };
};

const jobDescriptionFields = {
    cargo: 'cargo',
    funcao: 'funcao',
    descricaoSumaria: 'descricao_sumaria',
    atividades: 'atividades',
    competenciasTecnicasObrigatorias: 'competencias_tecnicas_obrigatorias',
    competenciasTecnicasDesejaveis: 'competencias_tecnicas_desejaveis',
    competenciasTecnicas: 'competencias_tecnicas',
    outrasCompetencias: 'outras_competencias',
    competenciasComportamentais: 'competencias_comportamentais',
    isGerencial: 'is_gerencial',
};

const pickJobDescriptionData = (data = {}) => {
    const picked = {};
    Object.entries(jobDescriptionFields).forEach(([field, alias]) => {
        const value = data[field] !== undefined ? data[field] : data[alias];
        if (value !== undefined) picked[field] = value;
    });
    return picked;
};

const mergeJobDescriptionData = (currentData, submittedData, responseData) => {
    const returnedData = responseData?.node || responseData || {};
    // Uma resposta de JD pode conter nome/user_id nulos vindos de um LEFT JOIN.
    // Somente campos do cargo podem alterar este estado; o ocupante é preservado.
    return normalizeCollaboratorData({
        ...currentData,
        ...pickJobDescriptionData(submittedData),
        ...pickJobDescriptionData(returnedData),
    });
};

const getEvaluationEndpoint = (node) => {
    if (!node?.id || !hasAssignedCollaborator(node.data)) {
        throw new Error('Preencha a vaga com um colaborador antes de avaliar.');
    }
    const userId = getRegisteredUserId(node.data);
    return userId !== null
        ? `/users/${encodeURIComponent(userId)}/evaluations`
        : `/organograma/${encodeURIComponent(node.id)}/evaluations`;
};

// Aceita REACT_APP_API_URL com ou sem /api no final.
const getOrganogramApiUrl = path => {
    const base = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const apiBase = base.endsWith('/api') ? base : `${base}/api`;
    return `${apiBase}/${String(path).replace(/^\/+/, '')}`;
};

const ORGANOGRAM_NODE_TYPES = {};
const ORGANOGRAM_EDGE_TYPES = {};

// Só monta o React Flow quando seu elemento pai já possui dimensões.
// A troca Organograma/Lista usa visibility, preservando tamanho e zoom.
const SizedOrganogramFlow = props => {
    const hostRef = useRef(null);
    const [hasSize, setHasSize] = useState(false);
    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;
        const measure = () => setHasSize(host.clientWidth > 0 && host.clientHeight > 0);
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(host);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, []);
    return <div ref={hostRef} style={{ width: '100%', height: '100%', minWidth: 1, minHeight: 280 }}>
        {hasSize && <ReactFlow {...props} nodeTypes={ORGANOGRAM_NODE_TYPES} edgeTypes={ORGANOGRAM_EDGE_TYPES} />}
    </div>;
};

// Visualização de colaboradores: usa somente os nós já autorizados na página.
const PeopleViewIcon = ({ name, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        {name === 'graph' && <><rect x="9" y="2" width="6" height="5" rx="1" /><path d="M12 7v5M5 16v-4h14v4" /><rect x="2" y="16" width="6" height="5" rx="1" /><rect x="16" y="16" width="6" height="5" rx="1" /></>}
        {name === 'list' && <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M8 8h.01M12 8h5M8 12h.01M12 12h5M8 16h.01M12 16h5" /></>}
        {name === 'search' && <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>}
        {name === 'people' && <><circle cx="9" cy="7" r="3" /><path d="M3 21v-3a6 6 0 0 1 12 0v3M17 4a3 3 0 0 1 0 6M21 21v-3a6 6 0 0 0-3-5" /></>}
        {name === 'area' && <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>}
        {name === 'vacancy' && <><rect x="3" y="7" width="18" height="14" rx="2" /><path d="M8 7V3h8v4M3 12h18M10 12v3h4v-3" /></>}
        {name === 'left' && <path d="m14 6-6 6 6 6" />}
        {name === 'right' && <path d="m10 6 6 6-6 6" />}
        {name === 'down' && <path d="m6 9 6 6 6-6" />}
        {name === 'close' && <path d="m6 6 12 12M6 18 18 6" />}
    </svg>
);

const peopleListStyles = `
    .gpc-team-switchbar, .gpc-people-view, .gpc-team-switchbar *, .gpc-people-view * { box-sizing: border-box; }
    .gpc-team-switchbar { display:flex; align-items:center; justify-content:space-between; gap:16px; padding:16px 24px; border-bottom:1px solid #e2e8f0; background:#fff; flex-shrink:0; }
    .gpc-team-context { min-width:0; }
    .gpc-team-context h2 { margin:0 0 4px; font-size:18px; line-height:1.3; color:#172033; letter-spacing:-.35px; }
    .gpc-team-context p { margin:0; color:#64748b; font-size:12px; line-height:1.4; overflow-wrap:anywhere; }
    .gpc-team-switch { display:flex; padding:4px; gap:4px; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:10px; flex-shrink:0; }
    .gpc-team-switch button { display:flex; align-items:center; justify-content:center; gap:8px; border:1px solid transparent; background:transparent; color:#526176; border-radius:7px; padding:9px 14px; min-height:40px; cursor:pointer; font-family:inherit; font-size:13px; font-weight:600; }
    .gpc-team-switch button[aria-pressed="true"] { background:#fff; color:#172033; border-color:#e2e8f0; box-shadow:0 1px 3px #0f172a0d; }
    .gpc-team-switch button:hover { color:#172033; background:#fff; }
    .gpc-team-switch button:focus-visible, .gpc-people-view button:focus-visible, .gpc-people-view select:focus-visible, .gpc-people-view input:focus-visible { outline:3px solid #2563eb; outline-offset:3px; }
    .gpc-people-view { height:100%; min-height:0; display:flex; flex-direction:column; background:#f5f7fa; padding:20px 24px 16px; color:#334155; font-size:14px; }
    .gpc-people-summary { display:flex; align-items:center; gap:10px 22px; flex-wrap:wrap; margin-bottom:16px; flex-shrink:0; }
    .gpc-people-summary span { display:inline-flex; gap:7px; align-items:center; font-size:12px; color:#64748b; line-height:1.5; }
    .gpc-people-summary strong { color:#172033; font-size:16px; }
    .gpc-people-summary svg { color:#64748b; }
    .gpc-people-summary .gpc-people-scope { margin-left:auto; font-size:11px; }
    .gpc-people-panel { background:#fff; border:1px solid #dfe5ee; border-radius:12px; display:flex; flex-direction:column; flex:1; min-height:0; overflow:hidden; box-shadow:0 2px 6px #0f172a03; }
    .gpc-people-tools { padding:16px 18px; display:flex; flex-wrap:wrap; align-items:center; gap:12px; border-bottom:1px solid #e2e8f0; flex-shrink:0; }
    .gpc-people-search { position:relative; flex:1; min-width:180px; max-width:440px; }
    .gpc-people-search > svg { position:absolute; top:12px; left:12px; pointer-events:none; color:#64748b; }
    .gpc-people-search input { width:100%; min-height:42px; padding:10px 40px; border:1px solid #cbd5e1; border-radius:8px; color:#172033; background:#fff; font:inherit; font-size:13px; }
    .gpc-people-search input::placeholder { color:#64748b; opacity:1; }
    .gpc-people-search input::-webkit-search-cancel-button { display:none; }
    .gpc-people-clear { position:absolute; right:4px; top:4px; width:34px; height:34px; padding:0; display:grid; place-items:center; border:0; background:transparent; color:#64748b; border-radius:5px; cursor:pointer; }
    .gpc-people-sort { margin-left:auto; display:flex; gap:8px; align-items:center; font-size:12px; color:#64748b; }
    .gpc-people-view select { border:1px solid #cbd5e1; border-radius:7px; min-height:40px; background:#fff; color:#334155; font-family:inherit; font-size:12px; padding:8px 10px; cursor:pointer; max-width:100%; }
    .gpc-people-scroll { overflow:auto; flex:1; min-height:0; overscroll-behavior:contain; }
    .gpc-people-table { width:100%; border-collapse:separate; border-spacing:0; text-align:left; table-layout:fixed; }
    .gpc-people-table caption, .gpc-people-sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    .gpc-people-table thead th { position:sticky; top:0; z-index:1; background:#f8fafc; padding:12px 14px; border-bottom:1px solid #e2e8f0; color:#64748b; font-size:11px; font-weight:600; letter-spacing:.25px; }
    .gpc-people-table th:nth-child(1) { width:26%; }
    .gpc-people-table th:nth-child(2) { width:19%; }
    .gpc-people-table th:nth-child(3) { width:15%; }
    .gpc-people-table th:nth-child(4) { width:15%; }
    .gpc-people-table th:nth-child(5) { width:13%; }
    .gpc-people-table th:nth-child(6) { width:146px; text-align:right; }
    .gpc-people-row td { background:var(--people-area-tint, #f8fafc); border-bottom:1px solid #e8edf3; padding:17px 14px; vertical-align:middle; overflow-wrap:anywhere; transition:background-color .15s; font-size:13px; }
    .gpc-people-row td:first-child { border-left:4px solid var(--people-area-color, #94a3b8); }
    .gpc-people-row:last-child td { border-bottom:0; }
    .gpc-people-row:hover td, .gpc-people-row:focus-within td { background:var(--people-area-hover, #f1f5f9); }
    .gpc-people-person { display:flex; align-items:center; gap:12px; min-width:0; }
    .gpc-people-avatar { width:44px; height:44px; border-radius:50%; background:#e2e8f0; color:#334155; border:2px solid white; box-shadow:0 0 0 1px #dce3ed; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; font-weight:700; font-size:13px; }
    .gpc-people-avatar img { width:100%; height:100%; object-fit:cover; object-position:center; }
    .gpc-people-person-info { min-width:0; }
    .gpc-people-name { padding:0; border:0; background:transparent; text-align:left; color:#172033; font:inherit; font-size:13px; font-weight:650; line-height:1.45; cursor:pointer; overflow-wrap:anywhere; }
    .gpc-people-name:hover { color:#1d4ed8; text-decoration:underline; text-underline-offset:3px; }
    .gpc-people-badge { display:inline-flex; width:fit-content; margin-top:5px; border-radius:4px; background:#fff; border:1px solid #d8e0eb; padding:2px 6px; font-size:10px; color:#526176; }
    .gpc-people-primary { display:block; color:#334155; font-weight:500; line-height:1.5; }
    .gpc-people-secondary { display:block; color:#64748b; font-size:11px; line-height:1.5; margin-top:3px; }
    .gpc-people-area { display:flex; align-items:center; gap:7px; font-size:12px; line-height:1.5; }
    .gpc-people-area-dot { width:9px; height:9px; border-radius:3px; flex-shrink:0; background:var(--people-area-color, #94a3b8); box-shadow:0 0 0 1px #0f172a20; }
    .gpc-people-actions { display:flex; align-items:center; justify-content:flex-end; gap:6px; }
    .gpc-people-action { display:inline-flex; align-items:center; justify-content:center; gap:6px; min-height:36px; border:1px solid #cbd5e1; background:#fff; border-radius:7px; color:#475569; padding:8px 10px; font-family:inherit; font-size:12px; cursor:pointer; white-space:nowrap; }
    .gpc-people-action:hover { background:#f8fafc; border-color:#94a3b8; color:#172033; }
    .gpc-people-action:disabled { opacity:.45; cursor:default; }
    .gpc-people-action-icon { width:36px; padding:8px; flex-shrink:0; }
    .gpc-people-footer { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 18px; border-top:1px solid #e2e8f0; font-size:12px; color:#64748b; flex-wrap:wrap; flex-shrink:0; }
    .gpc-people-pagination { display:flex; align-items:center; gap:8px; }
    .gpc-people-footer select { min-height:34px; padding:6px; }
    .gpc-people-empty { flex:1; min-height:180px; display:flex; align-items:center; justify-content:center; flex-direction:column; text-align:center; padding:32px 20px; gap:12px; color:#64748b; }
    .gpc-people-empty > svg { color:#94a3b8; }
    .gpc-people-empty h3 { margin:0; font-size:16px; color:#334155; }
    .gpc-people-empty p { margin:0; font-size:13px; line-height:1.6; max-width:440px; }
    .gpc-people-loading { width:26px; height:26px; border-radius:50%; border:3px solid #e2e8f0; border-top-color:#475569; animation:gpc-people-spin .8s linear infinite; }
    @keyframes gpc-people-spin { to { transform:rotate(360deg); } }
    @media (max-width:1100px) {
        .gpc-people-view { padding:16px; }
        .gpc-people-table th:nth-child(4), .gpc-people-table td:nth-child(4) { display:none; }
        .gpc-people-table th:nth-child(1) { width:28%; }
        .gpc-people-table th:nth-child(2) { width:22%; }
        .gpc-people-table th:nth-child(3) { width:18%; }
        .gpc-people-table th:nth-child(5) { width:14%; }
        .gpc-people-table th:nth-child(6) { width:130px; }
        .gpc-people-row td { padding:14px 10px; }
        .gpc-people-person { gap:9px; }
    }
    @media (max-width:767px) {
        .gpc-team-switchbar { padding:12px 16px; flex-wrap:wrap; gap:12px; }
        .gpc-team-context h2 { font-size:16px; }
        .gpc-team-switch { width:100%; }
        .gpc-team-switch button { flex:1; padding:8px; }
        .gpc-people-view { padding:12px; }
        .gpc-people-summary { gap:6px 14px; margin-bottom:12px; }
        .gpc-people-summary .gpc-people-scope { display:none; }
        .gpc-people-tools { padding:12px; gap:10px; }
        .gpc-people-search { flex-basis:100%; max-width:none; min-width:0; }
        .gpc-people-sort { margin:0; width:100%; justify-content:space-between; }
        .gpc-people-sort select { flex:1; max-width:190px; }
        .gpc-people-table, .gpc-people-table tbody { display:block; }
        .gpc-people-table thead { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; }
        .gpc-people-row { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); padding:14px; gap:14px 12px; border-left:4px solid var(--people-area-color, #94a3b8); border-bottom:1px solid #e2e8f0; background:var(--people-area-tint, #f8fafc); }
        .gpc-people-row td, .gpc-people-table td:nth-child(4) { display:block; padding:0; border:0; background:transparent; min-width:0; }
        .gpc-people-row td:first-child { border:0; grid-column:1 / -1; }
        .gpc-people-row td:last-child { grid-column:1 / -1; padding-top:12px; border-top:1px solid #dfe5ee; }
        .gpc-people-row td[data-label]::before { content:attr(data-label); display:block; font-size:10px; color:#64748b; margin-bottom:4px; }
        .gpc-people-row:hover td, .gpc-people-row:focus-within td { background:transparent; }
        .gpc-people-name { font-size:14px; }
        .gpc-people-actions { justify-content:flex-end; }
        .gpc-people-actions .gpc-people-action { min-height:40px; }
        .gpc-people-footer { padding:12px; gap:10px; }
        .gpc-people-pagination { margin-left:auto; }
    }
    @media (prefers-reduced-motion:reduce) { .gpc-people-view * { animation:none !important; transition:none !important; } }
`;

const normalizePeopleSearch = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const getPeopleAreaPalette = value => {
    let hex = typeof value === 'string' ? value.trim() : '';
    if (!/^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.test(hex)) hex = '#94a3b8';
    if (hex.length === 4) hex = '#' + hex.slice(1).split('').map(char => char + char).join('');
    const channels = [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16));
    return { color: hex, tint: `rgba(${channels.join(',')},0.055)`, hover: `rgba(${channels.join(',')},0.11)` };
};

const formatPeopleDate = value => {
    if (!value) return '';
    const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
};

const buildPeopleRows = (visibleNodes, allNodes, allEdges) => {
    const nodesById = new Map(allNodes.map(node => [String(node.id), node]));
    const parentById = new Map(allEdges.map(edge => [String(edge.target), nodesById.get(String(edge.source))]));
    return visibleNodes.filter(node => hasAssignedCollaborator(node.data)).map(node => {
        const data = normalizeCollaboratorData(node.data);
        const parent = parentById.get(String(node.id));
        const parentData = parent ? normalizeCollaboratorData(parent.data) : null;
        const manager = parentData
            ? (hasAssignedCollaborator(parentData) ? parentData.name : (parentData.cargo || 'Vaga em aberto'))
            : 'Sem vínculo definido';
        const area = data.areaName || data.area || 'Área não definida';
        const role = data.cargo || data.role || 'Cargo não definido';
        const history = Array.isArray(data.evaluationHistory) ? data.evaluationHistory : [];
        return {
            node, data, name: data.name || 'Nome não informado', area, role, manager,
            managerVacant: Boolean(parentData && !hasAssignedCollaborator(parentData)),
            palette: getPeopleAreaPalette(data.color),
            lastEvaluation: formatPeopleDate(data.lastEvaluationDate || history[0]?.date),
            nextEvaluation: formatPeopleDate(data.nextEvaluationDate),
            searchText: normalizePeopleSearch([data.name, role, data.funcao, area, manager].join(' ')),
        };
    });
};

const filterAndSortPeopleRows = (rows, query, sortBy) => {
    const terms = normalizePeopleSearch(query).split(/\s+/).filter(Boolean);
    const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });
    const field = sortBy === 'area' ? 'area' : sortBy === 'role' ? 'role' : 'name';
    return rows.filter(row => terms.every(term => row.searchText.includes(term))).sort((a, b) =>
        collator.compare(a[field], b[field]) || collator.compare(a.name, b.name) || collator.compare(String(a.node.id), String(b.node.id))
    );
};

const PeopleViewSwitch = ({ value, onChange, unitName, hasAreaFilter }) => (
    <div className="gpc-team-switchbar">
        <div className="gpc-team-context">
            <h2>{value === 'list' ? 'Colaboradores' : 'Organograma'}</h2>
            <p>{unitName || 'Selecione uma unidade'}{hasAreaFilter ? ' · Áreas filtradas' : ''}</p>
        </div>
        <div className="gpc-team-switch" role="group" aria-label="Visualização da equipe">
            <button type="button" aria-pressed={value === 'organogram'} aria-controls="gpc-organogram-panel" onClick={() => onChange('organogram')}>
                <PeopleViewIcon name="graph" /> Organograma
            </button>
            <button type="button" aria-pressed={value === 'list'} aria-controls="gpc-collaborators-panel" onClick={() => onChange('list')}>
                <PeopleViewIcon name="list" /> Lista
            </button>
        </div>
    </div>
);

const PeopleListAvatar = ({ src, name }) => {
    const [failedSrc, setFailedSrc] = useState(null);
    const words = name.trim().split(/\s+/).filter(Boolean);
    const initials = (words[0]?.[0] || '?') + (words.length > 1 ? words[words.length - 1][0] : '');
    return (
        <span className="gpc-people-avatar" aria-hidden="true">
            {src && src !== failedSrc
                ? <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailedSrc(src)} />
                : initials.toLocaleUpperCase('pt-BR')}
        </span>
    );
};

const CollaboratorListView = ({
    visibleNodes, allNodes, allEdges, unitId, scopeKey, hasAreaFilter,
    loading, error, onRetry, onClearFilters, onShowOrganogram,
    onOpenActions, onOpenDetails, activeNodeId, canEdit, onCreateVacancy,
}) => {
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const scrollRef = useRef(null);
    const rows = useMemo(() => buildPeopleRows(visibleNodes, allNodes, allEdges), [visibleNodes, allNodes, allEdges]);
    const matchingRows = useMemo(() => filterAndSortPeopleRows(rows, query, sortBy), [rows, query, sortBy]);
    const areaCount = new Set(rows.map(row => row.data.areaName || row.data.area).filter(Boolean)).size;
    const vacantCount = visibleNodes.filter(node => !hasAssignedCollaborator(node.data) && node.data.status !== 'pending').length;
    const pages = Math.max(1, Math.ceil(matchingRows.length / pageSize));
    const currentPage = Math.min(page, pages);
    const start = (currentPage - 1) * pageSize;
    const pageRows = matchingRows.slice(start, start + pageSize);
    const isFiltered = Boolean(query.trim() || hasAreaFilter);

    useEffect(() => { setQuery(''); setPage(1); }, [unitId]);
    useEffect(() => { setPage(1); }, [scopeKey]);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        onOpenActions(null);
    }, [query, sortBy, currentPage, pageSize, scopeKey, onOpenActions]);

    const clearFilters = () => { setQuery(''); setPage(1); onClearFilters(); };

    return (
        <div className="gpc-people-view" aria-busy={loading}>
            {!loading && !error && <div className="gpc-people-summary" aria-label="Resumo da equipe exibida">
                <span><PeopleViewIcon name="people" size={16} /><strong>{rows.length}</strong> {rows.length === 1 ? 'colaborador' : 'colaboradores'}</span>
                <span><PeopleViewIcon name="area" size={15} /><strong>{areaCount}</strong> {areaCount === 1 ? 'área' : 'áreas'}</span>
                <span><PeopleViewIcon name="vacancy" size={16} /><strong>{vacantCount}</strong> {vacantCount === 1 ? 'vaga aberta' : 'vagas abertas'}</span>
                <span className="gpc-people-scope">{hasAreaFilter ? 'Nas áreas selecionadas e subordinados, se incluídos' : 'Na equipe da unidade'}</span>
            </div>}
            <div className="gpc-people-panel">
                <div className="gpc-people-tools">
                    <div className="gpc-people-search">
                        <PeopleViewIcon name="search" />
                        <input type="search" value={query} aria-label="Buscar por nome, cargo, função, área ou gestor" placeholder="Buscar nome, cargo ou área…"
                            onChange={event => { setQuery(event.target.value); setPage(1); }} />
                        {query && <button type="button" className="gpc-people-clear" aria-label="Limpar busca" onClick={() => { setQuery(''); setPage(1); }}><PeopleViewIcon name="close" size={16} /></button>}
                    </div>
                    <label className="gpc-people-sort">Ordenar por
                        <select value={sortBy} onChange={event => { setSortBy(event.target.value); setPage(1); }}>
                            <option value="name">Nome: A–Z</option><option value="area">Área: A–Z</option><option value="role">Cargo: A–Z</option>
                        </select>
                    </label>
                </div>
                <div className="gpc-people-scroll" ref={scrollRef} onScroll={() => { if (activeNodeId) onOpenActions(null); }}>
                    {loading ? <div className="gpc-people-empty" role="status"><span className="gpc-people-loading" aria-hidden="true" /><p>Carregando colaboradores…</p></div>
                    : error ? <div className="gpc-people-empty" role="alert"><PeopleViewIcon name="people" size={32} /><h3>Não foi possível carregar a equipe</h3><p>{error}</p><button type="button" className="gpc-people-action" onClick={onRetry}>Tentar novamente</button></div>
                    : matchingRows.length === 0 ? <div className="gpc-people-empty" role="status">
                        <PeopleViewIcon name={isFiltered ? 'search' : 'people'} size={36} />
                        <h3>{isFiltered ? 'Nenhum colaborador encontrado' : 'Sua equipe aparecerá aqui'}</h3>
                        <p>{isFiltered ? 'Revise a busca ou os filtros de área para encontrar a pessoa que procura.' : visibleNodes.length === 0 && canEdit ? 'Crie a primeira vaga. Em seguida, selecione um colaborador ou digite o nome dele.' : 'A lista mostra os colaboradores vinculados às vagas desta unidade. Preencha uma das vagas no organograma.'}</p>
                        <button type="button" className="gpc-people-action" onClick={isFiltered ? clearFilters : visibleNodes.length === 0 && canEdit ? onCreateVacancy : () => onShowOrganogram()}>{isFiltered ? 'Limpar filtros' : visibleNodes.length === 0 && canEdit ? 'Adicionar primeiro colaborador' : canEdit ? 'Preencher vaga no organograma' : 'Ir para o organograma'}</button>
                    </div> : <table className="gpc-people-table">
                        <caption>Colaboradores da equipe. A cor de cada linha identifica a área.</caption>
                        <thead><tr><th scope="col">Colaborador</th><th scope="col">Cargo / função</th><th scope="col">Área</th><th scope="col">Gestor direto</th><th scope="col">Última avaliação</th><th scope="col">Ações</th></tr></thead>
                        <tbody>{pageRows.map(row => <tr key={row.node.id} className="gpc-people-row" style={{ '--people-area-color': row.palette.color, '--people-area-tint': row.palette.tint, '--people-area-hover': row.palette.hover }}>
                            <td><div className="gpc-people-person">
                                <PeopleListAvatar src={row.data.profile_picture_url || row.data.avatarUrl} name={row.name} />
                                <div className="gpc-people-person-info">
                                    <button type="button" className="gpc-people-name" onClick={() => onOpenDetails(row.node)} aria-label={`Ver descrição do cargo de ${row.name}`}>{row.name}</button>
                                    {row.data.isGerencial === true && <div className="gpc-people-badge">Gestor</div>}
                                </div>
                            </div></td>
                            <td data-label="Cargo / função"><span className="gpc-people-primary">{row.role}</span>{row.data.funcao && row.data.funcao !== row.role && <span className="gpc-people-secondary">{row.data.funcao}</span>}</td>
                            <td data-label="Área"><span className="gpc-people-area"><span className="gpc-people-area-dot" aria-hidden="true" />{row.area}</span></td>
                            <td data-label="Gestor direto"><span className="gpc-people-primary">{row.manager}</span>{row.managerVacant && <span className="gpc-people-secondary">Vaga de liderança em aberto</span>}</td>
                            <td data-label="Última avaliação"><span className="gpc-people-primary">{row.lastEvaluation || 'Sem avaliação registrada'}</span>{row.nextEvaluation && <span className="gpc-people-secondary">Próxima: {row.nextEvaluation}</span>}</td>
                            <td><div className="gpc-people-actions">
                                <button type="button" className="gpc-people-action gpc-people-action-icon" onClick={() => onShowOrganogram(row.node)} title="Localizar no organograma" aria-label={`Localizar ${row.name} no organograma`}><PeopleViewIcon name="graph" size={16} /></button>
                                <button type="button" className="gpc-people-action" onClick={event => onOpenActions(event, row.node)} aria-label={`Ações de ${row.name}`} aria-haspopup="menu" aria-controls={String(activeNodeId) === String(row.node.id) ? 'gpc-node-actions-menu' : undefined} aria-expanded={String(activeNodeId) === String(row.node.id)}>Ações <PeopleViewIcon name="down" size={13} /></button>
                            </div></td>
                        </tr>)}</tbody>
                    </table>}
                </div>
                {!loading && !error && <div className="gpc-people-footer">
                    <span role="status" aria-live="polite">{matchingRows.length ? `${start + 1}–${Math.min(start + pageSize, matchingRows.length)} de ${matchingRows.length}` : '0'} {matchingRows.length === 1 ? 'colaborador' : 'colaboradores'}{query.trim() ? ` · ${rows.length} no total` : ''}</span>
                    <div className="gpc-people-pagination">
                        <select value={pageSize} aria-label="Colaboradores por página" onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}><option value={15}>15 por página</option><option value={30}>30 por página</option><option value={60}>60 por página</option></select>
                        <button type="button" className="gpc-people-action gpc-people-action-icon" disabled={currentPage === 1} aria-label="Página anterior" onClick={() => setPage(currentPage - 1)}><PeopleViewIcon name="left" size={14} /></button>
                        <span aria-label={`Página ${currentPage} de ${pages}`}>{currentPage} / {pages}</span>
                        <button type="button" className="gpc-people-action gpc-people-action-icon" disabled={currentPage === pages} aria-label="Próxima página" onClick={() => setPage(currentPage + 1)}><PeopleViewIcon name="right" size={14} /></button>
                    </div>
                </div>}
            </div>
        </div>
    );
};


// ============================================================================
// 1. UTILITÁRIOS E ESTILOS GLOBAIS (TOPO DO ARQUIVO)
// ============================================================================
const ModalPortal = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
};

const modalOverlayStyle = { 
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
    backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', 
    justifyContent: 'center', alignItems: 'center', zIndex: 1800, 
    backdropFilter: 'blur(4px)', padding: '24px' 
};

const modalContentStyle = { 
    background: 'white', padding: '32px', borderRadius: '12px', 
    width: '90%', maxWidth: '800px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
};

const professionalModalContentStyle = { 
    background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)', 
    borderRadius: '16px', width: '95%', maxWidth: '1100px', 
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)', display: 'flex', 
    flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh'
};

const modalHeaderStyle = { padding: '28px 32px', color: 'white', position: 'relative' };
const modalBodyStyle = { padding: '32px', overflowY: 'auto', flexGrow: 1, backgroundColor: '#ffffff' };
    // --- FUNÇÃO DE FORMATAÇÃO DE DATA/HORA ---
    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            if (isNaN(date.getTime())) {
                return '';
            }
            const options = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            };
            return new Intl.DateTimeFormat('pt-BR', options).format(date);
        } catch (error) {
            console.error("Erro ao formatar data:", error, isoString);
            return '';
        }
    };


    // --- ESTILO GLOBAL ---
    const globalResetStyle = `
        html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            height: 100% !important;
            width: 100% !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
        }
        @keyframes pulse-glow-focus {
          0%, 100% {
            box-shadow: 0 0 8px 2px rgba(255, 131, 79, 0.7);
            border-color: #FF834F !important;
          }
          50% {
            box-shadow: 0 0 20px 8px rgba(255, 131, 79, 0.7);
            border-color: #FF834F !important;
          }
        }

        /* ✨ REVERTER PARA A ANIMAÇÃO VERMELHA ORIGINAL ✨ */
        @keyframes pulse-glow-vacant {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 5px 0px rgba(255, 0, 0, 0.7);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.7);
          }
        }

        /* ✨ ADICIONE ESTA NOVA ANIMAÇÃO ABAIXO ✨ */
        @keyframes pulse-glow-pending {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 5px 0px rgba(217, 119, 6, 0.7); /* Laranja */
          }
          50% {
            transform: scale(1.1);
        	   box-shadow: 0 0 15px 5px rgba(217, 119, 6, 0.7); /* Laranja */
          }
        }
        /* ✨ FIM DA ADIÇÃO ✨ */

        textarea, input {
          font-family: inherit;
        }
    `;


    // --- ÍCONES ---
    const SidebarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-boxes"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.27 6.96 8.73 5.05 8.73-5.05"/><path d="M12 22.08V12"/></svg>;
    const DragIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-grip-vertical"><circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/></svg>;
    const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><line x1="18" x2="22" y1="2" y2="6"/><path d="M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z"/></svg>;
    const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
    const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>;
    const ChevronDownIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>;
    const ChevronUpIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>;
    const MenuIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>;
    const XIcon = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
    const MoreOptionsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="grey" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>;
    const EvaluationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard-check"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/></svg>;
    const ChartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-area-chart"><path d="M3 3v18h18"/><path d="M7 12v5h12V8l-5 5-4-4Z"/></svg>;
    const DisciplinaryHistoryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-gavel"><path d="m14 13-7.5 7.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L11 10"/><path d="m16 16 6-6"/><path d="m8 8 6-6"/><path d="m9 7 4-4"/><path d="m13 11 4-4"/><path d="m18 12 -1.5 1.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L15 9"/></svg>;
    const PDIIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-target"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    const HelpCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>;
    const LayoutVerticalIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="20" x="3" y="2" rx="2" /><rect width="7" height="7" x="14" y="2" rx="2" /><rect width="7" height="9" x="14" y="13" rx="2" /></svg>;
    const LayoutHorizontalIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="7" x="2" y="3" rx="2" /><rect width="7" height="9" x="2" y="14" rx="2" /><rect width="9" height="9" x="13" y="14" rx="2" /></svg>;
    const InboxIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
    );


    // --- ÍCONE ANIMADO ---
    const AnimatedExclamationIcon = () => (
        <div style={{
            position: 'absolute', top: '-10px', right: '-10px', width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 'bold', fontSize: '16px', animation: 'pulse-glow-vacant 2s infinite ease-in-out', zIndex: 10,
        }}>!</div>
    );

    // --- COMPONENTE AVATAR ---
    const CustomAvatar = ({ src, name }) => {
        const [error, setError] = useState(!src);

        useEffect(() => {
            setError(!src);
        }, [src]);

        const handleImageError = () => {
            setError(true);
        };

        const initial = name ? name.charAt(0).toUpperCase() : '?';
        const avatarSize = '40px';
        const avatarStyle = {
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'white',
            objectFit: 'cover',
            objectPosition: 'center',  // ✅ ADICIONE ISTO
            flexShrink: 0, 
        };

        const colors = ['#f87171', '#fb923c', '#fbbf24', '#a3e635', '#4ade80', '#34d399', '#2dd4bf', '#22d3ee', '#38bdf8', '#60a5fa', '#818cf8', '#a78bfa', '#c084fc', '#e879f9', '#f472b6'];
        const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
        const backgroundColor = colors[colorIndex];

        if (error) {
            return (
                <div style={{ ...avatarStyle, backgroundColor }}>
                    {initial}
                </div>
            );
        }

        return (
            <img
                src={src}
                alt={name || 'Avatar'}
                style={avatarStyle}
                onError={handleImageError}
            />
        );
    };


    // --- ESTILOS ---
    const isEmbedded = window.self !== window.top;
const TOP_NAVBAR_HEIGHT = isEmbedded ? 0 : 65; 

const mainContainerStyle = { 
    top: `${TOP_NAVBAR_HEIGHT}px`,
    left: 0, 
    right: 0, 
    bottom: 0, 
    display: 'flex',         
    width: '100%', 
    height: `calc(100dvh - ${TOP_NAVBAR_HEIGHT}px)`,
    fontFamily: "Inter, sans-serif",
    position: 'fixed',
    overflow: 'hidden'       
};

    // ===== ESTILOS PROFISSIONAIS DA SIDEBAR =====
    const sidebarStyle = {
        height: '100%',
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
        borderRight: '2px solid #e2e8f0',
        transition: 'width 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '4px 0px 20px rgba(0, 0, 0, 0.08)',
        zIndex: 1100,
        overflow: 'hidden',      // ✅ ADICIONE ISTO
        flexShrink: 0            // ✅ ADICIONE ISTO
    };



    const sidebarHeaderStyle = {
        padding: '28px 24px',
        borderBottom: '2px solid #e2e8f0',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        fontWeight: '700',
        fontSize: '1.1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexShrink: 0,           // ✅ IMPORTANTE (já tem?)
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',      // ✅ ADICIONE ISTO também
        whiteSpace: 'nowrap'     // ✅ ADICIONE ISTO para texto não quebrar
    };


    const sidebarContentStyle = {
        padding: '20px 16px',
        overflowY: 'auto',
        overflowX: 'hidden',     // ✅ ADICIONE ISTO
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        backgroundColor: '#fafbfc',
        minWidth: 0,              // ✅ ADICIONE ISTO (importantíssimo!)
        minHeight: 0              // ✅ ADICIONE ISTO também
    };


    const draggableNodeStyle = {
        padding: '16px',
        borderRadius: '12px',
        background: 'white',
        border: '2px solid #e2e8f0',
        cursor: 'grab',
        textAlign: 'left',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',      // ✅ IMPORTANTE
        flexShrink: 0,           // ✅ ADICIONE ISTO
        minWidth: 0              // ✅ ADICIONE ISTO
    };


    const draggableNodeHoverStyle = {
        backgroundColor: '#f0f9ff',
        borderColor: '#3b82f6',
        boxShadow: '0 8px 16px rgba(59, 130, 246, 0.15)',
        transform: 'translateY(-4px)'
    };

    const emptyStateStyle = {
        textAlign: 'center',
        color: '#94a3b8',
        padding: '40px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexGrow: 1,
        gap: '16px',
        minWidth: 0,             // ✅ ADICIONE ISTO
        overflow: 'hidden'       // ✅ ADICIONE ISTO
    };


    // ===== ÍCONE PARA SIDEBAR VAZIA =====
    const EmptyBoxIcon = () => (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
    );
    const reactFlowWrapperStyle = {
        flexGrow: 1,
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        backgroundColor: 'transparent'  // ✅ ADICIONE ISTO para remover qualquer cor de fundo
    };
    const reactFlowInnerStyle = { width: '100%', height: '100%', overflow: 'hidden' };
    const layoutPaneStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, width: '100%', height: '100%', overflowY: 'auto' };
    const baseInputStyle = { padding: '10px 14px', fontSize: '14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none' };
    const buttonStyle = { 
        padding: '10px 18px', 
        fontSize: '14px', 
        cursor: 'pointer', 
        border: 'none', 
        borderRadius: '8px', 
        fontWeight: '500', 
        transition: 'all 0.2s', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',  // ✅ MUDE PARA 'center'
        gap: '8px' 
    };


    const iconButtonStyle = { ...buttonStyle, padding: '10px' };
    const primaryButtonStyle = { ...buttonStyle, backgroundColor: '#383838ff', color: 'white' };
const animationStyles = `
  @keyframes blinkRed {
    0%, 100% { box-shadow: inset 6px 0 0 0 inherit, 0 0 0 0 rgba(239, 68, 68, 0); }
    50% { box-shadow: inset 6px 0 0 0 inherit, 0 0 0 6px rgba(239, 68, 68, 0.3); }
  }
  
  .card-error-highlight {
    /* A animação usa box-shadow para não mexer na geometria da caixa */
    animation: blinkRed 1.5s ease-in-out infinite !important;
    background-color: #fff5f5 !important;
  }
  
  /* ... outros estilos ... */
`;


    const secondaryButtonStyle = { ...buttonStyle, backgroundColor: '#475569', color: 'white' };
    const ghostButtonStyle = { ...buttonStyle, backgroundColor: 'transparent', color: '#334155', border: '1px solid #cbd5e1' };

   
    const promptModalOverlayStyle = { 
        ...modalOverlayStyle, 
        zIndex: 1900 // ✅ CORRIGIDO
    };
    const confirmationModalOverlayStyle = { 
        ...modalOverlayStyle, 
        zIndex: 2000 // ✅ CORRIGIDO
    };

    // ✅ FIM DA CORREÇÃO DE Z-INDEX E CENTRALIZAÇÃO FIM DA CORREÇÃO DE Z-INDEX

   
    const inputStyle = { ...baseInputStyle, width: '100%', boxSizing: 'border-box' };
    const textareaStyle = { ...baseInputStyle, width: '100%', minHeight: '100px', boxSizing: 'border-box', resize: 'vertical' };
    const formSectionStyle = { marginBottom: '24px', border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', backgroundColor: '#f8fafc' };
    const formSectionTitleStyle = { marginTop: 0, marginBottom: '16px', color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' };
    const formLabelStyle = { display: 'block', fontWeight: '500', color: '#334155', marginBottom: '6px' };
    // --- COMPONENTE DO MODAL DE VÍDEO (NOVA VERSÃO) ---
    const ExplanatoryVideoModal = ({ isOpen, onClose }) => {
        const [activeVideoId, setActiveVideoId] = useState(null);

        const videoTopics = [
            { title: "Como definir Job Description", videoId: "SEU_ID_DO_YOUTUBE_AQUI" },
            { title: "Como preencher uma vaga com um colaborador", videoId: "SEU_ID_DO_YOUTUBE_AQUI" },
            { title: "Como usar os filtros de área", videoId: "SEU_ID_DO_YOUTUBE_AQUI" },
            { title: "Como criar e editar áreas", videoId: "SEU_ID_DO_YOUTUBE_AQUI" },
        ];

        useEffect(() => {
            if (!isOpen) {
                setTimeout(() => {
                    setActiveVideoId(null);
                }, 300);
            }
        }, [isOpen]);

        if (!isOpen) return null;

        const videoModalContentStyle = {
            background: 'white',
            padding: '32px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: activeVideoId ? '960px' : '600px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease-in-out',
            position: 'relative',
        };

        const iframeContainerStyle = {
            position: 'relative',
            paddingTop: '56.25%', 
            height: 0,
            overflow: 'hidden',
            borderRadius: '8px',
            backgroundColor: '#000',
        };

        const iframeStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
        };

        const topicItemStyle = {
            padding: '16px 20px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            marginBottom: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            color: '#1e293b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        };
        
        const TopicItem = ({ title, onClick }) => {
            const [hover, setHover] = useState(false);
            return (
                <div
                    style={{ ...topicItemStyle, backgroundColor: hover ? '#f8fafc' : '#fff', transform: hover ? 'translateY(-2px)' : 'none', boxShadow: hover ? '0 4px 10px rgba(0,0,0,0.05)' : 'none' }}
                    onClick={onClick}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    {title}
                    <span style={{ transition: 'transform 0.2s', transform: hover ? 'translateX(5px)' : 'none' }}>→</span>
                </div>
            );
        };

        return (
            <div style={modalOverlayStyle} onClick={onClose}>
                <div style={videoModalContentStyle} onClick={(e) => e.stopPropagation()}>
                    <button
                        style={{ ...ghostButtonStyle, position: 'absolute', top: '16px', right: '16px', padding: '8px', zIndex: 20 }}
                        onClick={onClose}
                    >
                        <XIcon />
                    </button>

                    {!activeVideoId ? (
                        <>
                            <h2 style={{ marginTop: 0, color: '#1e293b' }}>Vídeos Explicativos</h2>
                            <p style={{ color: '#475569', lineHeight: '1.6', marginTop: 0, marginBottom: '24px' }}>
                                Selecione um tópico abaixo para assistir ao vídeo correspondente.
                            </p>
                            <div>
                                {videoTopics.map((topic) => (
                                    <TopicItem
                                        key={topic.videoId}
                                        title={topic.title}
                                        onClick={() => setActiveVideoId(topic.videoId)}
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                                <button
                                    style={{...ghostButtonStyle, padding: '8px'}}
                                    onClick={() => setActiveVideoId(null)}
                                >
                                    <BackIcon />
                                </button>
                                <h3 style={{ margin: 0, color: '#1e293b' }}>
                                    {videoTopics.find(t => t.videoId === activeVideoId)?.title}
                                </h3>
                            </div>
                            <div style={iframeContainerStyle}>
                                <iframe
                                    style={iframeStyle}
                                    src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                                    title="YouTube video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };


    // --- LÓGICA DE LAYOUT COM ELKjs ---
    const elk = new ELK();

    const getLayoutedElements = (nodes, edges, direction = 'DOWN') => {
        if (!nodes || nodes.length === 0) return Promise.resolve({ nodes: [], edges: [] });

        const isHorizontal = direction === 'RIGHT';

        const elkOptions = {
            'elk.algorithm': 'mrtree',
            'elk.direction': direction,
            'elk.edgeRouting': 'ORTHOGONAL',
            'elk.separateConnectedComponents': 'false',
            'elk.spacing.nodeNode': '100',
            'elk.layered.spacing.nodeNodeBetweenLayers': isHorizontal ? '180' : '120',
        };

        const graph = {
            id: 'root',
            layoutOptions: elkOptions,
            children: nodes.map(node => ({
                ...node,
                width: node.width,
                height: node.height
            })),
            edges: edges
        };

        return elk.layout(graph)
            .then(layoutedGraph => ({
                nodes: layoutedGraph.children.map(node => ({
                    ...node,
                    sourcePosition: isHorizontal ? 'right' : 'bottom',
                    targetPosition: isHorizontal ? 'left' : 'top',
                    position: { x: node.x, y: node.y }
                })),
                edges: layoutedGraph.edges
            }))
            .catch(error => {
                console.error('Não foi possível organizar os cards; mantendo as posições existentes:', error);
                return { nodes, edges };
            });
    };

    const MultiSelectFilter = ({ options, selectedOptions, onChange }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
        const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
        const wrapperRef = useRef(null);
        const buttonRef = useRef(null);
        const dropdownRef = useRef(null); // ✅ Nova ref para o dropdown
        
        useEffect(() => {
            const handleResize = () => setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);
        
        useEffect(() => {
            function handleClickOutside(event) { 
                // ✅ Verifica se o clique foi no botão ou no dropdown
                if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
                    dropdownRef.current && !dropdownRef.current.contains(event.target)) { 
                    setIsOpen(false); 
                } 
            }
            
            if (isOpen) {
                document.addEventListener("mousedown", handleClickOutside);
            }
            
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [isOpen]); // ✅ Adiciona isOpen como dependência

        useEffect(() => {
            if (isOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.bottom + 4,
                    left: rect.left,
                    width: Math.max(rect.width, 220)
                });
            }
        }, [isOpen]);

        const handleSelect = (optionName) => {
            const newSelectedOptions = selectedOptions.includes(optionName) 
                ? selectedOptions.filter(item => item !== optionName) 
                : [...selectedOptions, optionName];
            onChange(newSelectedOptions);
        };

        return (
            <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
                <button 
                    ref={buttonRef}
                    onClick={() => setIsOpen(!isOpen)} 
                    style={{ 
                        ...ghostButtonStyle,
                        padding: isTablet ? '7px 9px' : '8px 11px',
                        fontSize: isTablet ? '0.75rem' : '0.82rem',
                        width: '100%', 
                        textAlign: 'left', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <span>
                        {selectedOptions.length > 0 
                            ? `${selectedOptions.length} área${selectedOptions.length > 1 ? 's' : ''}` 
                            : (isTablet ? 'Áreas' : 'Filtrar Área')}
                    </span>
                    {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </button>

                {isOpen && ReactDOM.createPortal(
                    <div 
                        ref={dropdownRef} // ✅ Adiciona ref ao dropdown
                        style={{ 
                            position: 'fixed',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                            background: 'white', 
                            border: '2px solid #cbd5e1',
                            zIndex: 9999,
                            maxHeight: '250px', 
                            overflowY: 'auto', 
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)', 
                            borderRadius: '8px' 
                        }}
                    >
                        {options.map(option => (
                            <div
                                key={option.name}
                                style={{ 
                                    padding: '10px 12px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between', 
                                    cursor: 'pointer', 
                                    backgroundColor: selectedOptions.includes(option.name) ? '#f1f5f9' : 'white',
                                    transition: 'background-color 0.2s'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation(); // ✅ Previne propagação
                                    handleSelect(option.name);
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedOptions.includes(option.name) ? '#f1f5f9' : 'white'}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={selectedOptions.includes(option.name)}
                                        style={{ 
                                            width: '16px', 
                                            height: '16px', 
                                            accentColor: '#383838ff', 
                                            cursor: 'pointer',
                                            pointerEvents: 'none' // ✅ Previne clique direto no checkbox
                                        }}
                                    />
                                    <span>{option.name}</span>
                                </div>
                                <span style={{ 
                                    backgroundColor: '#eef2ff', 
                                    color: '#4f46e5', 
                                    borderRadius: '9999px', 
                                    padding: '2px 10px', 
                                    fontSize: '12px', 
                                    fontWeight: '600' 
                                }}>
                                    {option.count}
                                </span>
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    };






    const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => { if (!isOpen) return null; return (<div style={confirmationModalOverlayStyle}><div style={{...modalContentStyle, maxWidth: '500px'}}><h2 style={{ marginTop: 0, color: '#1e293b' }}>Confirmação</h2><p style={{ color: '#475569', lineHeight: '1.6' }}>{message}</p><div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}><button onClick={onCancel} style={{ ...ghostButtonStyle }}>Cancelar</button><button onClick={onConfirm} style={{ ...buttonStyle, backgroundColor: '#dc2626', color: 'white' }}>Confirmar</button></div></div></div>); };

  const UserSelectionModal = ({ isOpen, onClose, onSelectUser, unitId, token }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isOpen && unitId) {
            setLoading(true);
            fetch(`${process.env.REACT_APP_API_URL}/available-users/unit/${unitId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
                setUsers(Array.isArray(data) ? data : []);
                console.log('Dados dos usuários recebidos:', data);
            })
            .catch(err => {
                console.error("Failed to fetch users:", err);
                setUsers([]);
            })
            .finally(() => setLoading(false));
        } else if (!isOpen) {
            setUsers([]);
            setSearchTerm('');
        }
    }, [isOpen, unitId, token]);

    const filteredUsers = users.filter(user => {
        const term = searchTerm.toLowerCase();

        if (term === '') {
            return true;
        }

        const nameMatch = user.name && user.name.toLowerCase().includes(term);
        const cargoMatch = user.cargo && user.cargo.toLowerCase().includes(term);
        const cpfMatch = user.cpf && user.cpf.includes(term);

        return nameMatch || cargoMatch || cpfMatch;
    });

    const handleSelectCustomUser = () => {
        const name = searchTerm.trim();
        if (!name || loading) return;
        onSelectUser({ id: null, name, isCustom: true });
    };

    const UserListItem = ({ user, onSelect }) => {
        const [hover, setHover] = useState(false);
        const itemStyle = {
            padding: '12px 16px',
            border: '1px solid transparent',
            borderBottom: '1px solid #f1f5f9',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            marginBottom: '4px',
            backgroundColor: hover ? '#f8fafc' : 'transparent',
            borderColor: hover ? '#e2e8f0' : 'transparent',
        };

        return (
            <li
                style={itemStyle}
                onClick={() => onSelect(user)}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <CustomAvatar src={user.profile_picture_url} name={user.name} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong style={{ color: '#1e293b', fontSize: '1rem', lineHeight: '1.4' }}>
                            {user.name}
                        </strong>
                        <small style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: '1.4' }}>
                            {user.cargo || 'Cargo não definido'}
                        </small>
                        {user.cpf && (
                            <small style={{ color: '#94a3b8', fontSize: '0.875rem', lineHeight: '1.4' }}>
                                CPF: {user.cpf}
                            </small>
                        )}
                    </div>
                </div>
                <span style={{ color: '#383838ff', fontWeight: 'bold', transition: 'transform 0.2s ease', transform: hover ? 'translateX(5px)' : 'translateX(0)' }}>→</span>
            </li>
        );
    };

    if (!isOpen) return null;

    return (
        <div style={modalOverlayStyle}>
            <div style={{...modalContentStyle, maxWidth: '550px'}}>
                <h2 style={{ marginTop: 0, color: '#1e293b' }}>Selecionar Colaborador</h2>
                
                {/* Melhoria de UX 1: Texto explicativo mais claro */}
                <p style={{ color: '#475569', lineHeight: '1.6', marginTop: 0, marginBottom: '24px' }}>
                    Escolha um colaborador na lista abaixo ou <strong>digite um novo nome</strong> para preencher a vaga manualmente.
                </p>

                <div style={{ position: 'relative', marginBottom: '16px' }}>
                    {/* Melhoria de UX 2: Placeholder instrucional */}
                    <input
                        type="text"
                        placeholder="Pesquisar na base ou digitar um novo nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.repeat) {
                                e.preventDefault();
                                handleSelectCustomUser();
                            }
                        }}
                        style={{...inputStyle, paddingLeft: '40px', borderColor: '#3b82f6'}}
                        autoFocus
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#3b82f6' }}>
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>

                {/* Melhoria de UX 3: Helper Box com dica visual */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                    <InfoOutlined style={{ fontSize: '1.2rem', color: '#3b82f6', marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem', color: '#1e3a8a', lineHeight: '1.4' }}>
                        <strong>Dica:</strong> Se não encontrar o colaborador, digite o nome completo e pressione Enter ou clique na opção azul abaixo para criar um registro nominal.
                    </span>
                </div>

                {loading ? (
                    <p style={{textAlign: 'center', color: '#475569'}}>A procurar colaboradores...</p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '40vh', overflowY: 'auto' }}>
                        
                        {/* Renderiza os usuários encontrados na busca */}
                        {filteredUsers.length > 0 && (
                            filteredUsers.map(user => (
                                <UserListItem key={user.id} user={user} onSelect={onSelectUser} />
                            ))
                        )}

                        {/* NOVA OPÇÃO: Criar colaborador a partir do texto digitado */}
                        {searchTerm.trim() !== '' && (
                            <li
                                style={{
                                    padding: '12px 16px',
                                    border: '1px dashed #3b82f6',
                                    backgroundColor: '#eff6ff',
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    marginTop: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                                onClick={handleSelectCustomUser}
                            >
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '50%', 
                                    backgroundColor: '#3b82f6', color: 'white', display: 'flex', 
                                    alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem'
                                }}>+</div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <strong style={{ color: '#1e3a8a', fontSize: '1rem' }}>
                                        Inserir "{searchTerm.trim()}" manualmente
                                    </strong>
                                    <small style={{ color: '#3b82f6', fontSize: '0.85rem' }}>
                                        Preencher vaga apenas com este texto
                                    </small>
                                </div>
                            </li>
                        )}

                        {/* Mensagem de vazio quando digita algo que não existe */}
                        {filteredUsers.length === 0 && searchTerm.trim() !== '' && (
                            <p style={{textAlign: 'center', color: '#64748b', padding: '15px 0 0 0', fontSize: '0.9rem'}}>
                                Nenhum colaborador encontrado. Use a opção acima para inserir o nome.
                            </p>
                        )}
                    </ul>
                )}
                
                <button onClick={onClose} style={{ ...ghostButtonStyle, width: '100%', marginTop: '24px' }}>
                    Cancelar
                </button>
            </div>
        </div>
    );
};

    // Substitua o componente AreaColorModal existente por este:

    const AreaColorModal = ({ 
        isOpen, 
        onClose, 
        node, 
        existingAreas, 
        onAssignArea, 
        onCreateArea, 
        onUpdateArea, 
        onDeleteArea, 
        onShowConfirmation, 
        canEdit,
        revisionContext
    }) => {
        const [view, setView] = useState('list');
        const [editableArea, setEditableArea] = useState(null);
        const [newAreaName, setNewAreaName] = useState('');
        const [newAreaColor, setNewAreaColor] = useState('#22c55e');

        useEffect(() => {
            if (isOpen) {
                setView('list');
                setEditableArea(null);
                setNewAreaName('');
                setNewAreaColor('#22c55e');
            }
        }, [isOpen]);

        useEffect(() => {
            if (editableArea) {
                setNewAreaName(editableArea.name);
                setNewAreaColor(editableArea.color);
            } else {
                setNewAreaName('');
                setNewAreaColor('#22c55e');
            }
        }, [editableArea]);

        if (!isOpen || !node) return null;

        const handleSave = () => {
            if (!newAreaName.trim()) {
                console.error('O nome da área não pode estar vazio.');
                return;
            }
            if (editableArea) {
                onUpdateArea({ areaId: editableArea.id, name: newAreaName, color: newAreaColor });
            } else {
                onCreateArea({ nodeId: node.id, name: newAreaName, color: newAreaColor });
            }
        };

        const handleDelete = (areaIdToDelete) => {
            onShowConfirmation(
                'Tem a certeza que deseja excluir esta área? Esta ação não pode ser desfeita. Todos os cartões associados a esta área perderão a sua atribuição de área.',
                () => onDeleteArea(areaIdToDelete)
            );
        };

        // ===== ESTILOS PROFISSIONAIS =====
        const professionalModalContentStyle = {
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
            padding: '0',
            borderRadius: '16px',
            width: '95%',
            maxWidth: '700px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            margin: 0,
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        };

        const modalHeaderStyle = {
            background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
            padding: '28px 32px',
            borderBottom: '3px solid #fb7185',
            color: 'white',
            position: 'relative'
        };

        const modalBodyStyle = {
            padding: '32px',
            overflowY: 'auto',
            flexGrow: 1,
            backgroundColor: '#ffffff'
        };

        const modalFooterStyle = {
            padding: '20px 32px',
            borderTop: '2px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            flexWrap: 'wrap'
        };

        const areaListItemStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            marginBottom: '12px',
            borderRadius: '12px',
            cursor: canEdit ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            border: '2px solid #f1f5f9',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
        };

        const colorSwatchStyle = {
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            border: '3px solid white',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.2s ease'
        };

        const iconButtonStyleInternal = {
            background: 'white',
            border: '2px solid #e2e8f0',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            transition: 'all 0.2s',
            width: '36px',
            height: '36px'
        };

        // Cores predefinidas mais bonitas
        const presetColors = [
            { color: '#ef4444', name: 'Vermelho' },
            { color: '#f97316', name: 'Laranja' },
            { color: '#f59e0b', name: 'Âmbar' },
            { color: '#eab308', name: 'Amarelo' },
            { color: '#84cc16', name: 'Lima' },
            { color: '#22c55e', name: 'Verde' },
            { color: '#10b981', name: 'Esmeralda' },
            { color: '#14b8a6', name: 'Turquesa' },
            { color: '#06b6d4', name: 'Ciano' },
            { color: '#0ea5e9', name: 'Azul Claro' },
            { color: '#3b82f6', name: 'Azul' },
            { color: '#6366f1', name: 'Índigo' },
            { color: '#8b5cf6', name: 'Violeta' },
            { color: '#a855f7', name: 'Roxo' },
            { color: '#d946ef', name: 'Fúcsia' },
            { color: '#ec4899', name: 'Rosa' },
            { color: '#f43f5e', name: 'Rosa Escuro' },
            { color: '#64748b', name: 'Cinza' }
        ];

        const DeleteIconInModal = () => (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
        );

        const PaintIcon = () => (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 20h4L18.5 9.5a2.828 2.828 0 0 0-4-4L4 16v4z"/>
                <path d="M13.5 6.5l4 4"/>
            </svg>
        );

        const ColorPresetButton = ({ color, name, isSelected, onClick }) => (
            <div
                onClick={onClick}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
                <div
                    style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: color,
                        border: isSelected ? '4px solid #1e293b' : '3px solid white',
                        boxShadow: isSelected 
                            ? '0 8px 20px rgba(0, 0, 0, 0.25), 0 0 0 2px ' + color
                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}
                >
                    {isSelected && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '24px'
                        }}>
                            ✓
                        </div>
                    )}
                </div>
                <span style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    fontWeight: '500',
                    textAlign: 'center'
                }}>
                    {name}
                </span>
            </div>
        );

        return (
            <ModalPortal>
                <div style={modalOverlayStyle}>
                    <div style={professionalModalContentStyle}>
                        {/* Header */}
                        <div style={modalHeaderStyle}>
                            <button
                                onClick={onClose}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    width: '36px',
                                    height: '36px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >
                                <XIcon style={{width: '20px', height: '20px'}} />
                            </button>
                            <h2 style={{margin: 0, fontSize: '1.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <PaintIcon />
                                Gerir Áreas do Card
                            </h2>
                            <p style={{margin: '8px 0 0', fontSize: '1rem', opacity: 0.9}}>
                                Card: <strong>{node?.data?.cargo || 'selecionado'}</strong>
                            </p>
                        </div>

                        {/* Body */}
                        <div style={modalBodyStyle}>
                            {view === 'list' && (
                                <>
                                    <p style={{color: '#475569', lineHeight: '1.6', marginTop: 0, marginBottom: '24px', fontSize: '0.95rem'}}>
                                        Selecione uma área existente para aplicar ao card ou crie/edite uma área.
                                    </p>
                                    
                                    {/* Lista de Áreas */}
                                    <div style={{marginBottom: '24px'}}>
                                        {existingAreas.length > 0 ? (
                                            [...existingAreas]
                                                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                                                .map(area => (
                                                    <div
                                                        key={area.id}
                                                        style={areaListItemStyle}
                                                        onClick={(e) => {
                                                            if (e.target.closest('button')) return;
                                                            if (canEdit) onAssignArea({ nodeId: node.id, areaId: area.id });
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (canEdit) {
                                                                e.currentTarget.style.borderColor = area.color;
                                                                e.currentTarget.style.backgroundColor = area.color + '08';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (canEdit) {
                                                                e.currentTarget.style.borderColor = '#f1f5f9';
                                                                e.currentTarget.style.backgroundColor = '#ffffff';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                                                            }
                                                        }}
                                                    >
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1, overflow: 'hidden'}}>
                                                            <div style={{...colorSwatchStyle, backgroundColor: area.color}}></div>
                                                            <span style={{
                                                                color: '#1e293b',
                                                                fontSize: '1.05rem',
                                                                fontWeight: '600',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }} title={area.name}>
                                                                {area.name}
                                                            </span>
                                                        </div>
                                                        {canEdit && (
                                                            <div style={{display: 'flex', gap: '8px', flexShrink: 0}}>
                                                                <button
                                                                    style={iconButtonStyleInternal}
                                                                    onClick={() => { setEditableArea(area); setView('form'); }}
                                                                    title="Editar Área"
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.borderColor = '#3b82f6';
                                                                        e.currentTarget.style.backgroundColor = '#eff6ff';
                                                                        e.currentTarget.style.color = '#3b82f6';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                                        e.currentTarget.style.backgroundColor = 'white';
                                                                        e.currentTarget.style.color = '#64748b';
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </button>
                                                                <button
                                                                    style={iconButtonStyleInternal}
                                                                    onClick={() => handleDelete(area.id)}
                                                                    title="Excluir Área"
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.borderColor = '#ef4444';
                                                                        e.currentTarget.style.backgroundColor = '#fef2f2';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                                        e.currentTarget.style.backgroundColor = 'white';
                                                                    }}
                                                                >
                                                                    <DeleteIconInModal />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                        ) : (
                                            <div style={{
                                                padding: '40px 20px',
                                                textAlign: 'center',
                                                backgroundColor: '#f8fafc',
                                                borderRadius: '12px',
                                                border: '2px dashed #cbd5e1'
                                            }}>
                                                <p style={{color: '#64748b', margin: 0, fontSize: '0.95rem'}}>
                                                    Nenhuma área foi criada ainda.
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {canEdit && (
                                        <button
                                            onClick={() => { setEditableArea(null); setView('form'); }}
                                            style={{
                                                ...primaryButtonStyle,
                                                width: '100%',
                                                backgroundColor: '#16a34a',
                                                padding: '14px',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            + Criar Nova Área
                                        </button>
                                    )}
                                </>
                            )}

                            {view === 'form' && (
                                <div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
                                        <button
                                            style={{...iconButtonStyleInternal, padding: '8px'}}
                                            onClick={() => setView('list')}
                                            title="Voltar para a lista"
                                        >
                                            <BackIcon />
                                        </button>
                                        <h3 style={{margin: 0, color: '#1e293b', fontSize: '1.2rem'}}>
                                            {editableArea ? 'Editar Área' : 'Criar Nova Área'}
                                        </h3>
                                    </div>

                                    {/* Nome da Área */}
                                    <div style={{marginBottom: '32px'}}>
                                        <label style={{
                                            ...formLabelStyle,
                                            fontSize: '0.95rem',
                                            marginBottom: '10px',
                                            display: 'block'
                                        }}>
                                            Nome da Área *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Ex: Marketing Digital"
                                            value={newAreaName}
                                            onChange={(e) => setNewAreaName(e.target.value)}
                                            style={{
                                                ...inputStyle,
                                                fontSize: '15px',
                                                padding: '12px 16px',
                                                borderRadius: '10px',
                                                border: '2px solid #e2e8f0',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                            disabled={!canEdit && !revisionContext}
                                        />
                                    </div>

                                    {/* Seletor de Cor com Preview */}
                                    <div>
                                        <label style={{
                                            ...formLabelStyle,
                                            fontSize: '0.95rem',
                                            marginBottom: '16px',
                                            display: 'block'
                                        }}>
                                            Cor da Área
                                        </label>

                                        {/* Preview da cor selecionada */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            marginBottom: '24px',
                                            padding: '20px',
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '12px',
                                            border: '2px solid #e2e8f0'
                                        }}>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '16px',
                                                backgroundColor: newAreaColor,
                                                border: '4px solid white',
                                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                flexShrink: 0
                                            }}></div>
                                            <div style={{flexGrow: 1}}>
                                                <div style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '600',
                                                    color: '#1e293b',
                                                    marginBottom: '4px'
                                                }}>
                                                    {newAreaName || 'Nome da Área'}
                                                </div>
                                                <div style={{
                                                    fontSize: '0.9rem',
                                                    color: '#64748b',
                                                    fontFamily: 'monospace',
                                                    backgroundColor: 'white',
                                                    padding: '4px 12px',
                                                    borderRadius: '6px',
                                                    display: 'inline-block',
                                                    border: '1px solid #e2e8f0'
                                                }}>
                                                    {newAreaColor.toUpperCase()}
                                                </div>
                                            </div>
                                        </div>

                                        {canEdit ? (
                                            <>
                                                <div style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    color: '#475569',
                                                    marginBottom: '12px'
                                                }}>
                                                    Cores Predefinidas
                                                </div>
                                                {/* Grid de cores predefinidas */}
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                                                    gap: '16px',
                                                    marginBottom: '24px'
                                                }}>
                                                    {presetColors.map(preset => (
                                                        <ColorPresetButton
                                                            key={preset.color}
                                                            color={preset.color}
                                                            name={preset.name}
                                                            isSelected={newAreaColor === preset.color}
                                                            onClick={() => setNewAreaColor(preset.color)}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Botão para cor personalizada */}
                                                <div style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    color: '#475569',
                                                    marginBottom: '12px'
                                                }}>
                                                    Ou escolha uma cor personalizada
                                                </div>
                                                <input
                                                    type="color"
                                                    value={newAreaColor}
                                                    onChange={(e) => setNewAreaColor(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        height: '60px',
                                                        border: '3px solid #e2e8f0',
                                                        borderRadius: '12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                <div style={{
                                                    ...colorSwatchStyle,
                                                    backgroundColor: newAreaColor,
                                                    width: '30px',
                                                    height: '30px'
                                                }}></div>
                                                <span>{newAreaColor}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
    <div style={modalFooterStyle}>
        {view === 'form' && (
            <>
                <button
                    onClick={() => setView('list')}
                    style={{...ghostButtonStyle, padding: '12px 24px'}}
                >
                    Cancelar
                </button>
                {canEdit && (
                    <button
                        onClick={handleSave}
                        style={{
                            ...primaryButtonStyle,
                            backgroundColor: '#16a34a',
                            padding: '12px 32px',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                        disabled={!newAreaName.trim()}
                    >
                        {editableArea ? '✓ Salvar Alterações' : '✓ Salvar Nova Área e Aplicar'}
                    </button>
                )}
            </>
        )}
                        </div>
                    </div>
                </div>
            </ModalPortal>
        );
    };

            

    const TabsComponent = ({ tabs }) => {
        const [activeTab, setActiveTab] = useState(0);

        const tabButtonStyle = (isActive) => ({
            padding: '10px 20px',
            border: 'none',
            borderBottom: isActive ? '3px solid #383838ff' : '3px solid transparent',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: isActive ? '600' : '500',
            color: isActive ? '#383838ff' : '#475569',
            transition: 'all 0.2s ease-in-out',
        });

        const visibleTabs = tabs.filter(tab => tab.content !== null);

        return (
            <div>
                <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
                    {visibleTabs.map((tab, index) => (
                        <button key={index} onClick={() => setActiveTab(index)} style={tabButtonStyle(activeTab === index)}>
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div>
                    {visibleTabs[activeTab]?.content}
                </div>
            </div>
        );
    };

const PromptModal = ({ isOpen, onClose, onConfirm, title, message, placeholder }) => {
    const [inputValue, setInputValue] = useState('');
    
    // Reseta o texto quando o modal fecha
    useEffect(() => {
        if (!isOpen) setInputValue('');
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (inputValue.trim()) {
            onConfirm(inputValue);
            setInputValue('');
        }
    };

    // Estilo local para garantir que a caixa seja grande
    const textAreaStyle = {
        width: '100%',
        minHeight: '150px', // Altura inicial maior
        padding: '12px 16px',
        borderRadius: '10px',
        border: '2px solid #e2e8f0',
        fontSize: '15px',
        lineHeight: '1.6',
        fontFamily: 'inherit',
        resize: 'vertical', // Permite ao gestor aumentar se precisar
        outline: 'none',
        boxSizing: 'border-box',
        backgroundColor: '#f8fafc',
        transition: 'border-color 0.2s'
    };

    return (
        <div style={promptModalOverlayStyle}>
            <div style={{ ...modalContentStyle, maxWidth: '600px', zIndex: 1601 }}>
                <h2 style={{ marginTop: 0, color: '#1e293b', fontSize: '1.5rem' }}>{title}</h2>
                <p style={{ color: '#475569', lineHeight: '1.6', marginBottom: '20px' }}>{message}</p>
                
                <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={placeholder}
                    style={textAreaStyle}
                    autoFocus
                    onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.backgroundColor = '#fff';
                    }}
                    onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                    <button 
                        onClick={onClose} 
                        style={ghostButtonStyle}
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={handleConfirm} 
                        style={{ 
                            ...primaryButtonStyle, 
                            backgroundColor: '#16a34a',
                            padding: '10px 24px' 
                        }} 
                        disabled={!inputValue.trim()}
                    >
                        Enviar Solicitação
                    </button>
                </div>
            </div>
        </div>
    );
};

    // ✨ NOVO COMPONENTE DE EDITOR DE COMPETÊNCIAS
const SelectOrCreateCompetencyEditor = ({ 
    competencyType, 
    title, 
    competencies, 
    onAddCompetency, 
    onRemoveCompetency, 
    dictionaryOptions = [], 
    areas = [], 
    categories = [],
    // 👇 ADICIONE ESTES:
    canEdit,
    revisionContext
    // 👆
}) => {
        const { user } = useAuth();

        const [isPickerOpen, setIsPickerOpen] = useState(false);
        const [pickerView, setPickerView] = useState('select'); // 'select' ou 'create'
        const [searchTerm, setSearchTerm] = useState('');
        // 👇 MODIFICADO: Adicionado 'categoriaId'
        const [newCompetency, setNewCompetency] = useState({ nome: '', descricao: '', areaId: '', categoriaId: '' }); 
        
        // --- NOVOS ESTADOS DE FILTRO ---
        const [selectedAreaFilterId, setSelectedAreaFilterId] = useState('');
        const [selectedCategoryFilterId, setSelectedCategoryFilterId] = useState(''); // 👈 NOVO
        const NO_CATEGORY_KEY = '_SEM_CATEGORIA_'; // Chave especial para filtro
        const [selectedCompetencies, setSelectedCompetencies] = useState(new Set());
        // --- LÓGICA DE ORDENAÇÃO E MAPEAMENTO (Áreas e Categorias) ---
        const sortedAreas = useMemo(() => {
            if (!Array.isArray(areas)) return [];
            return [...areas].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
        }, [areas]);
        
        const areaMap = useMemo(() => {
            return sortedAreas.reduce((map, area) => {
                map[area.id] = area;
                return map;
            }, {});
        }, [sortedAreas]);

        // 👈 NOVO: Ordenar e mapear categorias
        const sortedCategories = useMemo(() => {
            if (!Array.isArray(categories)) return [];
            return [...categories].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
        }, [categories]);

        // 👈 NOVO: Mapa de categorias
        const categoryMap = useMemo(() => {
            return sortedCategories.reduce((map, category) => {
                map[category.id] = category;
                return map;
            }, {});
        }, [sortedCategories]);


        // Mapeamento de estilos por tipo de competência
    const typeStyles = {
        competenciasTecnicas: {
            backgroundColor: '#ffffff', // BRANCO
            color: '#7c3aed',
            borderColor: '#7c3aed',
        },
        outrasCompetencias: {
            backgroundColor: '#ffffff', // BRANCO
            color: '#db2777',
            borderColor: '#db2777',
        },
        default: {
            backgroundColor: '#eef2ff', // Azul claro (padrão) - mantém igual
            color: '#4338ca',           // Azul principal
            borderColor: '#c7d2fe',
        }
    };


        const currentStyle = typeStyles[competencyType] || typeStyles.default;

        const chipStyle = {
            display: 'block',
            backgroundColor: currentStyle.backgroundColor,
            color: currentStyle.color,
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '10px',
            border: `1px solid ${currentStyle.borderColor}`,
            position: 'relative',
        };

        const removeChipButtonStyle = {
            background: 'rgba(0,0,0,0.08)',
            border: 'none',
            borderRadius: '50%',
            color: currentStyle.color,
            cursor: 'pointer',
            width: '22px',
            height: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            top: '10px',
            right: '10px',
        };


        const handleOpenPicker = () => {
            setSearchTerm('');
            // 👇 MODIFICADO: Resetar 'categoriaId'
            setNewCompetency({ nome: '', descricao: '', areaId: '', categoriaId: '' });
            setPickerView('select');
            setIsPickerOpen(true);
            setSelectedAreaFilterId(''); 
            setSelectedCategoryFilterId(''); // 👈 NOVO: Reseta filtro de categoria
        };

        const handleClosePicker = () => setIsPickerOpen(false);

        const handleSelectFromDictionary = (competency) => {
            onAddCompetency(competencyType, competency);
            handleClosePicker();
        };

        const handleCreateNew = () => {
            if (newCompetency.nome.trim() && newCompetency.descricao.trim()) {
                // Envia o objeto completo, incluindo areaId e categoriaId
                onAddCompetency(competencyType, { 
                    ...newCompetency,
                    id: `temp-${Date.now()}`, // ID temporário
                    areaId: newCompetency.areaId || null,
                    categoriaId: newCompetency.categoriaId || null
                });
                handleClosePicker();
            }
        };
        
        // Obtém as competências que já foram adicionadas ao job description
        const addedCompetencyNames = new Set((competencies || []).map(c => c.nome));
        
        // 👈 GRANDE MUDANÇA: Filtra e AGRUPA as opções do dicionário
        const availableDictionaryOptions = useMemo(() => {
            let options = (dictionaryOptions || []).filter(opt => !addedCompetencyNames.has(opt.nome));
            
            // 1. Filtra por termo de pesquisa
            if (searchTerm.trim()) {
                const lowerSearchTerm = searchTerm.toLowerCase();
                options = options.filter(opt =>
                    opt.nome.toLowerCase().includes(lowerSearchTerm) ||
                    opt.descricao.toLowerCase().includes(lowerSearchTerm)
                );
            }

            // 2. Filtra pela área selecionada
            if (selectedAreaFilterId) {
                options = options.filter(opt => (opt.areaId || '').toString() === selectedAreaFilterId);
            }
            
            // 3. Filtra pela categoria selecionada
            if (selectedCategoryFilterId) {
                if (selectedCategoryFilterId === NO_CATEGORY_KEY) {
                    // Filtra por competências SEM categoria
                    options = options.filter(opt => !opt.categoriaId);
                } else {
                    // Filtra por uma categoria específica
                    options = options.filter(opt => (opt.categoriaId || '').toString() === selectedCategoryFilterId);
                }
            }

            // 4. AGRUPA por Categoria
            const grouped = options.reduce((acc, opt) => {
                let categoryName = "Sem Categoria"; // Nome padrão
                if (opt.categoriaId && categoryMap[opt.categoriaId]) {
                    categoryName = categoryMap[opt.categoriaId].name;
                }
                
                if (!acc[categoryName]) {
                    acc[categoryName] = [];
                }
                acc[categoryName].push(opt);
                return acc;
            }, {});

            // Ordena os grupos (exceto "Sem Categoria", que vai por último)
            const sortedGroupNames = Object.keys(grouped).sort((a, b) => {
                if (a === "Sem Categoria") return 1;
                if (b === "Sem Categoria") return -1;
                return a.localeCompare(b, 'pt-BR');
            });
            
            // Retorna um objeto ordenado
            const sortedGroupedObject = {};
            for (const name of sortedGroupNames) {
                // Ordena as competências *dentro* de cada grupo
                sortedGroupedObject[name] = grouped[name].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
            }
            return sortedGroupedObject;
            

        }, [dictionaryOptions, addedCompetencyNames, searchTerm, selectedAreaFilterId, selectedCategoryFilterId, categoryMap]);
        // 👇 ADICIONE ESTE BLOCO AQUI (APÓS o useMemo de availableDictionaryOptions e ANTES do return)
        // Filtra categorias baseado na área selecionada
        const filteredCategoriesForArea = useMemo(() => {
            if (!selectedAreaFilterId) {
                // Se nenhuma área está selecionada, mostra todas as categorias
                return sortedCategories;
            }
            
            // Filtra para mostrar apenas categorias que existem em competências da área selecionada
            const categoryIdsInArea = new Set();
            dictionaryOptions.forEach(opt => {
                if (opt.areaId && opt.areaId.toString() === selectedAreaFilterId && opt.categoriaId) {
                    categoryIdsInArea.add(opt.categoriaId.toString());
                }
            });
            
            return sortedCategories.filter(cat => categoryIdsInArea.has(cat.id.toString()));
        }, [selectedAreaFilterId, sortedCategories, dictionaryOptions]);
        // 👆 FIM DO NOVO BLOCO
    // Funções para seleção múltipla
    const toggleCompetencySelection = (competency) => {
        setSelectedCompetencies(prev => {
            const newSet = new Set(prev);
            const key = competency.id || competency.nome;
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleAddSelectedCompetencies = () => {
        if (selectedCompetencies.size === 0) return;
        
        // Encontrar todas as competências selecionadas
        const competenciesToAdd = [];
        Object.values(availableDictionaryOptions).forEach(group => {
            group.forEach(opt => {
                const key = opt.id || opt.nome;
                if (selectedCompetencies.has(key)) {
                    competenciesToAdd.push(opt);
                }
            });
        });
        
        // Adicionar todas de uma vez
        competenciesToAdd.forEach(comp => {
            onAddCompetency(competencyType, comp);
        });
        
        // Limpar seleção e fechar
        setSelectedCompetencies(new Set());
        handleClosePicker();
    };

    const isCompetencySelected = (competency) => {
        const key = competency.id || competency.nome;
        return selectedCompetencies.has(key);
    };


        return (
            <div>
                {/* Lista de competências já adicionadas */}
                <div>
                    {(competencies || []).map((comp, index) => {
                        const areaInfo = comp.areaId ? areaMap[comp.areaId.toString()] : null;
                        // 👈 NOVO: Busca informação da categoria
                        const categoryInfo = comp.categoriaId ? categoryMap[comp.categoriaId.toString()] : null;

                        return (
                            <div key={index} style={chipStyle}>
                                <span style={{ display: 'block', marginBottom: '4px', fontSize: '15px', fontWeight: '500', color: '#1e293b' }}>{comp.nome}</span>
    {/* 👈 MODIFICADO */}
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{comp.descricao}</p>

                                {/* Rótulos de Área e Categoria */}
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                                    {areaInfo && (
                                        <div style={{
                                            display: 'inline-block',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: areaInfo.color || '#ccc', // Cor da área
                                            color: '#fff', // Texto branco para contraste
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                        }}>
                                            Área: {areaInfo.name}
                                        </div>
                                    )}
                                    {/* 👈 NOVO: Chip de Categoria */}
                                    {categoryInfo && (
                                        <div style={{
                                            display: 'inline-block',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: '#e0e7ff', // Cor fixa para categoria
                                            color: '#4338ca', 
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                        }}>
                                            Categoria: {categoryInfo.name}
                                        </div>
                                    )}
                                </div>
                                
                                {canEdit && (
                                    <button type="button" onClick={() => onRemoveCompetency(competencyType, index)} style={removeChipButtonStyle} title="Remover competência">
                                        <XIcon width={14} height={14} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Botão para abrir o seletor */}
                {canEdit && (
                    <button type="button" onClick={handleOpenPicker} style={{ ...ghostButtonStyle, marginTop: '12px', width: '100%' }}>
                        + Adicionar {title}
                    </button>
                )}

                {/* Modal de seleção/criação */}
                        {/* Modal de seleção/criação */}
                {isPickerOpen && (
                    <div style={promptModalOverlayStyle}>
                        <div style={{ ...modalContentStyle, maxWidth: '800px', padding: 0, overflow: 'hidden' }}>
                        {/* Banner Header */}
    <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        padding: '32px',
        textAlign: 'center',
        position: 'relative',
        borderBottom: '3px solid #ff834f'
    }}>


                                <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
                                    Adicionar {title}
                                </h2>
                                <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                                    Selecione do dicionário ou crie uma nova competência
                                </p>
                                
                                {/* Botão X */}
                                <button 
                                    onClick={handleClosePicker}
                                    style={{
                                        position: 'absolute',
                                        top: '16px',
                                        right: '16px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        width: '36px',
                                        height: '36px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        padding: 0,
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                            
                            {/* Conteúdo */}
                            <div style={{ padding: '32px' }}>
                                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '16px', gap: 0 }}>
                                    <button onClick={() => setPickerView('select')} style={{...buttonStyle, background: 'none', color: pickerView === 'select' ? '#1e293b' : '#64748b', borderBottom: pickerView === 'select' ? '3px solid #1e293b' : 'none', borderRadius: 0, flex: 1, textAlign: 'left', paddingBottom: '12px', paddingTop: '12px', borderRight: '1px solid #e2e8f0', fontWeight: pickerView === 'select' ? '600' : '500', transition: 'all 0.2s'}}>
                                        Selecionar do Dicionário
                                    </button>
                                    <button onClick={() => setPickerView('create')} style={{...buttonStyle, background: 'none', color: pickerView === 'create' ? '#1e293b' : '#64748b', borderBottom: pickerView === 'create' ? '3px solid #1e293b' : 'none', borderRadius: 0, flex: 1, textAlign: 'left', paddingBottom: '12px', paddingTop: '12px', fontWeight: pickerView === 'create' ? '600' : '500', transition: 'all 0.2s'}}>
                                        Criar Nova Competência
                                    </button>
                                </div>

                                {pickerView === 'select' && (
                                    <div>
                                       <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
  {/* Campo de Pesquisa */}
  <input
    type="text"
    placeholder="Pesquisar por nome..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    style={{...inputStyle, flex: 1}}
  />
  {/* Filtro de Área */}
  <select
    value={selectedAreaFilterId}
    onChange={(e) => setSelectedAreaFilterId(e.target.value)}
    style={{...inputStyle, width: '100%', flexShrink: 0}}
  >
    <option value="">Filtrar por Área...</option>
    {sortedAreas.map(area => (
      <option key={area.id} value={area.id}>
        {area.name}
      </option>
    ))}
  </select>
  {/* Filtro de Categoria */}
  <select
    value={selectedCategoryFilterId}
    onChange={(e) => setSelectedCategoryFilterId(e.target.value)}
    style={{...inputStyle, width: '100%', flexShrink: 0}}
    disabled={selectedAreaFilterId && filteredCategoriesForArea.length === 0}
  >
    <option value="">
      {selectedAreaFilterId 
        ? (filteredCategoriesForArea.length === 0 
            ? "Nenhuma categoria nesta área" 
            : "Filtrar por Categoria...")
        : "Filtrar por Categoria..."
      }
    </option>
    {filteredCategoriesForArea.map(cat => (
      <option key={cat.id} value={cat.id}>
        {cat.name}
      </option>
    ))}
    <option value={NO_CATEGORY_KEY}>Sem Categoria</option>
  </select>
</div>

                                        
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '40vh', overflowY: 'auto' }}>
                                            {Object.entries(availableDictionaryOptions).length > 0 ? (
                                                Object.entries(availableDictionaryOptions).map(([categoryName, competenciesInGroup]) => (
                                                <li key={categoryName}>
                                                    <h4 style={{ 
                                                        margin: '16px 0 8px 0', 
                                                        color: '#1e293b', 
                                                        paddingBottom: '8px', 
                                                        borderBottom: '2px solid #f1f5f9',
                                                        fontSize: '1.1rem',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {categoryName}
                                                    </h4>
                                                        {competenciesInGroup.map(opt => {
                                                            const area = areaMap[opt.areaId];
                                                            return (
                                                                <div 
                                                                    key={opt.id || opt.nome} 
                                                                    onClick={() => toggleCompetencySelection(opt)} 
                                                                    style={{ 
                                                                        padding: '12px', 
                                                                        cursor: 'pointer', 
                                                                        borderRadius: '8px', 
                                                                        borderBottom: '1px solid #f1f5f9',
                                                                        backgroundColor: isCompetencySelected(opt) ? '#e0f2fe' : 'transparent',
                                                                        transition: 'background-color 0.2s'
                                                                    }} 
                                                                    onMouseEnter={(e) => {
                                                                        if (!isCompetencySelected(opt)) {
                                                                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (!isCompetencySelected(opt)) {
                                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                                        } else {
                                                                            e.currentTarget.style.backgroundColor = '#e0f2fe';
                                                                        }
                                                                    }}
                                                                >
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                                                                            {selectedCompetencies.size > 0 && (
                                                                                <input 
                                                                                    type="checkbox" 
                                                                                    checked={isCompetencySelected(opt)}
                                                                                    readOnly
                                                                                    style={{ 
                                                                                        width: '18px', 
                                                                                        height: '18px', 
                                                                                        cursor: 'pointer',
                                                                                        accentColor: '#2563eb',
                                                                                        marginTop: '2px',
                                                                                        pointerEvents: 'none'
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            <div style={{ flex: 1 }}>
                                                                                <span style={{ color: '#1e293b', fontWeight: '500' }}>{opt.nome}</span>
                                                                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>{opt.descricao}</p>
                                                                            </div>
                                                                        </div>
                                                                        {area && (
                                                                            <span style={{
                                                                                padding: '2px 8px', 
                                                                                borderRadius: '4px', 
                                                                                backgroundColor: area.color || '#ccc',
                                                                                color: '#fff', 
                                                                                fontSize: '11px', 
                                                                                fontWeight: 'bold', 
                                                                                flexShrink: 0,
                                                                                marginLeft: '8px'
                                                                            }}>
                                                                                {area.name}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </li>
                                                ))
                                            ) : (
                                                <p style={{textAlign: 'center', color: '#64748b', padding: '20px 0'}}>Nenhuma competência disponível no dicionário com os filtros selecionados.</p>
                                            )}
                                        </ul>
                                    </div>
                                )}

                                {pickerView === 'create' && (
                                    <div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            {/* Seletor de Área */}
                                            <div>
                                                <label style={formLabelStyle}>Área da Competência</label>
                                                <select
                                                    style={inputStyle}
                                                    value={newCompetency.areaId}
                                                    onChange={(e) => setNewCompetency(p => ({...p, areaId: e.target.value}))}
                                                >
                                                    <option value="">Selecione uma Área (Opcional)</option>
                                                    {sortedAreas.map(area => (
                                                        <option key={area.id} value={area.id}>
                                                            {area.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {/* Seletor de Categoria */}
                                            <div>
                                                <label style={formLabelStyle}>Categoria da Competência</label>
                                                <select
                                                    style={inputStyle}
                                                    value={newCompetency.categoriaId}
                                                    onChange={(e) => setNewCompetency(p => ({...p, categoriaId: e.target.value}))}
                                                >
                                                    <option value="">Selecione uma Categoria (Opcional)</option>
                                                    {sortedCategories.map(cat => (
                                                        <option key={cat.id} value={cat.id}>
                                                            {cat.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <label style={{...formLabelStyle, marginTop: '16px'}}>Nome da Competência *</label>
                                        <input type="text" placeholder="Ex: Análise de Métricas" style={inputStyle} value={newCompetency.nome} onChange={(e) => setNewCompetency(p => ({...p, nome: e.target.value}))} />
                                        <label style={{...formLabelStyle, marginTop: '16px'}}>Descrição *</label>
                                        <textarea placeholder="Descreva o que significa ter esta competência..." style={textareaStyle} value={newCompetency.descricao} onChange={(e) => setNewCompetency(p => ({...p, descricao: e.target.value}))} />
                                        
                                        <button onClick={handleCreateNew} style={{ ...primaryButtonStyle, backgroundColor: '#16a34a', width: '100%', marginTop: '16px' }} disabled={!newCompetency.nome.trim() || !newCompetency.descricao.trim()}>
                                            Adicionar Nova Competência
                                        </button>
                                    </div>
                                )}

                                                        {selectedCompetencies.size > 0 && (
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'flex-end', 
                                    marginTop: '24px',
                                    paddingTop: '16px',
                                    borderTop: '1px solid #e2e8f0'
                                }}>
                                    <button 
                                        onClick={handleAddSelectedCompetencies}
                                        style={{
                                            ...primaryButtonStyle,
                                            backgroundColor: '#16a34a',
                                            padding: '12px 24px'
                                        }}
                                    >
                                        Adicionar {selectedCompetencies.size} Selecionada{selectedCompetencies.size > 1 ? 's' : ''}
                                    </button>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }




const JobDescriptionModal = ({
    isOpen,
    onClose,
    node,
    onSave,
    isGerenteViewer,
    dicionarioCompetencias,
    token,
    showConfirmation,
    handleFetchError,
    areas,
    categories,
    // 👇 ADICIONE ESTES:
    canEdit,
    revisionContext
    // 👆
}) => {

        // Função para obter o estado inicial vazio
        const getEmptyState = () => ({
            cargo: '',
            descricaoSumaria: '',
            atividades: '',
            competenciasTecnicasObrigatorias: '',
            competenciasTecnicasDesejaveis: '',
            competenciasTecnicas: [],
            outrasCompetencias: [],
            competenciasComportamentais: [],
            isGerencial: false,
        });

        // Função para obter o estado inicial baseado no nó selecionado
        const getInitialStateFromNode = useCallback(() => {
            if (!node) return getEmptyState();
            const formatCompetenciesArray = (competencies) => {
                if (!Array.isArray(competencies)) return [];
                return competencies.map(item =>
                    typeof item === 'string' ? { nome: item, descricao: '' } : item
                );
            };
            return {
                cargo: node.data.cargo || '',
                descricaoSumaria: node.data.descricaoSumaria || '',
                atividades: node.data.atividades || '',
                competenciasTecnicasObrigatorias: node.data.competenciasTecnicasObrigatorias || '',
                competenciasTecnicasDesejaveis: node.data.competenciasTecnicasDesejaveis || '',
                competenciasTecnicas: formatCompetenciesArray(node.data.competenciasTecnicas),
                outrasCompetencias: formatCompetenciesArray(node.data.outrasCompetencias),
                isGerencial: node.data.isGerencial || false,
            };
        }, [node]);

        // Estados do componente
        const [jobData, setJobData] = useState(getEmptyState());
        const [jobDescriptionTemplates, setJobDescriptionTemplates] = useState([]);
        const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
        const [selectedTemplate, setSelectedTemplate] = useState(null);
        const [isSaving, setIsSaving] = useState(false);
        const [isPromptOpen, setIsPromptOpen] = useState(false);
        const [promptConfig, setPromptConfig] = useState({ title: '', message: '', onConfirm: () => {} });

        useEffect(() => {
            if (isOpen) {
                setJobData(getInitialStateFromNode());
                setSelectedTemplate(null);
                setIsLoadingTemplates(true);

                fetch(`${process.env.REACT_APP_API_URL}/job-description-templates`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(handleFetchError)
                .then(data => {
                    setJobDescriptionTemplates(data || []);
                })
                .catch(error => console.error("Erro ao buscar modelos de JD:", error.message))
                .finally(() => setIsLoadingTemplates(false));
            }
        }, [isOpen, getInitialStateFromNode, token, handleFetchError]);

        // CSS Styles injetados (Slider e Animação)
        useEffect(() => {
            const style = document.createElement('style');
            style.textContent = `
                .custom-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 24px; height: 24px; border-radius: 50%; background: #1e40af; cursor: pointer; box-shadow: 0 2px 8px rgba(30, 64, 175, 0.4); transition: all 0.2s ease; border: 3px solid white; }
                .custom-slider::-webkit-slider-thumb:hover { transform: scale(1.2); box-shadow: 0 4px 12px rgba(30, 64, 175, 0.6); }
                .custom-slider::-moz-range-thumb { width: 24px; height: 24px; border-radius: 50%; background: #1e40af; cursor: pointer; box-shadow: 0 2px 8px rgba(30, 64, 175, 0.4); transition: all 0.2s ease; border: 3px solid white; }
                .custom-slider::-moz-range-thumb:hover { transform: scale(1.2); box-shadow: 0 4px 12px rgba(30, 64, 175, 0.6); }
                .custom-slider::-moz-range-track { background: transparent; border: none; }
                @keyframes blinkRed { 0%, 100% { border-color: #fde68a; background-color: #ffffff; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } 25% { border-color: #dc2626; background-color: #fee2e2; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3); } 50% { border-color: #ef4444; background-color: #fecaca; box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.4); } 75% { border-color: #dc2626; background-color: #fee2e2; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3); } }
                .blink-red { animation: blinkRed 0.6s ease-in-out 4 !important; border-width: 2px !important; }
            `;
            document.head.appendChild(style);
            return () => { if (document.head.contains(style)) document.head.removeChild(style); };
        }, []);

        if (!isOpen || !node) return null;

        const handleTemplateChange = (e) => {
            const templateId = e.target.value;
            if (!templateId) {
                setJobData(getInitialStateFromNode());
                setSelectedTemplate(null);
                return;
            }
            const template = jobDescriptionTemplates.find(t => t.id.toString() === templateId);
            if (template && template.jobData) {
                setJobData({
                    ...getEmptyState(),
                    ...template.jobData,
                    competenciasTecnicas: Array.isArray(template.jobData.competenciasTecnicas) ? template.jobData.competenciasTecnicas : [],
                    outrasCompetencias: Array.isArray(template.jobData.outrasCompetencias) ? template.jobData.outrasCompetencias : [],
                });
                setSelectedTemplate(template);
            }
        };

        const handleFormChange = (e) => {
            const { name, value } = e.target;
            setJobData(prev => ({ ...prev, [name]: value }));
        };

        const handleGerencialChange = (isGerencialValue) => {
            setJobData(prev => ({ ...prev, isGerencial: isGerencialValue }));
        };

        const addCompetency = (type, competencyToAdd) => {
            if (!canEdit) return;
            setJobData(prev => ({
                ...prev,
                [type]: [...(prev[type] || []), competencyToAdd]
            }));
        };

        const removeCompetency = (type, indexToRemove) => {
            if (!canEdit) return;
            setJobData(prev => ({
                ...prev,
                [type]: (prev[type] || []).filter((_, i) => i !== indexToRemove)
            }));
        };

        const handleSaveAsNewTemplate = (templateName) => {
            if (!templateName || !canEdit) return;
            setIsSaving(true);
            fetch(`${process.env.REACT_APP_API_URL}/job-description-templates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: templateName, jobData: jobData })
            })
            .then(handleFetchError)
            .then(newTemplate => {
                if (newTemplate) {
                    setJobDescriptionTemplates(prev => [...prev, newTemplate]);
                    setSelectedTemplate(newTemplate);
                    setIsPromptOpen(false);
                }
            })
            .catch(error => console.error("Erro ao salvar novo modelo:", error.message))
            .finally(() => setIsSaving(false));
        };

        const handleUpdateTemplate = () => {
            if (!selectedTemplate || !canEdit) return;
            setIsSaving(true);
            fetch(`${process.env.REACT_APP_API_URL}/job-description-templates/${selectedTemplate.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: jobData.cargo || selectedTemplate.name, jobData: jobData })
            })
            .then(handleFetchError)
            .then(updatedTemplate => {
                if (updatedTemplate) {
                    setJobDescriptionTemplates(prev => prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t));
                    setSelectedTemplate(updatedTemplate);
                }
            })
            .catch(error => console.error("Erro ao atualizar modelo:", error.message))
            .finally(() => setIsSaving(false));
        };

        const handleDeleteTemplate = () => {
            if (!selectedTemplate || !canEdit) return;
            showConfirmation(`Tem a certeza que deseja excluir o modelo "${selectedTemplate.name}"?`, () => {
                setIsSaving(true);
                fetch(`${process.env.REACT_APP_API_URL}/job-description-templates/${selectedTemplate.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(handleFetchError)
                .then(() => {
                    setJobDescriptionTemplates(prev => prev.filter(t => t.id !== selectedTemplate.id));
                    setJobData(getInitialStateFromNode());
                    setSelectedTemplate(null);
                })
                .catch(error => console.error("Erro ao excluir modelo:", error.message))
                .finally(() => setIsSaving(false));
            });
        };

        const handleApplyToNode = async () => {
            if (!canEdit || isSaving) return;
            setIsSaving(true);
            try {
                await onSave(jobData);
            } finally {
                setIsSaving(false);
            }
        };

        const openSaveAsNewPrompt = () => {
            setPromptConfig({
                title: 'Salvar Novo Modelo de Job Description',
                message: 'Insira um nome para este novo modelo. Este nome será usado para o identificar na lista de seleção.',
                placeholder: `Ex: ${jobData.cargo || 'Modelo de Cargo'}`,
                onConfirm: handleSaveAsNewTemplate,
            });
            setIsPromptOpen(true);
        };

        // --- CONSTANTES DE ESTILO ---
        const professionalModalContentStyle = { background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)', padding: '0', borderRadius: '16px', width: '95%', maxWidth: '1100px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)', margin: 0, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
        const modalHeaderStyle = { background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', padding: '28px 32px', borderBottom: '3px solid #ff834f', color: 'white', position: 'relative' };
        const modalBodyStyle = { padding: '32px', overflowY: 'auto', flexGrow: 1, backgroundColor: '#ffffff' };
        const modalFooterStyle = { padding: '20px 32px', borderTop: '2px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
        const sectionCardStyle = { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', transition: 'box-shadow 0.3s ease' };
        const sectionHeaderStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #f1f5f9' };
        const sectionIconStyle = { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' };
        
        // ✨ CORREÇÃO AQUI: Adicionado width: '100%' e boxSizing: 'border-box'
        const improvedInputStyle = { 
            ...baseInputStyle, 
            fontSize: '15px', 
            padding: '12px 16px', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0', 
            transition: 'all 0.2s ease', 
            backgroundColor: '#ffffff',
            width: '100%', 
            boxSizing: 'border-box' 
        };

        const improvedTextareaStyle = { ...textareaStyle, fontSize: '15px', padding: '12px 16px', borderRadius: '10px', border: '2px solid #e2e8f0', transition: 'all 0.2s ease', backgroundColor: '#ffffff', lineHeight: 1.6, width: '100%', boxSizing: 'border-box' };
        
        const InfoIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>;
        const RequirementsIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
        const CompetencyIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>;

        const tabs = [
            {
                label: 'Informações Gerais',
                content: (
                    <div>
                        {(!node || node?.data?.status !== 'pending') && (
                            <div style={{...sectionCardStyle, background: 'linear-gradient(to right, #fef3c7 0%, #fef9c3 100%)', borderColor: '#fbbf24'}}>
                                <div style={sectionHeaderStyle}>
                                    <div style={{...sectionIconStyle, backgroundColor: '#fbbf24', color: 'white'}}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    </div>
                                    <div style={{flexGrow: 1}}>
                                        <h3 style={{margin: 0, fontSize: '1.1rem', color: '#78350f'}}>Modelos de Job Description</h3>
                                        <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#92400e'}}>Reutilize templates existentes ou crie novos para agilizar o processo</p>
                                    </div>
                                </div>
                                <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                                    <select style={{...improvedInputStyle, flexGrow: 1}} value={selectedTemplate?.id || ''} onChange={handleTemplateChange} disabled={isLoadingTemplates || !canEdit || isSaving}>
                                        <option value="">{isLoadingTemplates ? 'A carregar modelos...' : 'Selecionar um modelo...'}</option>
                                        {jobDescriptionTemplates.map(template => (
                                            <option key={template.id} value={template.id}>{template.name}</option>
                                        ))}
                                    </select>
                                    {selectedTemplate && canEdit && (
                                        <button onClick={handleDeleteTemplate} style={{ ...iconButtonStyle, backgroundColor: '#fee2e2', color: '#dc2626', border: '2px solid #fecaca' }} disabled={isSaving} title={`Excluir modelo "${selectedTemplate.name}"`}>
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                                <div style={{...sectionIconStyle, backgroundColor: '#3b82f6', color: 'white'}}><InfoIcon /></div>
                                <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Informações do Cargo</h3>
                            </div>
                            <div style={{marginBottom: '20px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>Cargo/Função *</label>
                                <input type="text" name="cargo" style={improvedInputStyle} value={jobData.cargo} onChange={handleFormChange} placeholder="Ex: Analista de Marketing Digital" disabled={!canEdit} />
                            </div>
                            <div style={{marginBottom: '20px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>Descrição Sumária</label>
                                <textarea name="descricaoSumaria" style={{...improvedTextareaStyle, minHeight: '100px'}} value={jobData.descricaoSumaria} onChange={handleFormChange} placeholder="Descreva resumidamente as responsabilidades..." disabled={!canEdit} />
                            </div>
                            <div>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>Atividades e Responsabilidades</label>
                                <textarea name="atividades" style={{...improvedTextareaStyle, minHeight: '180px'}} value={jobData.atividades} onChange={handleFormChange} placeholder="Liste as principais atividades..." disabled={!canEdit} />
                            </div>
                            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '2px dashed #cbd5e1' }}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '12px', display: 'block'}}>Tipo de Vaga</label>
                                <div style={{display: 'flex', gap: '24px'}}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEdit ? 'pointer' : 'default'}}>
                                        <input type="radio" name="tipoVagaJD" checked={jobData.isGerencial} onChange={() => handleGerencialChange(true)} disabled={!canEdit} style={{width: '20px', height: '20px', accentColor: '#3b82f6'}} />
                                        <span style={{fontWeight: '500', color: jobData.isGerencial ? '#1e293b' : '#64748b'}}>Vaga Gerencial</span>
                                    </label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: canEdit ? 'pointer' : 'default'}}>
                                        <input type="radio" name="tipoVagaJD" checked={!jobData.isGerencial} onChange={() => handleGerencialChange(false)} disabled={!canEdit} style={{width: '20px', height: '20px', accentColor: '#3b82f6'}} />
                                        <span style={{fontWeight: '500', color: !jobData.isGerencial ? '#1e293b' : '#64748b'}}>Vaga Não Gerencial</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            },
            {
                label: 'Requisitos',
                content: (
                    <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                            <div style={{...sectionIconStyle, backgroundColor: '#10b981', color: 'white'}}><RequirementsIcon /></div>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Requisitos da Vaga</h3>
                        </div>
                        <div style={{marginBottom: '24px'}}>
                            <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px', color: '#dc2626'}}>Requisitos Obrigatórios *</label>
                            <textarea name="competenciasTecnicasObrigatorias" style={{...improvedTextareaStyle, minHeight: '140px', borderColor: '#fecaca'}} value={jobData.competenciasTecnicasObrigatorias} onChange={handleFormChange} placeholder="Formação, experiência..." disabled={!canEdit} />
                        </div>
                        <div>
                            <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px', color: '#16a34a'}}>Requisitos Desejáveis</label>
                            <textarea name="competenciasTecnicasDesejaveis" style={{...improvedTextareaStyle, minHeight: '140px', borderColor: '#bbf7d0'}} value={jobData.competenciasTecnicasDesejaveis} onChange={handleFormChange} placeholder="Diferenciais..." disabled={!canEdit} />
                        </div>
                    </div>
                )
            },
            {
                label: 'Competências Técnicas',
                content: (
                    <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                            <div style={{...sectionIconStyle, backgroundColor: '#8b5cf6', color: 'white'}}><CompetencyIcon /></div>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Competências Técnicas</h3>
                        </div>
                        <SelectOrCreateCompetencyEditor competencyType="competenciasTecnicas" title="Competência Técnica" competencies={jobData.competenciasTecnicas} canEdit={canEdit} onAddCompetency={addCompetency} onRemoveCompetency={removeCompetency} dictionaryOptions={dicionarioCompetencias?.tecnicas || []} areas={areas} categories={categories} revisionContext={revisionContext} />
                    </div>
                )
            },
            {
                label: 'Outras Competências',
                content: (
                    <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                            <div style={{...sectionIconStyle, backgroundColor: '#ec4899', color: 'white'}}><CompetencyIcon /></div>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Outras Competências</h3>
                        </div>
                        <SelectOrCreateCompetencyEditor competencyType="outrasCompetencias" title="Outra Competência" competencies={jobData.outrasCompetencias} canEdit={canEdit} onAddCompetency={addCompetency} onRemoveCompetency={removeCompetency} dictionaryOptions={dicionarioCompetencias?.outras || []} areas={areas} categories={categories} revisionContext={revisionContext}/>
                    </div>
                )
            },
            {
                label: 'Competências Comportamentais',
                content: (
                    <>
                        <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                                <div style={{...sectionIconStyle, backgroundColor: "#14b8a6", color: "white"}}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                                </div>
                                <h3 style={{ margin: 0, fontSize: "1.1rem", color: "#1e293b" }}>Competências Corporativas</h3>
                            </div>
                            {dicionarioCompetencias?.corporativas?.length > 0 ? (
                                <div style={{ display: "grid", gap: 12 }}>
                                    {dicionarioCompetencias.corporativas.map((comp) => (
                                        <div key={comp.id} style={{ background: "linear-gradient(to right, #f0fdfa, #ccfbf1)", padding: 16, borderRadius: 10, border: "1px solid #99f6e4" }}>
                                            <strong style={{ color: "#115e59", fontSize: "1rem", display: "block", marginBottom: 6 }}>{comp.nome}</strong>
                                            <p style={{ margin: 0, fontSize: "0.9em", color: "#134e4a", lineHeight: 1.6 }}>{comp.descricao}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : <p style={{ color: "#64748b", fontSize: "0.95em", fontStyle: "italic", textAlign: "center", padding: 20 }}>Nenhuma competência corporativa definida.</p>}
                        </div>
                        {jobData.isGerencial && (
                            <div style={sectionCardStyle}>
                                <div style={sectionHeaderStyle}>
                                    <div style={{...sectionIconStyle, backgroundColor: "#f59e0b", color: "white"}}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: "1.1rem", color: "#1e293b" }}>Competências Gerenciais</h3>
                                </div>
                                {dicionarioCompetencias?.gerenciais?.length > 0 ? (
                                    <div style={{ display: "grid", gap: 12 }}>
                                        {dicionarioCompetencias.gerenciais.map((comp) => (
                                            <div key={comp.id} style={{ background: "linear-gradient(to right, #fffbeb, #fef3c7)", padding: 16, borderRadius: 10, border: "1px solid #fde68a" }}>
                                                <strong style={{ color: "#78350f", fontSize: "1rem", display: "block", marginBottom: 6 }}>{comp.nome}</strong>
                                                <p style={{ margin: 0, fontSize: "0.9em", color: "#92400e", lineHeight: 1.6 }}>{comp.descricao}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p style={{ color: "#64748b", fontSize: "0.95em", fontStyle: "italic", textAlign: "center", padding: 20 }}>Nenhuma competência gerencial definida.</p>}
                            </div>
                        )}
                    </>
                )
            }
        ];

        return (
            <>
                <ModalPortal>
                    <PromptModal isOpen={isPromptOpen} onClose={() => setIsPromptOpen(false)} onConfirm={promptConfig.onConfirm} title={promptConfig.title} message={promptConfig.message} placeholder={promptConfig.placeholder} />
                </ModalPortal>
                <ModalPortal>
                    <div style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1300, backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '24px' }}>
                        <div style={professionalModalContentStyle}>
                            <div style={modalHeaderStyle}>
                                <button onClick={onClose} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}><XIcon style={{width: '20px', height: '20px'}} /></button>
                                <h2 style={{margin: 0, fontSize: '1.75rem', fontWeight: '700'}}>Job Description</h2>
                                <p style={{margin: '8px 0 0', fontSize: '1rem', opacity: 0.9}}>Cargo: <strong>{jobData.cargo || node?.data?.cargo || 'Não definido'}</strong></p>
                            </div>
                            <div style={modalBodyStyle}>
                                <TabsComponent tabs={tabs} />
                            </div>
<div style={modalFooterStyle}>
    <div style={{display: 'flex', gap: '12px'}}>
        {/* Se for Admin salva direto, se for Gestor ele solicita */}
        {canEdit && !isGerenteViewer ? (
            <button onClick={handleApplyToNode} disabled={isSaving} style={evalPrimaryBtnStyle}>{isSaving ? 'Salvando...' : '✓ Salvar Alterações'}</button>
        ) : isGerenteViewer ? (
            <button 
                onClick={() => onSave(jobData)} // A função handleSaveJobDetails no pai já sabe se é solicitação
                style={{ ...evalPrimaryBtnStyle, backgroundColor: '#f59e0b' }}
            >
                ⚠️ Solicitar Alteração de JD
            </button>
        ) : null}
    </div>
</div>
                        </div>
                    </div>
                </ModalPortal>
            </>
        );
    }; 
// --- HELPERS DE ESTILO E ÍCONES ---
const competencyStyles = {
    'Técnica': { color: '#7c3aed', bgLight: '#ede9fe', icon: ShieldCheck },
    'Corporativa': { color: '#0d9488', bgLight: '#ccfbf1', icon: Wand2 },
    'Gerencial': { color: '#f59e0b', bgLight: '#fef3c7', icon: Users },
    'Outra': { color: '#db2777', bgLight: '#fce7f3', icon: Shapes },
    'Histórico': { color: '#64748b', bgLight: '#f1f5f9', icon: FilePenLine }
};

const getCompetencyStyle = (tipo) => competencyStyles[tipo] || competencyStyles['Outra'];

// --- ESTILOS EXCLUSIVOS ---
const evalModalContainerStyle = { background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '1000px', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' };
const evalHeaderStyle = { background: 'linear-gradient(to right, #1e293b, #334155)', padding: '24px 32px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const evalContentStyle = { padding: '32px', overflowY: 'auto', flexGrow: 1, backgroundColor: '#f8fafc', paddingBottom: '80px' };
const evalFooterStyle = { padding: '20px 32px', borderTop: '1px solid #e2e8f0', backgroundColor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const evalPrimaryBtnStyle = { padding: '10px 18px', fontSize: '14px', cursor: 'pointer', border: 'none', borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#383838ff', color: 'white' };
const evalGhostBtnStyle = { padding: '10px 18px', fontSize: '14px', cursor: 'pointer', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: 'transparent', color: '#334155' };

const CompetencyEvaluationTable = ({ 
    id, 
    competency, 
    onUpdateRating, 
    onUpdateFact, 
    onAddFact, 
    onRemoveFact, 
    onEnterSelectionMode, 
    onToggleFactSelection, 
    selectedFactIndices = [], 
    isSelectionMode = false, 
    evaluationData, 
    canEdit, 
    isAppendOnly = false, 
    hasError = false,
    onSwap, 
    allowSwap = false 
}) => {
    const currentRating = evaluationData?.rating;
    const facts = evaluationData?.facts || [];
    const style = getCompetencyStyle(competency.tipo);
    const IconComponent = style.icon;
    
    const [activeMenuIndex, setActiveMenuIndex] = useState(null);

    const ratingLevels = {
        1: { text: 'Insatisfatório', color: '#ef4444' },
        2: { text: 'Necessita Melhoria', color: '#f97316' },
        3: { text: 'Satisfatório', color: '#facc15' },
        4: { text: 'Bom', color: '#a3e635' },
        5: { text: 'Excelente', color: '#22c55e' }
    };

    return (
        <div 
            id={id} 
            // A classe serve apenas para a animação de "pulsar"
            className={hasError ? 'card-error-highlight' : ''}
            style={{ 
                backgroundColor: '#ffffff', 
                borderRadius: '12px', 
                marginBottom: '20px', 
                position: 'relative',
                transition: 'all 0.3s ease',
                
                // 👇 MUDANÇA FUNDAMENTAL DE LAYOUT 👇
                
                // 1. A BORDA REAL:
                // Se tiver erro, é vermelha e envolve TUDO (incluindo a listra).
                // Se não, é cinza sutil.
                border: hasError ? '2px solid #ef4444' : '1px solid #e2e8f0',

                // 2. A LISTRA COLORIDA (Categoria):
                // Agora desenhamos a listra como uma sombra INTERNA (inset).
                // Isso faz com que ela fique "dentro" da borda vermelha.
                // Sintaxe: inset [largura] 0 0 0 [cor]
                boxShadow: hasError 
                    ? `inset 6px 0 0 0 ${style.color}, 0 0 0 4px rgba(239, 68, 68, 0.15)` // Listra + Glow Vermelho Externo
                    : `inset 6px 0 0 0 ${style.color}, 0 4px 6px -1px rgba(0, 0, 0, 0.05)`, // Listra + Sombra Padrão
                
                // 3. PADDING AJUSTADO:
                // Como a listra agora é interna, precisamos empurrar o conteúdo para a direita
                // Padding padrão (24px) + Largura da Listra (6px) + Respiro (4px) = 34px
                padding: '24px 24px 24px 34px'
            }}
        >
            {/* Cabeçalho */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: style.bgLight, color: style.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconComponent size={20} /></div>
                <div style={{ flexGrow: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ color: '#1e293b', fontSize: '1.1rem' }}>{competency.nome}</strong>
                        <span style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', color: style.color, backgroundColor: style.bgLight, padding: '2px 6px', borderRadius: '4px' }}>{competency.tipo}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>{competency.descricao}</p>
                </div>

                {/* BOTÃO DE TROCAR COMPETÊNCIA */}
                {canEdit && allowSwap && !isAppendOnly && (
                    <button 
                        onClick={() => onSwap(competency)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 12px', borderRadius: '6px',
                            border: '1px solid #e2e8f0', backgroundColor: '#fff',
                            color: '#64748b', fontSize: '0.85rem', fontWeight: '500',
                            cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
                        title="Trocar esta competência por outra"
                    > 
                        <RefreshCw size={14} /> Trocar Competência
                    </button>
                )}
            </div>

            {/* Notas */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: hasError ? '#ef4444' : '#334155', marginBottom: '12px' }}>Nível de Desempenho Atual {hasError && '*'}</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {Object.entries(ratingLevels).map(([level, { text, color }]) => {
                        const isSelected = currentRating === parseInt(level, 10);
                        return (
                            <label key={level} style={{ flex: 1, minWidth: '100px', cursor: (canEdit && !isAppendOnly) ? 'pointer' : 'default' }}>
                                <input type="radio" name={`rating-${competency.nome.replace(/\s+/g, '-')}`} value={level} checked={isSelected} onChange={(e) => onUpdateRating(competency.nome, parseInt(e.target.value, 10))} disabled={!canEdit || isAppendOnly} style={{ display: 'none' }} />
                                <div style={{ padding: '10px', backgroundColor: isSelected ? color : '#f8fafc', color: isSelected ? 'white' : '#64748b', border: isSelected ? `2px solid ${color}` : '1px solid #e2e8f0', borderRadius: '8px', textAlign: 'center', transition: 'all 0.2s', opacity: (!canEdit || isAppendOnly) && !isSelected ? 0.5 : 1 }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{level}</div>
                                    <div style={{ fontSize: '0.7rem' }}>{text}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Fatos */}
            <div>
                <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#334155', marginBottom: '12px', display: 'block' }}>Evidências / Fatos Observados</label>
                {facts.length === 0 && <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>Nenhuma evidência registrada.</div>}
                
                {facts.map((fact, i) => {
                    const isExisting = isAppendOnly && (fact.factDate || fact.fact);
                    const disabled = !canEdit || isExisting;
                    const isChecked = selectedFactIndices.includes(i);
                    const isMenuOpen = activeMenuIndex === i;

                    return (
                        <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px', position: 'relative' }}>
                            {isSelectionMode && (
                                <div style={{ paddingTop: '12px' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={isChecked} 
                                        onChange={() => onToggleFactSelection(competency.nome, i)}
                                        style={{ width: '20px', height: '20px', accentColor: '#dc2626', cursor: 'pointer' }}
                                    />
                                </div>
                            )}

                            <div style={{ flex: 1, border: isChecked ? '2px solid #dc2626' : '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', backgroundColor: isChecked ? '#fef2f2' : (isExisting ? '#f9fafb' : '#fff'), transition: 'all 0.2s' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: '12px', marginBottom: '8px' }}>
                                    <input type="date" style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1' }} value={fact.factDate || ''} onChange={(e) => onUpdateFact(competency.nome, i, 'factDate', e.target.value)} disabled={disabled} />
                                    <input type="text" style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1' }} value={fact.fact || ''} onChange={(e) => onUpdateFact(competency.nome, i, 'fact', e.target.value)} placeholder="Título do fato" disabled={disabled} />
                                    
                                    {canEdit && !isExisting && !isSelectionMode && (
                                        <div style={{ position: 'relative' }}>
                                            <button 
                                                onClick={() => setActiveMenuIndex(isMenuOpen ? null : i)}
                                                style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', padding: '6px', cursor: 'pointer' }} 
                                                title="Opções"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            {isMenuOpen && (
                                                <>
                                                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setActiveMenuIndex(null)}></div>
                                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 999, width: '140px', overflow: 'hidden' }}>
                                                        <button 
                                                            onClick={() => { setActiveMenuIndex(null); onRemoveFact(competency.nome, i); }}
                                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'white', cursor: 'pointer', fontSize: '0.85rem', color: '#dc2626', borderBottom: '1px solid #f1f5f9' }}
                                                        >
                                                            🗑️ Apagar
                                                        </button>
                                                        <button 
                                                            onClick={() => { setActiveMenuIndex(null); onEnterSelectionMode(competency.nome, i); }}
                                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'white', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}
                                                        >
                                                            ✅ Selecionar
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <textarea style={{ width: '100%', minHeight: '50px', padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', resize: 'vertical' }} value={fact.factDescription || ''} onChange={(e) => onUpdateFact(competency.nome, i, 'factDescription', e.target.value)} placeholder="Detalhes..." disabled={disabled} />
                            </div>
                        </div>
                    );
                })}
                {canEdit && !isSelectionMode && (
                    <button onClick={() => onAddFact(competency.nome)} style={{ marginTop: '8px', fontSize: '0.85rem', fontWeight: '600', color: style.color, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><Plus size={16} /> Adicionar Novo Fato</button>
                )}
            </div>
        </div>
    );
};
const HistoryAverageCard = ({ label, value, color }) => (
    <div style={{
        textAlign: 'center',
        padding: '12px',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        minWidth: '110px',
        flex: 1
    }}>
        <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: color,
            lineHeight: '1',
            marginBottom: '6px'
        }}>
            {value}
        </div>
        <div style={{
            fontSize: '0.75rem',
            color: '#64748b',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        }}>
            {label}
        </div>
    </div>
);
// --- COMPONENTES AUXILIARES DE UI (Definidos fora para evitar erros de escopo) ---

const TabButton = ({ label, isActive }) => (
    <div style={{ 
        padding: '10px 16px', 
        borderBottom: isActive ? '2px solid #3b82f6' : '2px solid transparent', 
        color: isActive ? '#3b82f6' : '#94a3b8', 
        fontWeight: isActive ? '600' : '500', 
        cursor: 'default' 
    }}>
        {label}
    </div>
);

const AutoSaveFeedback = ({ status, lastTime, isEditingHistory }) => {
   
    if (status === 'typing') return <div style={{ color: '#d97706', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><span className="blink-red" style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }}></span> Salvando...</div>;
    if (status === 'saved' && lastTime) return <div style={{ color: '#16a34a', fontSize: '0.85rem' }}>☁️ Salvo às {lastTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>;
    return <div style={{ minWidth: '150px' }}></div>;
};

const EvaluationSheetModal = ({ 
    isOpen, onClose, node, dicionarioCompetencias, canEdit, onSave, onOpenResults,
    isGerenteViewer, isAdminOrEditor, setJdEditMode, setSelectedNode, 
    setIsEvaluationSheetModalOpen, setIsJobDescriptionModalOpen,
    step, setStep, 
    evaluations, setEvaluations, 
    period, setPeriod,
    selectedCompetencies, setSelectedCompetencies,
    availableCompetencies, setAvailableCompetencies, 
    revisionContext, setRevisionContext,
    handleRequestEvalRevision,
    handleStartEvalRevision,
    promptConfig, setPromptConfig,
    isPromptOpen, setIsPromptOpen 
}) => {



    const [isReady, setIsReady] = useState(false);
    
    // Estados UI Gerais
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveError, setSaveError] = useState('');
    const [lastSavedTime, setLastSavedTime] = useState(null);
    
    // Modais de Confirmação e Alertas
    const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
    const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);
    const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  
    
    // Estados de Edição/Histórico
    const [factToDelete, setFactToDelete] = useState(null);
    const [historyItemToDelete, setHistoryItemToDelete] = useState(null);
    const [viewingHistoryItem, setViewingHistoryItem] = useState(null);
    const [historyIndexEditing, setHistoryIndexEditing] = useState(null); 
    const [isEditingAllowed, setIsEditingAllowed] = useState(false);
    const [missingFields, setMissingFields] = useState([]); 
    
    // Estados de Seleção em Massa
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedFactsMap, setSelectedFactsMap] = useState({}); 

    // Estados para Troca de Competência
    const [competencyToSwap, setCompetencyToSwap] = useState(null);
    const [swapSearchTerm, setSwapSearchTerm] = useState('');

    // REF PARA O SCROLL DO MODAL
    const modalBodyRef = useRef(null);

    const saveTimeoutRef = useRef(null);
    const saveQueueRef = useRef(Promise.resolve());
    const saveVersionRef = useRef(0);
    const submittingRef = useRef(false);
    const pendingSaveRef = useRef(null);
    const historyRef = useRef([]);
    const hasInitialized = useRef(false);

    // Memoização do history para evitar warnings do useEffect
    const history = useMemo(() => node?.data?.evaluationHistory || [], [node]);

    // --- FUNÇÃO AUXILIAR PARA IDs (Blindada) ---
    const generateSafeId = useCallback((name) => {
        if (!name) return 'comp-unknown';
        return 'comp-' + name
            .toString()
            .trim()
            .toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]/g, "");
    }, []);

    // --- ESTILOS INTERNOS ---
    const sectionCardStyle = { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', transition: 'box-shadow 0.3s ease' };
    const sectionHeaderStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #f1f5f9' };
    const sectionIconStyle = { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'white' };

    // --- HELPERS ---
    const isFirstEvaluation = useMemo(() => !history || history.length === 0, [history]);

    const isUserManager = useMemo(() => {
        if (!node?.data) return false;
        const isG = node.data.isGerencial;
        return isG === true || isG === 'true' || isG === 1 || isG === '1';
    }, [node?.data]);

    const checkIsMandatory = useCallback((comp) => {
        if (!isFirstEvaluation) return false;
        if (comp.tipo === 'Corporativa') return true;
        if (isUserManager && comp.tipo === 'Gerencial') return true;
        return false;
    }, [isFirstEvaluation, isUserManager]);

    const calculateNextCycleDate = (startDateStr, periodType) => {
        const date = new Date(startDateStr);
        switch (periodType) {
            case 'Semestral': date.setMonth(date.getMonth() + 6); break;
            case 'Anual': date.setFullYear(date.getFullYear() + 1); break;
            default: date.setMonth(date.getMonth() + 6); 
        }
        return date;
    };

    // --- HANDLERS DE UX ---
    const persistEvaluation = useCallback(async (payload, isFinal = false) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
        pendingSaveRef.current = null;
        const version = ++saveVersionRef.current;
        setSaveStatus('typing');
        setSaveError('');

        // Ordena as gravações: um rascunho anterior não pode ultrapassar a finalização.
        const saving = saveQueueRef.current.catch(() => {}).then(() => onSave(payload, isFinal));
        saveQueueRef.current = saving;
        try {
            await saving;
            if (version === saveVersionRef.current) {
                setSaveStatus('saved');
                setLastSavedTime(new Date());
            }
            return true;
        } catch (error) {
            if (version === saveVersionRef.current) {
                setSaveStatus('error');
                setSaveError('Não foi possível salvar a avaliação. Os dados continuam no formulário. Tente salvar novamente.');
            }
            console.error('Erro ao salvar avaliação:', error);
            return false;
        }
    }, [onSave]);

    const handleSaveAndExit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            if (await persistEvaluation({ evaluations, period }, false)) onClose();
        } finally {
            submittingRef.current = false;
        }
    };

    const handleCloseEvaluation = async () => {
        if (submittingRef.current) return;
        if (step === 'evaluation' && historyIndexEditing === null && !revisionContext) {
            await handleSaveAndExit();
            return;
        }
        const pendingSave = pendingSaveRef.current;
        if (pendingSave && !await persistEvaluation(pendingSave.payload, pendingSave.isFinal)) return;
        try {
            await saveQueueRef.current;
            onClose();
        } catch (error) {
            // A mensagem de falha já está visível; não descarta o formulário.
        }
    };

    const handleRequestBack = () => {
        setIsBackConfirmOpen(true);
    };

    const getCompetencyStatus = useCallback((competencyName) => {
        if (!history || history.length === 0) return { locked: false, label: 'Nunca avaliada' };
        const sortedHistory = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
        for (const entry of sortedHistory) {
            if (entry.evaluations && entry.evaluations[competencyName]) {
                const nextCycleDate = calculateNextCycleDate(entry.date, entry.period);
                const today = new Date();
                if (today < nextCycleDate) {
                    return { locked: true, nextDate: nextCycleDate, label: `Próxima: ${nextCycleDate.toLocaleDateString('pt-BR')}` };
                }
                return { locked: false, label: `Disponível desde: ${nextCycleDate.toLocaleDateString('pt-BR')}` }; 
            }
        }
        return { locked: false, label: 'Disponível' }; 
    }, [history]);

    const technicalSelectionCount = useMemo(() => {
        return selectedCompetencies.filter(c => c.tipo === 'Técnica' || c.tipo === 'Outra').length;
    }, [selectedCompetencies]);

    const canProceedEvaluation = technicalSelectionCount >= 3;

    const areEvaluationsEqual = (eval1, eval2) => {
        if (!eval1 && !eval2) return true;
        if (!eval1 || !eval2) return false;
        const canonicalize = (obj) => {
            if (obj === null || typeof obj !== 'object') return obj;
            if (Array.isArray(obj)) return obj.map(canonicalize);
            return Object.keys(obj).sort().reduce((result, key) => {
                result[key] = canonicalize(obj[key]);
                return result;
            }, {});
        };
        return JSON.stringify(canonicalize(eval1)) === JSON.stringify(canonicalize(eval2));
    };

    const getCountdownText = (targetDate) => {
        const now = new Date();
        const diff = targetDate - now;
        if (diff <= 0) return null;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        return `${days} dias e ${hours} horas restantes`;
    };

    const getRatingColor = (rating) => {
        if (rating >= 5) return '#22c55e';
        if (rating >= 4) return '#84cc16';
        if (rating >= 3) return '#eab308';
        return '#ef4444';
    };

    const totalSelectedFacts = useMemo(() => {
        return Object.values(selectedFactsMap).reduce((acc, indices) => acc + indices.length, 0);
    }, [selectedFactsMap]);

    // --- EFEITOS ---
    useEffect(() => {
        if (!isOpen) {
            hasInitialized.current = false;
            setIsReady(false);
            setSaveStatus('idle');
            setMissingFields([]);
            setHistoryIndexEditing(null);
            setIsSelectionMode(false);
            setSelectedFactsMap({});
            setHistoryItemToDelete(null);
            setCompetencyToSwap(null);
            setSwapSearchTerm('');
        }
    }, [isOpen]);

    useEffect(() => {
        historyRef.current = history;
    }, [history]);

    useEffect(() => {
        if (isOpen) setSaveError('');
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
            pendingSaveRef.current = null;
            saveVersionRef.current += 1;
        };
    }, [isOpen, node?.id]);

    // --- INICIALIZAÇÃO ---
    // --- INICIALIZAÇÃO CORRIGIDA ---
    useEffect(() => {
        if (isOpen && node && !hasInitialized.current) {
            setLastSavedTime(null);
            setSaveStatus('idle');
            setHistoryIndexEditing(null);
            setIsSelectionMode(false);
            setSelectedFactsMap({});

            // 1. Preparar lista de todas as competências possíveis
            const allCompetencies = [
                ...(node.data?.competenciasTecnicas || []).map(c => ({ ...c, tipo: 'Técnica' })),
                ...(node.data?.outrasCompetencias || []).map(c => ({ ...c, tipo: 'Outra' })),
                ...(dicionarioCompetencias?.corporativas || []).map(c => ({ ...c, tipo: 'Corporativa' })),
                ...(isUserManager ? (dicionarioCompetencias?.gerenciais || []).map(c => ({ ...c, tipo: 'Gerencial' })) : [])
            ];
            setAvailableCompetencies(allCompetencies);

            const hasHistory = history && history.length > 0;
            const savedDraft = node.data?.evaluations || {};
            
            // Verifica se o rascunho tem alguma chave real (ignorando objeto vazio)
            const hasDraftData = savedDraft && Object.keys(savedDraft).length > 0;
            
            // ✨ LÓGICA DE DECISÃO DE ETAPA ✨
            if (hasHistory && !hasDraftData) {
                // Caso A: Tem histórico e NÃO tem rascunho -> Vai para Histórico (Passo 3)
                setStep('history');
                setIsEditingAllowed(false);
                setEvaluations({});
                // Não precisa selecionar competências aqui, pois é só visualização
            } 
            else if (hasDraftData) {
                // Caso B: Tem rascunho em andamento -> Vai para Avaliação (Passo 2)
                setEvaluations(savedDraft);
                
                // Recupera as competências baseadas nas chaves do rascunho
                const recoveredCompetencies = [];
                Object.keys(savedDraft).forEach(compName => {
                    const compObj = allCompetencies.find(c => c.nome === compName);
                    if (compObj) recoveredCompetencies.push(compObj);
                });

                // Garante que as obrigatórias estejam lá
                const mandatory = allCompetencies.filter(c => checkIsMandatory(c));
                mandatory.forEach(mc => {
                    if (!recoveredCompetencies.some(rc => rc.nome === mc.nome)) recoveredCompetencies.push(mc);
                });

                setSelectedCompetencies(recoveredCompetencies);
                setStep('evaluation'); 
                setIsEditingAllowed(true);
            } 
            else {
                // Caso C: Nem histórico, nem rascunho -> Novo Ciclo (Passo 1 - Seleção)
                setStep('selection');
                setIsEditingAllowed(true);
                setEvaluations({});
                
                // Pré-seleciona as obrigatórias
                const mandatory = allCompetencies.filter(c => checkIsMandatory(c));
                setSelectedCompetencies(mandatory);
            }

            if (node.data.evaluationPeriod) setPeriod(node.data.evaluationPeriod);
            
            setViewingHistoryItem(null);
            setIsSaveConfirmOpen(false);
            setIsBackConfirmOpen(false);
            setIsLimitModalOpen(false);
            setIsValidationModalOpen(false);
            setFactToDelete(null);
            setHistoryItemToDelete(null);
            
            hasInitialized.current = true;
            setIsReady(true); 
        }
    }, [isOpen, node, dicionarioCompetencias, isUserManager, checkIsMandatory, history]);
    // --- HANDLERS ---
    const handleProceedToEvaluation = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            const initialEvaluations = {};
            selectedCompetencies.forEach(comp => {
                initialEvaluations[comp.nome] = evaluations[comp.nome] || { rating: undefined, facts: [] };
            });
            setEvaluations(initialEvaluations);
            if (await persistEvaluation({ evaluations: initialEvaluations, period }, false)) {
                setStep('evaluation');
            }
        } finally {
            submittingRef.current = false;
        }
    };

    // Handlers de Seleção em Massa
    const handleEnterSelectionMode = (competencyName, factIndex) => {
        setIsSelectionMode(true);
        setSelectedFactsMap({ [competencyName]: [factIndex] });
    };

    const handleToggleFactSelection = (competencyName, factIndex) => {
        setSelectedFactsMap(prev => {
            const currentIndices = prev[competencyName] || [];
            if (currentIndices.includes(factIndex)) {
                const newIndices = currentIndices.filter(i => i !== factIndex);
                if (newIndices.length === 0) {
                    const newState = { ...prev };
                    delete newState[competencyName];
                    return newState;
                }
                return { ...prev, [competencyName]: newIndices };
            } else {
                return { ...prev, [competencyName]: [...currentIndices, factIndex] };
            }
        });
    };

    const handleCancelSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedFactsMap({});
    };

    const handleDeleteSelectedFacts = () => {
        setEvaluations(prev => {
            const newState = { ...prev };
            Object.keys(selectedFactsMap).forEach(compName => {
                const indicesToDelete = selectedFactsMap[compName];
                if (newState[compName] && newState[compName].facts) {
                    newState[compName].facts = newState[compName].facts.filter((_, idx) => !indicesToDelete.includes(idx));
                }
            });
            if (historyIndexEditing === null) triggerDebouncedSave(newState, period);
            return newState;
        });
        handleCancelSelectionMode();
    };

    const handleRequestRemoveFact = useCallback((competencyName, factIndex) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); 
        setFactToDelete({ competencyName, index: factIndex });
    }, []);

    const handleConfirmRemoveFact = () => {
        if (!factToDelete) return;
        setEvaluations(prev => {
            const comp = prev[factToDelete.competencyName];
            const facts = comp.facts.filter((_, i) => i !== factToDelete.index);
            const newState = { ...prev, [factToDelete.competencyName]: { ...comp, facts } };
            triggerDebouncedSave(newState, period);
            return newState;
        });
        setFactToDelete(null);
    };

// Dentro de EvaluationSheetModal

    const triggerDebouncedSave = useCallback((currentEvaluations, currentPeriod) => {
        if (historyIndexEditing !== null || revisionContext) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveVersionRef.current += 1;
        setSaveStatus('typing');
        setSaveError('');
        const payload = { evaluations: currentEvaluations, period: currentPeriod };
        pendingSaveRef.current = { payload, isFinal: false };
        saveTimeoutRef.current = setTimeout(() => persistEvaluation(payload, false), 1500);
    }, [persistEvaluation, historyIndexEditing, revisionContext]);

    const triggerDebouncedHistorySave = useCallback((updatedHistoryItem, index) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveVersionRef.current += 1;
        setSaveStatus('typing');
        setSaveError('');
        const updatedHistory = [...historyRef.current];
        if (index !== null && index >= 0 && index < updatedHistory.length) {
            updatedHistory[index] = updatedHistoryItem;
        }
        const payload = {
            evaluations: node?.data?.evaluations || {},
            period: node?.data?.evaluationPeriod,
            evaluationHistory: updatedHistory,
            lastEvaluationDate: node?.data?.lastEvaluationDate,
            nextEvaluationDate: node?.data?.nextEvaluationDate,
        };
        pendingSaveRef.current = { payload, isFinal: true };
        saveTimeoutRef.current = setTimeout(() => persistEvaluation(payload, true), 1500);
    }, [persistEvaluation, node]);

    const handleRatingUpdate = (competencyName, rating) => {
        setEvaluations(prev => {
            const newState = { ...prev, [competencyName]: { ...(prev[competencyName] || { facts: [] }), rating: rating } };
            if (historyIndexEditing === null) triggerDebouncedSave(newState, period);
            if (missingFields.includes(competencyName)) setMissingFields(p => p.filter(m => m !== competencyName));
            return newState;
        });
    };

    const handleFactUpdate = (name, idx, field, val) => {
        setEvaluations(prev => {
            const comp = prev[name] || { rating: undefined, facts: [] };
            const facts = [...(comp.facts || [])];
            if (!facts[idx]) facts[idx] = {};
            facts[idx] = { ...facts[idx], [field]: val };
            const newState = { ...prev, [name]: { ...comp, facts } };
            if (historyIndexEditing === null) triggerDebouncedSave(newState, period);
            return newState;
        });
    };

    const handleAddFact = (name) => {
        setEvaluations(prev => {
            const comp = prev[name] || { rating: undefined, facts: [] };
            const facts = [...(comp.facts || []), { factDate: new Date().toISOString().split('T')[0], fact: '', factDescription: '' }];
            const newState = { ...prev, [name]: { ...comp, facts } };
            if (historyIndexEditing === null) triggerDebouncedSave(newState, period);
            return newState;
        });
    };

    const handleToggleCompetency = (comp) => {
        if (!isEditingAllowed || checkIsMandatory(comp)) return;
        setSelectedCompetencies(prev => {
            const exists = prev.find(c => c.nome === comp.nome);
            if (exists) return prev.filter(c => c.nome !== comp.nome);
            const techCount = prev.filter(c => c.tipo === 'Técnica' || c.tipo === 'Outra').length;
            if (comp.tipo !== 'Corporativa' && comp.tipo !== 'Gerencial' && techCount >= 5) {
                setIsLimitModalOpen(true); return prev;
            }
            return [...prev, comp];
        });
    };

    const handleRequestFinalize = () => {
        const missing = [];
        selectedCompetencies.forEach(comp => {
            const d = evaluations[comp.nome];
            if (!d?.rating && !d?.facts?.some(f => f.fact && f.fact.trim())) missing.push(comp.nome);
        });
        if (missing.length > 0) {
            setMissingFields(missing);
            setIsValidationModalOpen(true);
        } else {
            setIsSaveConfirmOpen(true);
        }
    };

const handleCloseValidation = () => {
        setIsValidationModalOpen(false);

        setTimeout(() => {
            if (missingFields.length > 0) {
                const firstMissingName = missingFields[0];
                const elementId = generateSafeId(firstMissingName);
                
                const element = document.getElementById(elementId);
                const container = modalBodyRef.current;

                if (element && container) {
                    // --- CÁLCULO DE CENTRALIZAÇÃO PRECISA ---
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = element.getBoundingClientRect();
                    
                    // Posição atual do scroll
                    const currentScrollTop = container.scrollTop;

                    // Calcula a posição relativa do elemento dentro do container
                    // (Onde ele estaria se o scroll fosse 0)
                    const elementRelativeTop = elementRect.top - containerRect.top + currentScrollTop;

                    // Calcula o centro:
                    // Posição do elemento - Metade da tela + Metade do elemento
                    const targetScrollTop = elementRelativeTop - (containerRect.height / 2) + (elementRect.height / 2);

                    container.scrollTo({
                        top: targetScrollTop,
                        behavior: "smooth"
                    });

                    // --- EFEITO VISUAL (Sem mexer na borda física, apenas visual) ---
                    // A classe 'card-error-highlight' (no animationStyles) cuida do piscar
                    // Mas podemos reforçar aqui com Javascript para garantir
                    
                    const originalTransition = element.style.transition;
                    const originalTransform = element.style.transform;

                    element.style.transition = "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)";
                    element.style.transform = "scale(1.02)";
                    
                    // Nota: A borda vermelha real é controlada pelo componente CompetencyEvaluationTable
                    // através da prop 'hasError'. Aqui apenas damos o 'zoom'.

                    setTimeout(() => {
                        element.style.transform = originalTransform || "scale(1)";
                        setTimeout(() => {
                            element.style.transition = originalTransition;
                        }, 500);
                    }, 1500);
                } else {
                    console.warn("Scroll Debug: Elemento ou Container não encontrados.", { elementId });
                }
            }
        }, 300);
    };

    const confirmDeleteHistoryItem = async () => {
        if (historyItemToDelete === null) return;

        const updatedHistory = [...history];
        updatedHistory.splice(historyItemToDelete, 1);

        const payload = {
            evaluations: evaluations, 
            period: period,
            evaluationHistory: updatedHistory, 
            lastEvaluationDate: node.data.lastEvaluationDate,
            nextEvaluationDate: node.data.nextEvaluationDate
        };

        if (await persistEvaluation(payload, true)) {
            setHistoryItemToDelete(null);
            historyRef.current = updatedHistory;
        }
    };

    const handleFinalize = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            let payload = { evaluations, period };
            if (historyIndexEditing !== null) {
                const updatedHistory = [...history];
                updatedHistory[historyIndexEditing] = {
                    ...updatedHistory[historyIndexEditing], evaluations, period,
                };
                payload = {
                    evaluations: node.data.evaluations || {},
                    period: node.data.evaluationPeriod,
                    evaluationHistory: updatedHistory,
                    lastEvaluationDate: node.data.lastEvaluationDate,
                    nextEvaluationDate: node.data.nextEvaluationDate,
                };
            }
            if (!await persistEvaluation(payload, true)) {
                setIsSaveConfirmOpen(false);
                return;
            }
            setIsSaveConfirmOpen(false);
            setHistoryIndexEditing(null);
            setIsEditingAllowed(false);
            setStep('history');
        } finally {
            submittingRef.current = false;
        }
    };

    const handleStartNewCycle = () => {
        setHistoryIndexEditing(null); setStep('selection'); setIsEditingAllowed(true); setEvaluations({}); setSaveStatus('idle');
        const mandatory = availableCompetencies.filter(c => checkIsMandatory(c));
        setSelectedCompetencies(mandatory);
    };

    const handleRestartFromEntry = (entry) => {
        const compsFromHistory = [];
        if (entry.evaluations) {
            Object.keys(entry.evaluations).forEach(compName => {
                const compObj = availableCompetencies.find(c => c.nome === compName);
                if (compObj) compsFromHistory.push(compObj);
            });
        }
        setSelectedCompetencies(compsFromHistory);
        setPeriod(entry.period || 'Semestral');
        setEvaluations({});
        setSaveStatus('idle');
        setIsEditingAllowed(true);
        setHistoryIndexEditing(null);
        setStep('selection');
    };

    const handleConfirmBack = async () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        if (historyIndexEditing !== null) {
             setHistoryIndexEditing(null);
             setStep('history');
        } else {
            if (!await persistEvaluation({ evaluations: {}, period }, false)) return;
            setEvaluations({});
            setMissingFields([]);
            setStep('selection');
        }
        setIsBackConfirmOpen(false);
        handleCancelSelectionMode(); 
    };

    const handleDeleteHistoryItem = (index) => { setHistoryItemToDelete(index); };
    
    const handleEditFullHistoryItem = (entry, index) => {
        setHistoryIndexEditing(index);
        setEvaluations(entry.evaluations);
        setPeriod(entry.period || 'Semestral');
        const recoveredCompetencies = [];
        Object.keys(entry.evaluations).forEach(compName => {
            const compObj = availableCompetencies.find(c => c.nome === compName);
            if (compObj) recoveredCompetencies.push(compObj);
        });
        setSelectedCompetencies(recoveredCompetencies);
        setIsEditingAllowed(true);
        setStep('evaluation');
    };

    const handleOpenAddFactToHistory = (entry, index) => { setViewingHistoryItem(entry); setHistoryIndexEditing(index); setStep('add_fact_history'); };
    const handleViewHistoryItem = (item) => { setViewingHistoryItem(item); setStep('view_history'); };
    const handleBackToHistoryList = () => { setViewingHistoryItem(null); setHistoryIndexEditing(null); setStep('history'); };
    const handleAddFactToHistoryItem = (competencyName) => { setViewingHistoryItem(prev => { const newItem = JSON.parse(JSON.stringify(prev)); const compEval = newItem.evaluations[competencyName] || { facts: [] }; if (!compEval.facts) compEval.facts = []; compEval.facts.push({ factDate: new Date().toISOString().split('T')[0], fact: '', factDescription: '' }); newItem.evaluations[competencyName] = compEval; triggerDebouncedHistorySave(newItem, historyIndexEditing); return newItem; }); };
    const handleUpdateFactInHistory = (competencyName, factIndex, field, value) => { setViewingHistoryItem(prev => { const newItem = JSON.parse(JSON.stringify(prev)); newItem.evaluations[competencyName].facts[factIndex][field] = value; triggerDebouncedHistorySave(newItem, historyIndexEditing); return newItem; }); };
    const handleRemoveFactInHistory = (competencyName, factIndex) => { setViewingHistoryItem(prev => { const newItem = JSON.parse(JSON.stringify(prev)); newItem.evaluations[competencyName].facts = newItem.evaluations[competencyName].facts.filter((_, i) => i !== factIndex); triggerDebouncedHistorySave(newItem, historyIndexEditing); return newItem; }); };

    const handleOpenSwap = (competency) => {
        setCompetencyToSwap(competency);
        setSwapSearchTerm('');
    };

    const handleConfirmSwap = (newCompetencyData) => {
        if (!competencyToSwap) return;

        const oldName = competencyToSwap.nome;
        const newName = newCompetencyData.nome;
        const newType = competencyToSwap.tipo;

        setSelectedCompetencies(prev => prev.map(comp => {
            if (comp.nome === oldName) {
                return { ...newCompetencyData, tipo: newType };
            }
            return comp;
        }));

        setEvaluations(prev => {
            const newState = { ...prev };
            delete newState[oldName];
            newState[newName] = { rating: undefined, facts: [] };
            if (historyIndexEditing === null) triggerDebouncedSave(newState, period);
            return newState;
        });

        setCompetencyToSwap(null);
    };

    const getSwapOptions = () => {
        if (!competencyToSwap) return [];
        const techFromNode = (node.data?.competenciasTecnicas || []).map(c => ({ ...c, tipo: 'Técnica' }));
        const otherFromNode = (node.data?.outrasCompetencias || []).map(c => ({ ...c, tipo: 'Outra' }));
        const jdCompetencies = [...techFromNode, ...otherFromNode];
        const selectedNames = selectedCompetencies.map(c => c.nome);
        let options = jdCompetencies.filter(opt => !selectedNames.includes(opt.nome));
        if (swapSearchTerm.trim()) {
            const term = swapSearchTerm.toLowerCase();
            options = options.filter(opt => opt.nome.toLowerCase().includes(term));
        }
        return options;
    };

    const InternalHistoryTimeline = () => {
        if (!history || history.length === 0) return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#94a3b8', fontStyle: 'italic', height: '100%' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.5 }}>📜</div>
                <p style={{ margin: '0 0 24px 0', fontSize: '1.1rem', color: '#64748b' }}>Nenhum histórico de avaliações encontrado.</p>
                {canEdit && (
                    <button onClick={handleStartNewCycle} style={{ ...evalPrimaryBtnStyle, background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', padding: '12px 28px', fontSize: '1rem', fontWeight: '600', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)', display: 'flex', alignItems: 'center', gap: '8px', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', border: 'none', borderRadius: '8px', color: 'white' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(22, 163, 74, 0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.25)'; }}>
                        <span style={{ fontSize: '1.2rem' }}>✨</span> Fazer Nova Avaliação
                    </button>
                )}
            </div>
        );
        const sortedHistory = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
        return (
            <div style={{ position: 'relative', paddingLeft: '24px', marginTop: '24px' }}>
                <div style={{ position: 'absolute', left: '11px', top: '10px', bottom: '0', width: '2px', backgroundColor: '#e2e8f0' }}></div>
                {sortedHistory.map((entry, index) => {
                    const originalIndex = history.indexOf(entry);
                    const nextCycleDate = calculateNextCycleDate(entry.date, entry.period);
                    const today = new Date();
                    const isExpired = today >= nextCycleDate;
                    const dateColor = isExpired ? '#ef4444' : '#22c55e';
                    const dateBg = isExpired ? '#fef2f2' : '#f0fdf4';
                    const dateBorder = isExpired ? '#fca5a5' : '#86efac';
                    const dateLabel = isExpired ? "Venceu em:" : "Próxima Avaliação:";
                    const countdownText = getCountdownText(nextCycleDate);
                    const isMostRecent = index === 0;
                    return (
                        <div key={index} style={{ marginBottom: '32px', position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-18px', top: '0', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: isMostRecent ? '#3b82f6' : '#94a3b8', border: '4px solid white', boxShadow: '0 0 0 2px #e2e8f0', zIndex: 2 }}></div>
                            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                    <div>
                                        <div style={{fontSize: '0.85rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', marginBottom:'4px'}}>Avaliação realizada em</div>
                                        <strong style={{ color: '#1e293b', fontSize: '1.2rem', display: 'block' }}>{new Date(entry.date).toLocaleDateString('pt-BR')}</strong>
                                        <span style={{ fontSize: '0.9rem', color: '#475569', marginTop:'4px', display:'block' }}>Ciclo: <strong>{entry.period}</strong></span>
                                        <span style={{ fontSize: '0.85rem', color: '#64748b', display:'block', marginTop:'2px' }}>Por: {entry.evaluatorName || 'Gestor'}</span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ background: dateBg, border: `1px solid ${dateBorder}`, borderLeft: `4px solid ${dateColor}`, padding: '10px 16px', borderRadius: '8px', display: 'inline-block' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>{dateLabel}</div>
                                            <div style={{ fontSize: '1.1rem', color: dateColor, fontWeight: 'bold' }}>{nextCycleDate.toLocaleDateString('pt-BR')}</div>
                                        </div>
                                        {!isExpired && countdownText && <div style={{ marginTop: '6px', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>⏳ {countdownText}</div>}
                                    </div>
                                </div>
                                <div style={{ borderTop: '1px solid #f1f5f9', marginBottom: '20px' }}></div>
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#334155', fontWeight: '600' }}>Competências Avaliadas ({Object.keys(entry.evaluations).length})</h4>
                                    <div style={{ display: 'grid', gap: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                                        {Object.entries(entry.evaluations).map(([name, data], i) => {
                                            const ratingColor = getRatingColor(data.rating);
                                            return (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <span style={{ fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' }}>{name}</span>
                                                    {data.rating && (<span style={{ backgroundColor: ratingColor, color: 'white', borderRadius: '6px', padding: '2px 6px', fontSize: '0.8rem', fontWeight: 'bold', minWidth: '20px', textAlign: 'center' }}>{data.rating}</span>)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                               <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>


<div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
    <button onClick={() => handleViewHistoryItem(entry)} style={{ ...evalGhostBtnStyle, padding: '8px 16px', fontSize: '0.9rem' }}>
        👁️ Ver Detalhes
    </button>
    
    {/* Visão do ADMIN/EDITOR: Pode editar e excluir o histórico diretamente */}
    {isAdminOrEditor && (
        <>
            <button 
                onClick={() => handleEditFullHistoryItem(entry, originalIndex)} 
                style={{ ...evalGhostBtnStyle, padding: '8px', color: '#f59e0b', borderColor: '#f59e0b' }} 
                title="Editar esta avaliação"
            >
                <EditIcon size={16}/>
            </button>
            <button 
                onClick={() => handleDeleteHistoryItem(originalIndex)} 
                style={{ ...evalGhostBtnStyle, padding: '8px', color: '#ef4444', borderColor: '#ef4444' }} 
                title="Excluir esta avaliação"
            >
                <Trash2 size={16}/>
            </button>
        </>
    )}

{/* Visão do GESTOR: Solicitar alteração dos dados desta avaliação específica */}
{isGerenteViewer && (
    <button 
        onClick={() => handleStartEvalRevision(entry, originalIndex)} // ✨ Apenas inicia a edição
        style={{ 
            ...evalGhostBtnStyle, 
            backgroundColor: '#fff7ed', 
            color: '#c2410c', 
            borderColor: '#fdba74',
            fontWeight: '700',
            marginTop: '8px'
        }}
    >
        ✏️ Ajustar Notas/Fatos desta Avaliação
    </button>
)}
</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!isOpen || !node || !isReady) return null;

    return (
        <ModalPortal>
            <style>{animationStyles}</style>
            <PromptModal isOpen={isPromptOpen} onClose={() => setIsPromptOpen(false)} onConfirm={promptConfig.onConfirm} title={promptConfig.title} message={promptConfig.message} placeholder={promptConfig.placeholder} />
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1400 }}>
                <div style={evalModalContainerStyle}>
                    {historyItemToDelete !== null && (
                         <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.95)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1700}}>
                            <div style={{background:'white', padding:30, borderRadius:12, border:'1px solid #eee', boxShadow:'0 10px 25px rgba(0,0,0,0.1)', textAlign:'center', maxWidth: '400px'}}>
                                <h3>Excluir Ciclo do Histórico?</h3>
                                <p style={{color: '#64748b'}}>Esta ação removerá permanentemente este registro de avaliação.</p>
                                <div style={{display:'flex', gap:10, justifyContent:'center', marginTop:20}}>
                                    <button onClick={()=>setHistoryItemToDelete(null)} style={evalGhostBtnStyle}>Cancelar</button>
                                    <button onClick={confirmDeleteHistoryItem} style={{...evalPrimaryBtnStyle, background:'#dc2626'}}>Sim, Excluir</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {factToDelete && (
                        <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.9)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1600}}>
                            <div style={{background:'white', padding:30, borderRadius:12, border:'1px solid #eee', boxShadow:'0 10px 25px rgba(0,0,0,0.1)', textAlign:'center'}}>
                                <h3>Excluir Fato?</h3>
                                <div style={{display:'flex', gap:10, justifyContent:'center', marginTop:20}}>
                                    <button onClick={()=>setFactToDelete(null)} style={evalGhostBtnStyle}>Cancelar</button>
                                    <button onClick={handleConfirmRemoveFact} style={{...evalPrimaryBtnStyle, background:'#dc2626'}}>Sim, Excluir</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {isValidationModalOpen && (
                        <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.8)', backdropFilter:'blur(3px)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1700}}>
                            <div style={{background:'white', padding:30, borderRadius:12, border:'2px solid #f87171', textAlign:'center', maxWidth:400, boxShadow:'0 10px 30px rgba(239,68,68,0.15)'}}>
                                <h3 style={{color:'#991b1b'}}>🛑 Avaliação Incompleta</h3>
                                <p style={{color:'#7f1d1d', lineHeight: '1.5'}}>
                                    Existem <strong>{missingFields.length}</strong> competência(s) sem avaliação (nota ou fato).<br/>
                                    Preencha todos os itens para continuar.
                                </p>
                                <button onClick={handleCloseValidation} style={{...evalPrimaryBtnStyle, background:'#ef4444', width:'100%'}}>Corrigir Agora</button>
                            </div>
                        </div>
                    )}
                    {isSaveConfirmOpen && (
                        <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.9)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500}}>
                            <div style={{background:'white', padding:30, borderRadius:12, border:'1px solid #e2e8f0', textAlign:'center', maxWidth: '450px'}}>
                                <h3 style={{color:'#1e293b', marginBottom: '16px'}}>
                                    {historyIndexEditing !== null ? 'Salvar Alterações no Histórico?' : '✅ Finalizar Avaliação?'}
                                </h3>
                                <p style={{color:'#475569', lineHeight:'1.5', marginBottom:'24px', textAlign: 'left'}}>
                                    {historyIndexEditing !== null 
                                        ? 'As alterações feitas neste registro antigo serão salvas permanentemente.' 
                                        : 'Confirma o encerramento deste ciclo? Os dados serão arquivados e o rascunho limpo.'
                                    }
                                </p>
                                <div style={{display:'flex', gap:12, justifyContent:'center'}}>
                                    <button onClick={()=>setIsSaveConfirmOpen(false)} style={evalGhostBtnStyle}>Cancelar</button>
                                    <button onClick={handleFinalize} style={evalPrimaryBtnStyle}>
                                        {historyIndexEditing !== null ? 'Salvar' : 'Sim, Finalizar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {isBackConfirmOpen && (
                        <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.95)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500, backdropFilter: 'blur(4px)'}}>
                            <div style={{background:'white', padding:40, borderRadius:16, border:'1px solid #e2e8f0', textAlign:'center', maxWidth: '550px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'}}>
                                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                                    <div style={{ background: '#fee2e2', padding: '16px', borderRadius: '50%', color: '#dc2626' }}>
                                        <AlertTriangle size={40} />
                                    </div>
                                </div>
                                <h3 style={{color:'#7f1d1d', marginBottom: '16px', fontSize: '1.5rem', fontWeight: '800'}}>
                                    {historyIndexEditing !== null ? 'Sair do modo de edição?' : 'Deseja reiniciar a avaliação?'}
                                </h3>
                                <div style={{color:'#475569', lineHeight:'1.6', marginBottom:'32px', fontSize: '1rem', textAlign: 'left', background: '#fff1f2', padding: '16px', borderRadius: '8px', border: '1px solid #fecaca'}}>
                                    {historyIndexEditing !== null 
                                        ? 'Você está editando um histórico passado. Se sair agora, as alterações não salvas serão perdidas.' 
                                        : (
                                            <>
                                                <strong>Atenção:</strong> Você clicou em <strong>"Reconfigurar Competências"</strong>.<br/><br/>
                                                Isso fará você voltar para a tela de seleção (Etapa 1).<br/>
                                                ⚠️ <strong>Todas as notas e fatos que você preencheu agora serão apagados</strong> para que você possa escolher novas competências.<br/><br/>
                                                • Se você quer apenas parar por hoje, clique em <strong>"Não, quero continuar preenchendo"</strong> abaixo e depois use o botão <strong>"Salvar Rascunho e Sair"</strong>.
                                            </>
                                        )
                                    }
                                </div>
                                <div style={{display:'flex', gap:16, justifyContent:'flex-end'}}>
                                    <button 
                                        onClick={()=>setIsBackConfirmOpen(false)} 
                                        style={{ 
                                            ...evalPrimaryBtnStyle, 
                                            background: '#3b82f6', 
                                            fontSize: '1rem',
                                            padding: '12px 24px'
                                        }}
                                    >
                                        Não, quero continuar preenchendo
                                    </button>
                                    <button 
                                        onClick={handleConfirmBack} 
                                        style={{ 
                                            ...evalGhostBtnStyle, 
                                            color: '#dc2626', 
                                            borderColor: '#fca5a5',
                                            fontSize: '0.9rem' 
                                        }}
                                    >
                                        Sim, apagar tudo e voltar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {isLimitModalOpen && (
                         <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1600}}>
                            <div style={{background:'white', padding:32, borderRadius:16, width:400, border:'1px solid #e2e8f0', textAlign:'center'}}>
                                <h3>Limite Atingido</h3>
                                <p>Selecione no máximo 5 competências técnicas/outras.</p>
                                <button onClick={()=>setIsLimitModalOpen(false)} style={{...evalPrimaryBtnStyle, width:'100%'}}>Entendi</button>
                            </div>
                        </div>
                    )}
                    {competencyToSwap && (
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1800 }}>
                            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', width: '90%', maxWidth: '600px', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
                                <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: '#1e293b' }}>Trocar Competência</h3>
                                        <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' }}>
                                            Substituindo: <strong>{competencyToSwap.nome}</strong> <span style={{fontSize: '0.8em', color: '#6b7280', marginLeft: '5px'}}>({competencyToSwap.tipo})</span>
                                        </p>
                                    </div>
                                    <button onClick={() => setCompetencyToSwap(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><XIcon /></button>
                                </div>
                                <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                                    <input type="text" placeholder="Pesquisar no Job Description..." value={swapSearchTerm} onChange={(e) => setSwapSearchTerm(e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} autoFocus />
                                </div>
                                <div style={{ padding: '16px', overflowY: 'auto', flexGrow: 1 }}>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {getSwapOptions().map((opt, idx) => (
                                            <div key={idx} onClick={() => handleConfirmSwap(opt)} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                <div style={{ flex: 1, minWidth: 0 }}> 
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                                        <strong style={{ color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.nome}</strong>
                                                        <span style={{ fontSize: '0.7em', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', backgroundColor: opt.tipo === 'Técnica' ? '#ede9fe' : '#fce7f3', color: opt.tipo === 'Técnica' ? '#7c3aed' : '#db2777' }}>{opt.tipo}</span>
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.descricao}</div>
                                                </div>
                                                <span style={{ color: '#16a34a', whiteSpace: 'nowrap', fontWeight: '500', flexShrink: 0 }}> Selecionar → </span>
                                            </div>
                                        ))}
                                        {getSwapOptions().length === 0 && (
                                            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>Nenhuma outra competência disponível no Job Description para troca.</p>
                                        )}
                                    </div>
                                </div>
                                <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', textAlign: 'right', background: '#f8fafc', borderRadius: '0 0 12px 12px' }}>
                                    <button onClick={() => setCompetencyToSwap(null)} style={evalGhostBtnStyle}>Cancelar</button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                   {/* HEADER - Ajustado para ser limpo e organizado */}
<div style={{ ...evalHeaderStyle, background: 'white', borderBottom: '1px solid #e2e8f0', color: '#1e293b', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Ícone Informativo */}
        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}>
            📝
        </div>
        
        {/* Título e Nome do Colaborador */}
        <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>
                {step === 'history' ? 'Ficha de Avaliação' : 'Avaliação de Desempenho'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                    Colaborador: <strong style={{ color: '#334155' }}>{node?.data?.name || node?.data?.nome}</strong>
                </span>
                {/* Badge de Edição (Opcional, apenas para feedback visual) */}
                {revisionContext && (
                    <span style={{ backgroundColor: '#fff7ed', color: '#c2410c', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', border: '1px solid #fdba74' }}>
                        ✏️ Editando Avaliação
                    </span>
                )}
            </div>
        </div>
    </div>

    {/* Fecha somente após a gravação do rascunho ou das alterações pendentes. */}
    <button
        onClick={handleCloseEvaluation}
        style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '36px', height: '36px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} 
        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }} 
        onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }} 
        title="Fechar modal"
    >
        <XIcon style={{ width: '20px', height: '20px' }} />
    </button>
</div>
                    {/* Tabs */}
                    <div style={{ padding: '0 32px', borderBottom: '1px solid #e2e8f0', backgroundColor: 'white', display: 'flex', gap: '16px' }}>
                        <TabButton label="1. Configuração" isActive={step === 'selection'} />
                        <TabButton label="2. Avaliação" isActive={step === 'evaluation'} />
                        <TabButton label="3. Ciclos" isActive={step === 'history'} />
                    </div>

                    <div aria-live="polite" style={{ padding: '8px 32px' }}>
                        <AutoSaveFeedback status={saveStatus} lastTime={lastSavedTime} />
                        {saveError && <p role="alert" style={{ color: '#b91c1c', margin: '8px 0' }}>{saveError}</p>}
                    </div>
                    {/* Content */}
                    <div style={evalContentStyle} ref={modalBodyRef}>
                        {step === 'selection' && (
                            <div>
                                <div style={sectionCardStyle}>
                                    <div style={sectionHeaderStyle}>
                                        <div style={{...sectionIconStyle, backgroundColor: '#fbbf24', color: 'white'}}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg>
                                        </div>
                                        <div style={{flexGrow: 1}}>
                                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#78350f'}}>Configuração do Ciclo</h3>
                                            <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#92400e'}}>Defina a frequência e os parâmetros desta avaliação.</p>
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '12px' }}>Frequência do Ciclo</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {['Semestral', 'Anual'].map(p => (
                                                <button key={p} onClick={() => { setPeriod(p); triggerDebouncedSave(evaluations, p); }} style={{ padding: '10px 20px', borderRadius: 8, border: period === p ? '2px solid #f59e0b' : '1px solid #cbd5e1', background: period === p ? '#fffbeb' : 'white', color: period === p ? '#b45309' : '#64748b', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div style={sectionCardStyle}>
                                    <div style={sectionHeaderStyle}>
                                        <div style={{...sectionIconStyle, backgroundColor: '#14b8a6', color: 'white'}}><Wand2 size={20} /></div>
                                        <div style={{flexGrow: 1}}>
                                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#134e4a'}}>Competências Corporativas {isUserManager && '& Gerenciais'}</h3>
                                            <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#115e59'}}>Itens obrigatórios definidos pela organização.</p>
                                        </div>
                                        {isFirstEvaluation && <span style={{fontSize: '0.8em', backgroundColor: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '99px', fontWeight: '600'}}>Obrigatórias</span>}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                                        {availableCompetencies.filter(c => c.tipo === 'Corporativa' || (isUserManager && c.tipo === 'Gerencial')).map((comp, idx) => {
                                            const isSelected = selectedCompetencies.some(c => c.nome === comp.nome);
                                            const status = getCompetencyStatus(comp.nome);
                                            const isMandatory = checkIsMandatory(comp);
                                            return (
                                                <div key={idx} onClick={() => handleToggleCompetency(comp)} style={{ padding: '16px', borderRadius: '10px', border: isSelected ? '2px solid #14b8a6' : '1px solid #e2e8f0', background: isSelected ? '#f0fdfa' : 'white', cursor: isMandatory ? 'not-allowed' : 'pointer', opacity: 1 }}>
                                                    <strong style={{color: '#134e4a'}}>{comp.nome} {status.locked && '🔒'} {isMandatory && '📌'}</strong>
                                                    <br/><span style={{fontSize:'0.8em', color: '#0f766e'}}>{comp.tipo}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div style={sectionCardStyle}>
                                    <div style={sectionHeaderStyle}>
                                        <div style={{...sectionIconStyle, backgroundColor: '#8b5cf6', color: 'white'}}><ShieldCheck size={20} /></div>
                                        <div style={{flexGrow: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                            <div>
                                                <h3 style={{margin: 0, fontSize: '1.1rem', color: '#4338ca'}}>Seleção de Competências</h3>
                                                <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#3730a3'}}>Escolha as competências técnicas e comportamentais para este ciclo.</p>
                                            </div>
                                            <span style={{fontSize: '0.9rem', fontWeight: '600', color: technicalSelectionCount >= 3 && technicalSelectionCount <= 5 ? '#16a34a' : '#64748b', backgroundColor: technicalSelectionCount >= 3 && technicalSelectionCount <= 5 ? '#dcfce7' : '#f1f5f9', padding: '6px 12px', borderRadius: '8px'}}>
                                                Selecionadas: {technicalSelectionCount}/5 (Min: 3)
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                                        {availableCompetencies.filter(c => c.tipo !== 'Corporativa' && c.tipo !== 'Gerencial').map((comp, idx) => {
                                            const isSelected = selectedCompetencies.some(c => c.nome === comp.nome);
                                            const status = getCompetencyStatus(comp.nome);
                                            const style = getCompetencyStyle(comp.tipo);
                                            return (
                                                <div key={idx} onClick={() => handleToggleCompetency(comp)} style={{ padding: 16, borderRadius: 10, border: isSelected ? `2px solid ${style.color}` : '1px solid #e2e8f0', background: isSelected ? style.bgLight : 'white', cursor: 'pointer', opacity: status.locked ? 0.6 : 1 }}>
                                                    <strong style={{ color: '#1e293b' }}>{comp.nome} {status.locked && '🔒'}</strong>
                                                    <div style={{ fontSize: '0.8rem', marginTop: 4, color: style.color, fontWeight: 600 }}>{comp.tipo}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 'evaluation' && (
                            <div>
                                <div style={{ marginBottom: 20, padding: 15, background: historyIndexEditing !== null ? '#fff7ed' : '#f0f9ff', borderRadius: 8, border: historyIndexEditing !== null ? '1px solid #fdba74' : '1px solid #bae6fd', color: historyIndexEditing !== null ? '#c2410c' : '#0369a1', fontWeight: 600 }}>
                                    {historyIndexEditing !== null ? '✏️ Editando Ciclo Passado' : `Avaliação #${history.length + 1} - ${period}`}
                                </div>
                                {historyIndexEditing !== null && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Alterar Frequência do Ciclo</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {['Semestral', 'Anual'].map(p => (
                                                <button key={p} onClick={() => setPeriod(p)} style={{ padding: '8px 16px', borderRadius: 6, border: period === p ? '2px solid #f59e0b' : '1px solid #cbd5e1', background: period === p ? '#fff7ed' : 'white', fontWeight: 600, color: period === p ? '#c2410c' : '#64748b', cursor: 'pointer' }}>{p}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedCompetencies.map((comp, i) => (
                                    <CompetencyEvaluationTable 
                                        key={i} 
                                        // 👇 ID Seguro para o scroll funcionar
                                        id={generateSafeId(comp.nome)} 
                                        competency={comp} 
                                        evaluationData={evaluations[comp.nome]} 
                                        onUpdateRating={handleRatingUpdate} 
                                        onUpdateFact={handleFactUpdate} 
                                        onAddFact={handleAddFact} 
                                        onEnterSelectionMode={handleEnterSelectionMode} 
                                        onToggleFactSelection={handleToggleFactSelection} 
                                        selectedFactIndices={selectedFactsMap[comp.nome] || []} 
                                        isSelectionMode={isSelectionMode} 
                                        onRemoveFact={handleRequestRemoveFact} 
                                        canEdit={canEdit} 
                                        hasError={missingFields.includes(comp.nome)} 
                                        onSwap={handleOpenSwap} 
                                        allowSwap={!checkIsMandatory(comp)} 
                                    />
                                ))}
                            </div>
                        )}

                        {step === 'history' && (
                            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                                <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <div style={{ marginRight: 'auto', textAlign: 'left' }}>
                                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem' }}>Linha do Tempo</h3>
                                        <p style={{ margin: '4px 0 0', fontSize: '0.9rem' }}>Histórico completo de ciclos.</p>
                                    </div>
                                    <button onClick={onOpenResults} style={{ ...evalGhostBtnStyle, backgroundColor: 'white', borderColor: '#3b82f6', color: '#2563eb', padding: '10px 20px', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.transform = 'translateY(-1px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                        <ChartIcon style={{ marginRight: '8px' }} /> Ver Gráficos de Resultado
                                    </button>
                                </div>
                                <InternalHistoryTimeline 
    handleRequestEvalRevision={handleRequestEvalRevision} // ✨ PASSE AQUI TAMBÉM
/>
                            </div>
                        )}
                        {step === 'view_history' && viewingHistoryItem && (
                            <div>
                                <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <button onClick={handleBackToHistoryList} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
                                    <div><h3 style={{ margin: 0, color: '#1e293b' }}>Avaliação de {new Date(viewingHistoryItem.date).toLocaleDateString('pt-BR')}</h3></div>
                                </div>
                                {viewingHistoryItem.evaluations && Object.keys(viewingHistoryItem.evaluations).length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                        {Object.entries(viewingHistoryItem.evaluations).map(([compName, evaluationData], index) => {
                                            const compData = availableCompetencies.find(c => c.nome === compName) || { nome: compName, tipo: 'Histórico', descricao: '' };
                                            return <CompetencyEvaluationTable key={`hist-${index}`} competency={compData} evaluationData={evaluationData} canEdit={false} onUpdateRating={()=>{}} onUpdateFact={()=>{}} onAddFact={()=>{}} onRemoveFact={()=>{}} />;
                                        })}
                                    </div>
                                ) : <p style={{ textAlign: 'center', color: '#94a3b8' }}>Sem detalhes.</p>}
                            </div>
                        )}
                        {step === 'add_fact_history' && viewingHistoryItem && (
                            <div>
                                <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div><h3 style={{ margin: 0, color: '#1e293b' }}>Adicionar Fatos</h3></div>
                                    <div style={{ marginLeft: 'auto' }}><AutoSaveFeedback /></div>
                                </div>
                                {Object.keys(viewingHistoryItem.evaluations).map((compName, index) => {
                                     const compData = availableCompetencies.find(c => c.nome === compName) || { nome: compName, tipo: 'Histórico', descricao: '' };
                                     return <CompetencyEvaluationTable key={index} competency={compData} onUpdateRating={()=>{}} onUpdateFact={(n, i, f, v) => handleUpdateFactInHistory(n, i, f, v)} onAddFact={() => handleAddFactToHistoryItem(compName)} onRemoveFact={(n, i) => handleRemoveFactInHistory(n, i)} evaluationData={viewingHistoryItem.evaluations[compName]} canEdit={true} isAppendOnly={true} />;
                                })}
                            </div>
                        )}
                    </div>

                    {/* FOOTER */}
<div style={evalFooterStyle}>
    {/* 1. Botões para os passos de Histórico e Fatos */}
    {/* ✨ CORREÇÃO: Removido "|| step === 'history'" da condição. 
        Agora o botão Voltar só aparece se estiver vendo detalhes ('view_history') 
        ou adicionando fatos antigos ('add_fact_history'). */}
    {(step === 'view_history' || step === 'add_fact_history') && (
        <button onClick={handleBackToHistoryList} style={evalGhostBtnStyle}>Voltar</button>
    )}

    {/* 2. Botão para o Passo de Seleção (Início de nova avaliação) */}
    {step === 'selection' && (
        <button 
            onClick={handleProceedToEvaluation} 
            disabled={!canProceedEvaluation} 
            style={{ ...evalPrimaryBtnStyle, background: canProceedEvaluation ? '#3b82f6' : '#94a3b8' }}
        >
            Ir para Avaliação →
        </button>
    )}

    {/* 3. Botões do Passo de Avaliação (Onde ocorre o ajuste) */}
   {/* 3. Botões do Passo de Avaliação (Onde ocorre o ajuste) */}
    {step === 'evaluation' && (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {revisionContext ? (
                    <button 
                        onClick={() => { setRevisionContext(null); setStep('history'); }} 
                        style={{ ...evalGhostBtnStyle, color: '#dc2626', borderColor: '#fecaca' }}
                    >
                        ✖ Cancelar Ajuste
                    </button>
                ) : (
                    <button onClick={handleRequestBack} style={{ ...evalGhostBtnStyle, fontSize: '0.85rem' }}>
                        ← Reconfigurar Competências
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
                
                {/* ✨ CORREÇÃO: Envolver o botão Salvar Rascunho nesta condicional */}
                {!revisionContext && (
                    <button onClick={handleSaveAndExit} style={{ ...evalGhostBtnStyle, color: '#2563eb' }}>
                        💾 Salvar Rascunho
                    </button>
                )}
                {/* ✨ FIM DA CORREÇÃO */}

                {revisionContext ? (
                    <button 
                        onClick={() => {
                            setPromptConfig({
                                title: 'Justificativa da Alteração',
                                message: 'Explique o motivo desta mudança para a aprovação do RH:',
                                placeholder: 'Ex: Correção de nota...',
                                onConfirm: (just) => handleRequestEvalRevision(evaluations, revisionContext.index, just)
                            });
                            setIsPromptOpen(true);
                        }}
                        style={{ ...evalPrimaryBtnStyle, backgroundColor: '#f59e0b' }}
                    >
                        🚀 Enviar Alterações
                    </button>
                ) : (
                    <button onClick={handleRequestFinalize} style={evalPrimaryBtnStyle}>
                        ✅ Concluir Avaliação
                    </button>
                )}
            </div>
        </div>
    )}
</div>
                </div>
            </div>
        </ModalPortal>
    );
};
    const AverageItem = ({ label, value, color }) => (
        <div style={{
            textAlign: 'center',
            padding: '16px 8px',
            borderRadius: '8px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0'
        }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color, lineHeight: '1.1' }}>
                {value}
            </div>
            <div style={{
                fontSize: '0.875rem',
                color: '#64748b',
                marginTop: '8px',
                textTransform: 'capitalize'
            }}>
                {label}
            </div>
        </div>
    );
// =============================================================================
// COMPONENTE: Linha de Comparativo de Competência Singular (Layout Ajustado)
// =============================================================================
const ComparativeRow = ({ label, currentScore, previousScore, type, areaAverageMock, competencyArea, userArea }) => {
    
    // Lógica de Variação (Histórico vs Atual)
    let delta = null;
    let deltaColor = '#94a3b8';
    let DeltaIcon = Minus;

    if (previousScore !== undefined && previousScore !== null && previousScore > 0) {
        delta = (currentScore - previousScore).toFixed(1);
        if (delta > 0) { deltaColor = '#16a34a'; DeltaIcon = TrendingUp; }
        else if (delta < 0) { deltaColor = '#ef4444'; DeltaIcon = TrendingDown; }
    }

    // --- LÓGICA DE VISIBILIDADE DA MÉDIA ---
    const isBehavioral = ['Corporativa', 'Gerencial', 'Outra'].includes(type);
    const isTechnicalNative = type === 'Técnica' && (!competencyArea || competencyArea === userArea);
    const showAverage = isBehavioral || isTechnicalNative;
    
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
            gap: '12px',
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'center',
            fontSize: '0.9rem'
        }}>
            {/* 1. Nome da Competência */}
            <div style={{fontWeight: '500', color: '#334155', display: 'flex', flexDirection: 'column'}}>
                <span>{label}</span>
                <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                    <span style={{fontSize: '0.75rem', color: '#94a3b8', fontWeight: 'normal'}}>{type}</span>
                    {!isTechnicalNative && type === 'Técnica' && competencyArea && (
                         <span style={{fontSize: '0.65rem', color: '#f59e0b', backgroundColor: '#fffbeb', padding: '1px 4px', borderRadius: '4px', border: '1px solid #fde68a'}}>
                            {competencyArea}
                         </span>
                    )}
                </div>
            </div>

            {/* 2. Nota Atual + Indicador de Status (AQUI FOI A MUDANÇA) */}
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                <span style={{fontWeight: '700', color: '#1e293b', fontSize: '1rem'}}>
                    {Number(currentScore).toFixed(1)}
                </span>
                
                {/* Indicador movido para cá */}
                {showAverage && (
                    <span style={{
                        fontSize: '0.65rem',
                        marginTop: '4px',
                        fontWeight: '600',
                        color: currentScore >= areaAverageMock ? '#166534' : '#991b1b',
                        backgroundColor: currentScore >= areaAverageMock ? '#dcfce7' : '#fee2e2',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap'
                    }}>
                        {currentScore >= areaAverageMock ? 'Acima da média' : 'Abaixo da média'}
                    </span>
                )}
            </div>

            {/* 3. Comparativo com Histórico */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: deltaColor, fontWeight: '600', fontSize: '0.85rem'}}>
                {previousScore !== null && previousScore > 0 ? (
                    <>
                        <DeltaIcon size={14} />
                        {delta > 0 ? '+' : ''}{delta}
                    </>
                ) : (
                    <span style={{color: '#cbd5e1', fontSize: '0.75rem'}}>Sem histórico</span>
                )}
            </div>

            {/* 4. Média da Área (Valor de Referência) */}
            <div style={{textAlign: 'center', fontSize: '0.85rem'}}>
                {showAverage ? (
                    <span style={{fontWeight: '600', color: '#64748b', fontSize: '1rem'}}>
                        {areaAverageMock}
                    </span>
                ) : (
                    <span style={{fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: '1.2', display: 'block'}}>
                        Média indisponível<br/>(Competência de outra área)
                    </span>
                )}
            </div>
        </div>
    );
};

// =============================================================================
// COMPONENTE: Painel de Análise Comparativa (Completo)
// =============================================================================
const ComparativeAnalysis = ({ node, overallAverage, technicalAverage, behavioralAverage, technicalData, otherData, corporateData, managerialData, historyOffset = 0 }) => {
    const [filter, setFilter] = useState('all');

    // 1. Obter Histórico Anterior usando o offset correto para evitar comparar com a avaliação atual
    const previousEvaluation = useMemo(() => {
        const history = node.data.evaluationHistory || [];
        if (history.length > historyOffset) {
            return [...history].sort((a, b) => new Date(b.date) - new Date(a.date))[historyOffset];
        }
        return null;
    }, [node, historyOffset]);

    // Mocks de dados
    const companyAvg = "4.20"; 
    const areaAvg = "4.05";
    const companyTechAvg = "4.15";
    const companyBehavAvg = "4.25";
    const areaTechAvg = "3.95";
    const areaBehavAvg = "4.10";

    // Área do Colaborador
    const userAreaName = node.data.areaName;

    // Combinar dados para a lista
    const allCompetencies = [
        ...technicalData.map(d => ({ ...d, type: 'Técnica' })),
        ...otherData.map(d => ({ ...d, type: 'Outra' })),
        ...corporateData.map(d => ({ ...d, type: 'Corporativa' })),
        ...managerialData.map(d => ({ ...d, type: 'Gerencial' }))
    ];

    const filteredList = allCompetencies.filter(comp => {
        if (filter === 'all') return true;
        if (filter === 'technical') return comp.type === 'Técnica';
        if (filter === 'behavioral') return comp.type === 'Corporativa' || comp.type === 'Outra' || comp.type === 'Gerencial';
        return true;
    });

    const ProgressBar = ({ value, color, label, subLabel }) => (
        <div style={{marginBottom: '10px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '0.8rem'}}>
                <span style={{color: '#475569', fontWeight: '500'}}>{label}</span>
                <span style={{fontWeight: '700', color: color}}>{value}</span>
            </div>
            <div style={{width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden'}}>
                <div style={{width: `${(parseFloat(value) / 5) * 100}%`, height: '100%', backgroundColor: color, borderRadius: '4px', transition: 'width 0.5s ease'}}></div>
            </div>
            {subLabel && <div style={{fontSize: '0.7rem', color: '#94a3b8', marginTop: '1px'}}>{subLabel}</div>}
        </div>
    );

    return (
        <div id="comparative-analysis-container" style={{marginTop: '32px', borderTop: '2px solid #e2e8f0', paddingTop: '32px'}}>
            <h3 data-pdf-part="comp-main-title" style={{
                color: '#1e293b', fontSize: '1.2rem', fontWeight: '700', marginBottom: '24px', 
                display: 'flex', alignItems: 'center', gap: '10px'
            }}>
                <BarChart3 /> Análise Comparativa Detalhada
            </h3>

            <div id="comparative-grid" style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '32px'}}>
                
                {/* COLUNA ESQUERDA: Resumos */}
                <div data-pdf-part="comp-summary-block" style={{backgroundColor: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', height: 'fit-content'}}>
                    
                    <h4 style={{marginTop: 0, color: '#334155', fontSize: '0.95rem', marginBottom: '16px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Comparativo Geral
                    </h4>
                    <ProgressBar value={overallAverage} color="#3b82f6" label="Colaborador (Geral)" />
                    <ProgressBar value={areaAvg} color="#f59e0b" label="Média da Área" />
                    <ProgressBar value={companyAvg} color="#10b981" label="Média da Empresa" />

                    <div style={{borderTop: '1px solid #e2e8f0', margin: '20px 0'}}></div>

                    <h4 style={{marginTop: 0, color: '#334155', fontSize: '0.95rem', marginBottom: '16px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Médias Específicas
                    </h4>

                    <div style={{marginBottom: '20px'}}>
                        <div style={{fontSize: '0.75rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '8px', letterSpacing: '0.5px'}}>
                            TÉCNICAS
                        </div>
                        <ProgressBar value={technicalAverage} color="#8b5cf6" label="Colaborador" />
                        <ProgressBar value={areaTechAvg} color="#f59e0b" label="Média da Área" />
                        <ProgressBar value={companyTechAvg} color="#10b981" label="Média da Empresa" />
                    </div>

                    <div>
                        <div style={{fontSize: '0.75rem', fontWeight: '700', color: '#ec4899', marginBottom: '8px', letterSpacing: '0.5px'}}>
                            COMPORTAMENTAIS
                        </div>
                        <ProgressBar value={behavioralAverage} color="#ec4899" label="Colaborador" />
                        <ProgressBar value={areaBehavAvg} color="#f59e0b" label="Média da Área" />
                        <ProgressBar value={companyBehavAvg} color="#10b981" label="Média da Empresa" />
                    </div>
                </div>

                {/* COLUNA DIREITA: Lista */}
                <div id="comparative-list-column">
                    <div data-pdf-ignore="true" style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
                        <button onClick={() => setFilter('all')} style={{padding: '8px 16px', borderRadius: '20px', border: filter === 'all' ? 'none' : '1px solid #cbd5e1', backgroundColor: filter === 'all' ? '#1e293b' : 'white', color: filter === 'all' ? 'white' : '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500'}}>Todos</button>
                        <button onClick={() => setFilter('technical')} style={{padding: '8px 16px', borderRadius: '20px', border: filter === 'technical' ? 'none' : '1px solid #cbd5e1', backgroundColor: filter === 'technical' ? '#8b5cf6' : 'white', color: filter === 'technical' ? 'white' : '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500'}}>Técnicas</button>
                        <button onClick={() => setFilter('behavioral')} style={{padding: '8px 16px', borderRadius: '20px', border: filter === 'behavioral' ? 'none' : '1px solid #cbd5e1', backgroundColor: filter === 'behavioral' ? '#ec4899' : 'white', color: filter === 'behavioral' ? 'white' : '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500'}}>Comportamentais</button>
                    </div>

                    <div data-pdf-part="comp-table-header" style={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '12px', padding: '10px 16px', backgroundColor: '#f1f5f9', borderRadius: '8px 8px 0 0', fontSize: '0.8rem', fontWeight: '700', color: '#475569', border: '1px solid #e2e8f0', borderBottom: 'none'}}>
                        <div>Competência</div>
                        <div style={{textAlign: 'center'}}>Nota Atual</div>
                        <div style={{textAlign: 'center'}}>Evolução</div>
                        <div style={{textAlign: 'center'}}>Média Área</div>
                    </div>

                    <div 
                        data-pdf-expand="true"
                        style={{border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', maxHeight: '450px', overflowY: 'auto', backgroundColor: '#fff'}}
                    >
                        {filteredList.map((comp, idx) => {
                            const prevScore = previousEvaluation?.evaluations?.[comp.subject]?.rating || null;
                            const areaAvgMock = (Math.random() * (4.8 - 3.5) + 3.5).toFixed(1);

                            // Busca dados extras
                            let originalComp = null;
                            if (comp.type === 'Técnica') {
                                originalComp = (node.data.competenciasTecnicas || []).find(c => c.nome === comp.subject);
                            }
                            const compArea = originalComp ? originalComp.areaName : null;

                            return (
                                <div key={idx} data-pdf-part="comp-row">
                                    <ComparativeRow 
                                        label={comp.subject}
                                        type={comp.type}
                                        currentScore={comp.score}
                                        previousScore={prevScore}
                                        areaAverageMock={areaAvgMock}
                                        competencyArea={compArea} 
                                        userArea={userAreaName}   
                                    />
                                </div>
                            );
                        })}
                        {filteredList.length === 0 && (
                            <div style={{padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic'}}>
                                Nenhuma competência encontrada para este filtro.
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

// =============================================================================
// COMPONENTE: Modal de Resultados
// =============================================================================
const EvaluationResultModal = ({ isOpen, onClose, node, dicionarioCompetencias }) => {
    if (!isOpen || !node) return null;

    // ✨ CORREÇÃO AQUI: Lógica inteligente para recuperar as notas!
    const hasActiveDraft = Object.keys(node.data.evaluations || {}).length > 0;
    let evaluations = {};
    let historyOffset = 0; // Controla com qual histórico a análise comparativa vai fazer o diff

    if (hasActiveDraft) {
        // Tem rascunho? Usa ele (a avaliação está a decorrer)
        evaluations = node.data.evaluations;
        historyOffset = 0; // Compara evolução com o item 0 do histórico
    } else if (node.data.evaluationHistory && node.data.evaluationHistory.length > 0) {
        // Se o rascunho está limpo (foi finalizado), a avaliação "atual" é o último histórico fechado!
        const sortedHistory = [...node.data.evaluationHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
        evaluations = sortedHistory[0].evaluations || {};
        historyOffset = 1; // Compara evolução com o penúltimo histórico (índice 1)
    }
    // ✨ FIM DA CORREÇÃO

    const technicalCompetencies = node.data?.competenciasTecnicas || [];
    const otherCompetencies = node.data?.outrasCompetencias || [];
    const corporateCompetencies = dicionarioCompetencias?.corporativas || [];
    const managerialCompetencies = node.data?.isGerencial ? (dicionarioCompetencias?.gerenciais || []) : [];

    // Função auxiliar para criar dados do gráfico e cálculo
    const createChartData = (competencies) => {
        return competencies.map(comp => ({
            subject: comp.nome,
            score: evaluations[comp.nome]?.rating || 0,
            fullMark: 5
        }));
    };

    // Calcula a média APENAS das competências que possuem nota (score > 0)
    const calculateAverage = (data) => {
        if (!data || data.length === 0) return "0.00";
        
        // Filtra apenas os itens que foram avaliados (score maior que 0)
        const ratedItems = data.filter(item => item.score > 0);

        // Se nenhum item foi avaliado, a média é 0
        if (ratedItems.length === 0) return "0.00";

        const totalScore = ratedItems.reduce((acc, item) => acc + item.score, 0);
        return (totalScore / ratedItems.length).toFixed(2);
    };

    // Prepara dados
    const technicalData = createChartData(technicalCompetencies);
    const otherData = createChartData(otherCompetencies);
    const corporateData = createChartData(corporateCompetencies);
    const managerialData = createChartData(managerialCompetencies);

    // Calcula médias individuais
    const technicalAverage = calculateAverage(technicalData);
    const otherAverage = calculateAverage(otherData);
    const corporateAverage = calculateAverage(corporateData);
    const managerialAverage = calculateAverage(managerialData);
    
    // Calcula média comportamental e geral
    const behavioralAverage = calculateAverage([...corporateData, ...managerialData, ...otherData]);
    const overallAverage = calculateAverage([...technicalData, ...otherData, ...corporateData, ...managerialData]);

    const modalHeaderStyle = {
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', // Fundo Slate Escuro
        padding: '28px 32px',
        borderBottom: '3px solid #475569',
        color: 'white', 
        position: 'relative'
    };

    const modalFooterStyle = {
        padding: '20px 32px',
        borderTop: '2px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        display: 'flex',
        justifyContent: 'flex-end'
    };

    // Componente interno para Card de Média
    const AverageCard = ({ label, value, color }) => (
        <div style={{
            textAlign: 'center',
            padding: '20px',
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            border: '2px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'default'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 8px 16px ${color}30`;
            e.currentTarget.style.borderColor = color;
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
            e.currentTarget.style.borderColor = '#e2e8f0';
        }}
        >
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: color, lineHeight: '1', marginBottom: '8px' }}>
                {value}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </div>
        </div>
    );

    // Componentes internos para o Gráfico Radar (Eixos e Labels)
    const CustomizedAxisTick = (props) => {
        const { x, y, payload, textAnchor } = props;
        const words = payload.value.split(' ');
        const MAX_CHARS_PER_LINE = 15;
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            if ((currentLine + ' ' + word).length <= MAX_CHARS_PER_LINE) {
                currentLine += ' ' + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);

        const verticalOffset = lines.length > 1 ? -(lines.length - 1) * 7 : 0;

        return (
            <g transform={`translate(${x},${y + verticalOffset})`}>
                <text x={0} y={0} dy={4} textAnchor={textAnchor || 'middle'} fill="#475569" fontSize="12" fontWeight="500">
                    {lines.map((line, index) => (
                        <tspan x={0} dy={index === 0 ? '1.2em' : '1.2em'} key={index}>{line}</tspan>
                    ))}
                </text>
            </g>
        );
    };

    const CustomizedRadiusTick = ({ payload, x, y }) => (
        <text x={x} y={y} textAnchor="middle" fill="#9ca3af" fontSize="12px" dy={4}>
            {payload.value}
        </text>
    );

    const ChartComponent = ({ data, color, title }) => (
        <div style={{backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'}}>
            <h3 style={{ margin: '0 0 20px', color: '#1e293b', fontSize: '1.1rem', fontWeight: '700', textAlign: 'center', paddingBottom: '12px', borderBottom: '2px solid #f1f5f9' }}>
                {title}
            </h3>
            {data.length > 0 ? (
                <div style={{ width: '100%', height: '450px' }}>
                    <ResponsiveContainer>
                        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data} margin={{ top: 30, right: 50, left: 50, bottom: 30 }}>
                            <PolarGrid gridType="polygon" stroke="#e2e8f0" />
                            <PolarAngleAxis dataKey="subject" tick={<CustomizedAxisTick />} />
                            <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} tick={<CustomizedRadiusTick />} axisLine={false} tickLine={false} />
                            <Radar name={node.data.name} dataKey="score" stroke={color} fill={color} fillOpacity={0.6} strokeWidth={2} />
                            <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #e2e8f0', borderRadius: '8px', backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', fontSize: '0.95rem' }}>
                    Nenhum dado disponível para exibir o gráfico.
                </p>
            )}
        </div>
    );

    const chartTabs = [
        {
            label: `Técnicas (${technicalData.length})`,
            content: <ChartComponent data={technicalData} color="#8b5cf6" title="Competências Técnicas" />
        },
        {
            label: `Outras (${otherData.length})`,
            content: <ChartComponent data={otherData} color="#ec4899" title="Outras Competências" />
        },
        {
            label: `Corporativas (${corporateData.length})`,
            content: <ChartComponent data={corporateData} color="#14b8a6" title="Competências Corporativas" />
        },
        ...(managerialData.length > 0 ? [{
            label: `Gerenciais (${managerialData.length})`,
            content: <ChartComponent data={managerialData} color="#f59e0b" title="Competências Gerenciais" />
        }] : [])
    ];

    return (
        <ModalPortal>
            <div style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', justifyContent: 'center', alignItems: 'center', zIndex: 1300, backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '24px' }}>
                <div style={professionalModalContentStyle}>
                    <div style={modalHeaderStyle}>
                        <button
                            onClick={onClose}
                            style={{
                                position: 'absolute', top: '20px', right: '20px', background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                        >
                            <XIcon style={{ width: '20px', height: '20px' }} />
                        </button>
                        <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                            📈 Resultados da Avaliação de Desempenho
                        </h2>
                        <p style={{ margin: '8px 0 0', fontSize: '1rem', opacity: 0.9 }}>
                            Colaborador: <strong>{node.data.name}</strong>
                        </p>
                    </div>

                    <div style={modalBodyStyle}>
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{
                                color: '#1e293b', fontSize: '1.2rem', fontWeight: '700', marginBottom: '20px',
                                paddingBottom: '12px', borderBottom: '2px solid #e2e8f0'
                            }}>
                                📊 Resumo das Médias (Considerando apenas itens avaliados)
                            </h3>
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px'
                            }}>
                                <AverageCard label="Geral" value={overallAverage} color="#1e293b" />
                                {technicalData.length > 0 && <AverageCard label="Técnicas" value={technicalAverage} color="#8b5cf6" />}
                                {otherData.length > 0 && <AverageCard label="Outras" value={otherAverage} color="#ec4899" />}
                                {corporateData.length > 0 && <AverageCard label="Corporativas" value={corporateAverage} color="#14b8a6" />}
                                {managerialData.length > 0 && <AverageCard label="Gerenciais" value={managerialAverage} color="#f59e0b" />}
                            </div>
                        </div>

                        <TabsComponent tabs={chartTabs} />

                        <ComparativeAnalysis 
                            node={node}
                            overallAverage={overallAverage}
                            technicalAverage={technicalAverage}
                            behavioralAverage={behavioralAverage}
                            technicalData={technicalData}
                            otherData={otherData}
                            corporateData={corporateData}
                            managerialData={managerialData}
                            historyOffset={historyOffset} // ✨ PASSANDO A PROPRIEDADE CORRETA
                        />

                    </div>

                    <div style={modalFooterStyle}>
                        <button onClick={onClose} style={{
                            padding: '10px 24px', fontSize: '14px', cursor: 'pointer', border: '1px solid #cbd5e1',
                            borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', backgroundColor: 'white', color: '#334155'
                        }}>
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};
// Localize o componente NodeActionDropdown (por volta da linha 1503) e substitua por este:

const NodeActionDropdown = ({ node, position, onClose, onFill, onVacate, onDelete, onOpenAreaColorModal, onOpenJobDescriptionModal, onOpenEvaluationSheetModal, onOpenEvaluationResultModal, onOpenDisciplinaryHistoryModal, onOpenPDIModal, onShowConfirmation, onOpenEvaluation360, canEdit, isGerenteViewer }) => {
    const dropdownRef = useRef(null);
    const [dynamicStyle, setDynamicStyle] = useState({
        opacity: 0,
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateX(-100%)',
        width: '280px',
        zIndex: 1100,
    });

    // ✨ LÓGICA DE VALIDAÇÃO DE COMPETÊNCIAS ADICIONADA AQUI ✨

const techCompArray = node?.data?.competenciasTecnicas || [];
const techCount = Array.isArray(techCompArray) ? techCompArray.length : 0;

// O botão deve habilitar se tiver 3 competências E (for Admin OU for Gestor)
const hasPermission = canEdit || isGerenteViewer; 
const canEvaluateFinal = techCount >= 3 && hasPermission;
    const canEvaluate = techCount >= 3;
    // --------------------------------------------------------

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        if (dropdownRef.current) {
            const menuRect = dropdownRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let newTop = position.top;
            let newLeft = position.left;

            if (newTop + menuRect.height > viewportHeight) {
                newTop = viewportHeight - menuRect.height - 10;
            }
            if (newTop < 0) {
                newTop = 10;
            }
            if (newLeft - menuRect.width < 0) {
                newLeft = menuRect.width + 10;
            }

            setDynamicStyle(prev => ({
                ...prev,
                top: `${newTop}px`,
                left: `${newLeft}px`,
                opacity: 1,
            }));
        }
    }, [position]);

    useEffect(() => {
        const trigger = document.activeElement;
        return () => { if (trigger && document.contains(trigger)) trigger.focus?.({ preventScroll: true }); };
    }, []);

    useEffect(() => {
        if (dynamicStyle.opacity === 1) dropdownRef.current?.querySelector('button:not(:disabled)')?.focus();
    }, [dynamicStyle.opacity]);

    const handleMenuKeyDown = event => {
        if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = Array.from(dropdownRef.current.querySelectorAll('button:not(:disabled)'));
        if (!buttons.length) return;
        const currentIndex = buttons.indexOf(document.activeElement);
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[nextIndex].focus();
    };

    if (!node) return null;

    const isVacant = !hasAssignedCollaborator(node.data);

    const handleActionClick = (action) => {
        onClose();
        action();
    };

    const handleVacateClick = () => {
        onClose();
        onShowConfirmation(`Tem a certeza que deseja desocupar esta vaga?`, () => onVacate());
    };

    const handleDeleteClick = () => {
        onClose();
        onShowConfirmation(`Tem a certeza que deseja excluir este card? Esta ação não pode ser desfeita.`, () => onDelete());
    };

    const baseDropdownStyle = {
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e2e8f0',
        transition: 'opacity 0.1s ease-in-out',
    };

    const menuItemStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        borderRadius: '6px',
        textAlign: 'left',
        fontSize: '14px',
        color: '#334155',
        transition: 'background-color 0.2s, color 0.2s'
    };

    const menuItemHoverStyle = {
        backgroundColor: '#f1f5f9'
    };

    const disabledMenuItemStyle = {
        color: '#cbd5e1',
        cursor: 'not-allowed',
        backgroundColor: 'transparent'
    };

    const separatorStyle = {
        height: '1px',
        backgroundColor: '#e2e8f0',
        margin: '4px 0'
    };

    const MenuItem = ({ onClick, children, style = {}, disabled = false, title = '' }) => {
        const [hover, setHover] = useState(false);

        const finalStyle = {
            ...menuItemStyle,
            ...style,
            ...(disabled ? disabledMenuItemStyle : (hover ? menuItemHoverStyle : {}))
        };

        return (
            <button
                type="button" role="menuitem"
                onClick={!disabled ? onClick : undefined}
                style={finalStyle}
                onMouseEnter={() => !disabled && setHover(true)}
                onMouseLeave={() => !disabled && setHover(false)}
                disabled={disabled}
                title={disabled ? title : ''}
            >
                {children}
            </button>
        );
    };

    return (
        <div id="gpc-node-actions-menu" ref={dropdownRef} role="menu" aria-label="Ações do colaborador" onKeyDown={handleMenuKeyDown} style={{ ...dynamicStyle, ...baseDropdownStyle, zIndex: 1300 }}>
            <MenuItem onClick={() => handleActionClick(onFill)} disabled={!canEdit || !isVacant} style={{ color: canEdit && isVacant ? '#166534' : undefined }}>
                Preencher Vaga com Colaborador
            </MenuItem>
            <MenuItem onClick={handleVacateClick} disabled={!canEdit || isVacant} style={{ color: canEdit && !isVacant ? '#d97706' : undefined }}>
                Remover Colaborador
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} disabled={!canEdit} style={{ color: canEdit ? '#dc2626' : undefined }}>
                Excluir Card
            </MenuItem>

            <div style={separatorStyle} />

            <MenuItem onClick={() => handleActionClick(onOpenAreaColorModal)}>
                Gerir / Alterar Área
            </MenuItem>
            <MenuItem onClick={() => handleActionClick(onOpenJobDescriptionModal)}>
                Editar Job Description
            </MenuItem>

            <div style={separatorStyle} />

            <MenuItem onClick={() => handleActionClick(onOpenEvaluation360)}
                disabled={isVacant || !hasPermission || !onOpenEvaluation360}
                title={isVacant ? 'Preencha a vaga primeiro' : !hasPermission ? 'Disponível para administradores e gestores' : ''}>
                <Users width={18} /> Avaliação 360°
            </MenuItem>

            {/* ✨ ITEM DE AVALIAÇÃO COM A NOVA TRAVA ✨ */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <MenuItem 
                    onClick={() => handleActionClick(onOpenEvaluationSheetModal)} 
                    disabled={isVacant || !canEvaluate} 
                    title={isVacant ? "Preencha a vaga primeiro" : !canEvaluate ? "Mínimo de 3 competências técnicas" : ""}
                    style={{
                        fontWeight: canEvaluate ? '600' : '400'
                    }}
                >
                    <EvaluationIcon /> Carregar Ficha de Avaliação
                </MenuItem>
                
                {/* Mensagem de alerta caso não tenha 3 competências e a vaga não esteja vazia */}
                {!isVacant && !canEvaluate && (
                    <div style={{
                        color: '#ef4444',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '0 12px 8px 38px',
                        lineHeight: '1.2',
                        maxWidth: '240px'
                    }}>
                        Defina no mínimo 3 competências técnicas no Job Description para avaliar.
                    </div>
                )}
            </div>

            <MenuItem onClick={() => handleActionClick(onOpenEvaluationResultModal)} disabled={isVacant}>
                <ChartIcon /> Ver Resultados da Avaliação
            </MenuItem>
            <MenuItem onClick={() => handleActionClick(onOpenDisciplinaryHistoryModal)} disabled={isVacant}>
                <DisciplinaryHistoryIcon /> Histórico de Avaliações
            </MenuItem>
            <MenuItem onClick={() => handleActionClick(onOpenPDIModal)} disabled={isVacant}>
                <PDIIcon /> PDI - Plano de Desenvolvimento
            </MenuItem>
        </div>
    );
};

// --- Componente Auxiliar para os Cards de Média ---
const HistorySummaryCard = ({ label, value, color }) => (
    <div style={{
        textAlign: 'center',
        padding: '12px',
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        flex: 1,
        minWidth: '100px'
    }}>
        <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: color,
            lineHeight: '1',
            marginBottom: '4px'
        }}>
            {value}
        </div>
        <div style={{
            fontSize: '0.7rem',
            color: '#64748b',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        }}>
            {label}
        </div>
    </div>
);
// =============================================================================
// MODAL DE HISTÓRICO - COM INDICADOR DE DOWNLOAD NO RODAPÉ
// =============================================================================
const DisciplinaryHistoryModal = ({ isOpen, onClose, node, canEdit, onSave, dicionarioCompetencias, scriptsLoaded }) => {
    const [description, setDescription] = useState('');
    const [allFacts, setAllFacts] = useState([]);
    const pdfContentRef = useRef(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // --- LÓGICA DE DADOS ---
    const evaluations = node?.data?.evaluations || {};
    const technicalCompetencies = node?.data?.competenciasTecnicas || [];
    const otherCompetencies = node?.data?.outrasCompetencias || [];
    const corporateCompetencies = dicionarioCompetencias?.corporativas || [];
    const managerialCompetencies = node?.data?.isGerencial ? (dicionarioCompetencias?.gerenciais || []) : [];

    const createChartData = (competencies) => competencies.map(c => ({ subject: c.nome, score: evaluations[c.nome]?.rating || 0, fullMark: 5 }));
    const calculateAverage = (data) => {
        if (!data || data.length === 0) return "0.00";
        const ratedItems = data.filter(item => item.score > 0);
        if (ratedItems.length === 0) return "0.00";
        return (ratedItems.reduce((acc, item) => acc + item.score, 0) / ratedItems.length).toFixed(2);
    };

    const technicalData = createChartData(technicalCompetencies);
    const otherData = createChartData(otherCompetencies);
    const corporateData = createChartData(corporateCompetencies);
    const managerialData = createChartData(managerialCompetencies);

    const technicalAverage = calculateAverage(technicalData);
    const otherAverage = calculateAverage(otherData);
    const corporateAverage = calculateAverage(corporateData);
    const managerialAverage = calculateAverage(managerialData);
    const behavioralAverage = calculateAverage([...corporateData, ...managerialData, ...otherData]);
    const overallAverage = calculateAverage([...technicalData, ...otherData, ...corporateData, ...managerialData]);

    useEffect(() => {
        if (isOpen && node) {
            setDescription(node.data.disciplinaryHistory?.description || '');
            const evaluationsData = node.data.evaluations || {};
            const factsList = [];
            const processCompetencies = (competencies, type) => {
                if (!Array.isArray(competencies)) return;
                competencies.forEach(comp => {
                    const evaluation = evaluationsData[comp.nome];
                    if (evaluation && Array.isArray(evaluation.facts)) {
                        evaluation.facts.forEach(fact => {
                            if (fact.factDate && fact.fact && fact.factDescription) {
                                factsList.push({
                                    competencyType: type, competencyName: comp.nome, rating: evaluation.rating,
                                    date: fact.factDate, summary: fact.fact, details: fact.factDescription
                                });
                            }
                        });
                    }
                });
            };
            processCompetencies(node.data.competenciasTecnicas, 'Técnica');
            processCompetencies(node.data.outrasCompetencias, 'Outra');
            processCompetencies(dicionarioCompetencias?.corporativas, 'Corporativa');
            if (node.data.isGerencial) processCompetencies(dicionarioCompetencias?.gerenciais, 'Gerencial');
            factsList.sort((a, b) => new Date(b.date) - new Date(a.date));
            setAllFacts(factsList);
        }
    }, [isOpen, node, dicionarioCompetencias]);

    const handleSave = () => onSave({ description });

    // --- LÓGICA DE PDF INTELIGENTE ---
    const handleGeneratePdf = async () => {
        if (!scriptsLoaded || isGeneratingPdf || !pdfContentRef.current) return;
        setIsGeneratingPdf(true);

        const sourceElement = pdfContentRef.current;
        
        // 1. Ocultar botões
        const elementsToHide = sourceElement.querySelectorAll('[data-pdf-ignore]');
        elementsToHide.forEach(el => el.style.display = 'none');

        // 2. Expandir Listas
        const expandedElements = [];
        const expandableLists = sourceElement.querySelectorAll('[data-pdf-expand]');
        expandableLists.forEach(el => {
            expandedElements.push({ element: el, originalMaxHeight: el.style.maxHeight, originalOverflowY: el.style.overflowY, originalHeight: el.style.height });
            el.style.maxHeight = 'none';
            el.style.overflowY = 'visible';
            el.style.height = 'auto';
        });

        // 3. Linearizar Grid
        const compGrid = sourceElement.querySelector('#comparative-grid');
        let originalGridTemplate = '';
        let originalGap = '';
        if (compGrid) {
            originalGridTemplate = compGrid.style.gridTemplateColumns;
            originalGap = compGrid.style.gap;
            compGrid.style.gridTemplateColumns = '1fr'; 
            compGrid.style.gap = '20px';
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        const jsPDF = window.jspdf.jsPDF;
        const pdf = new jsPDF('p', 'mm', 'a4');
        const PAGE_HEIGHT = pdf.internal.pageSize.getHeight();
        const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
        const MARGIN = 10;
        const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
        
        let cursorY = MARGIN;

        const addBlockToPdf = async (element) => {
            if (!element) return;
            
            const canvas = await window.html2canvas(element, {
                scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff',
                windowWidth: element.scrollWidth, windowHeight: element.scrollHeight
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const imgHeight = (canvas.height * CONTENT_WIDTH) / canvas.width;

            if (cursorY + imgHeight > PAGE_HEIGHT - MARGIN) {
                pdf.addPage();
                cursorY = MARGIN;
            }
            pdf.addImage(imgData, 'JPEG', MARGIN, cursorY, CONTENT_WIDTH, imgHeight, undefined, 'FAST');
            cursorY += imgHeight + 5;
        };

        try {
            await addBlockToPdf(sourceElement.querySelector('[data-pdf-part="header"]'));
            await addBlockToPdf(sourceElement.querySelector('[data-pdf-part="description"]'));

            const compTitle = sourceElement.querySelector('[data-pdf-part="comp-main-title"]');
            await addBlockToPdf(compTitle);
            
            const compSummary = sourceElement.querySelector('[data-pdf-part="comp-summary-block"]');
            await addBlockToPdf(compSummary);

            const compTableHeader = sourceElement.querySelector('[data-pdf-part="comp-table-header"]');
            await addBlockToPdf(compTableHeader);

            const compRows = sourceElement.querySelectorAll('[data-pdf-part="comp-row"]');
            for (let i = 0; i < compRows.length; i++) {
                await addBlockToPdf(compRows[i]);
            }

            const factsHeader = sourceElement.querySelector('[data-pdf-part="facts-header"]');
            const factItems = sourceElement.querySelectorAll('[data-pdf-part="fact-item"]');
            const noFacts = sourceElement.querySelector('[data-pdf-part="no-facts"]');

            if (factItems.length > 0 || noFacts) {
                pdf.addPage(); 
                cursorY = MARGIN; 

                await addBlockToPdf(factsHeader);

                if (noFacts) {
                    await addBlockToPdf(noFacts);
                } else {
                    for (let i = 0; i < factItems.length; i++) {
                        await addBlockToPdf(factItems[i]);
                    }
                }
            }

            pdf.save(`historico-${node.data.name.replace(/\s+/g, '_')}.pdf`);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            alert('Erro ao gerar PDF.');
        } finally {
            elementsToHide.forEach(el => el.style.display = '');
            expandedElements.forEach(item => {
                item.element.style.maxHeight = item.originalMaxHeight;
                item.element.style.overflowY = item.originalOverflowY;
                item.element.style.height = item.originalHeight;
            });
            if (compGrid) {
                compGrid.style.gridTemplateColumns = originalGridTemplate;
                compGrid.style.gap = originalGap;
            }
            setIsGeneratingPdf(false);
        }
    };

    if (!isOpen || !node) return null;

    // --- ESTILOS ---
    const ratingColors = { 1: '#ef4444', 2: '#f97316', 3: '#facc15', 4: '#a3e635', 5: '#22c55e' };
    const competencyTypeStyle = (type) => {
        const styles = { 'Técnica': { backgroundColor: '#ffffff', color: '#7c3aed', border: '2px solid #7c3aed' }, 'Outra': { backgroundColor: '#ffffff', color: '#db2777', border: '2px solid #db2777' }, 'Corporativa': { backgroundColor: '#d1fae5', color: '#065f46' }, 'Gerencial': { backgroundColor: '#fef9c3', color: '#854d0e' } };
        const s = styles[type] || styles['Outra'];
        return { ...s, padding: '4px 12px', borderRadius: '9999px', fontSize: '0.8em', fontWeight: '600' };
    };
    const professionalModalContentStyle = { background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)', padding: '0', borderRadius: '16px', width: '95%', maxWidth: '1100px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)', margin: 0, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
    const modalHeaderStyle = { background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', padding: '28px 32px', borderBottom: '3px solid #f87171', color: 'white', position: 'relative' };
    const modalBodyStyle = { padding: '32px', overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, backgroundColor: '#ffffff', maxHeight: 'calc(90vh - 200px)' };
    const modalFooterStyle = { padding: '16px 32px', borderTop: '2px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '12px' }; // Alterado para flexDirection column para acomodar o loader
    const factCardStyle = { border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '16px', backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', pageBreakInside: 'avoid' };
    const secondaryButtonStyle = { padding: '10px 18px', fontSize: '14px', cursor: 'pointer', border: 'none', borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#3b82f6', color: 'white' };
    const primaryButtonStyle = { padding: '10px 18px', fontSize: '14px', cursor: 'pointer', border: 'none', borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#16a34a', color: 'white' };

    return (
        <ModalPortal>
            {/* Estilo para animação de rotação */}
            <style>
                {`@keyframes spin-loader { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
            
            <div style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', justifyContent: 'center', alignItems: 'center', zIndex: 1300, backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '24px' }}>
                <div style={professionalModalContentStyle}>
                    <div ref={pdfContentRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fff' }}>
                        
                        <div data-pdf-part="header" style={modalHeaderStyle}>
                            <button data-pdf-ignore="true" onClick={onClose} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', transition: 'background 0.2s' }}>
                                <XIcon style={{ width: '20px', height: '20px' }} />
                            </button>
                            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>📋 Histórico Completo</h2>
                            <p style={{ margin: '8px 0 0', fontSize: '1rem', opacity: 0.9 }}>Colaborador: <strong>{node.data.name}</strong></p>
                        </div>

                        <div data-pdf-part="modal-body" style={modalBodyStyle}>
                            <div data-pdf-part="description" style={{ marginBottom: '32px' }}>
                                <label style={{ display: 'block', fontWeight: '500', color: '#1e293b', marginBottom: '12px', fontSize: '1.05rem' }}>📝 Descritivo Geral do Histórico</label>
                                <textarea
                                    style={{ padding: '14px', fontSize: '0.95rem', borderRadius: '10px', border: '2px solid #e2e8f0', backgroundColor: '#fff', width: '100%', minHeight: '120px', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
                                    placeholder="Resumo geral..." value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit}
                                />
                            </div>

                            <div data-pdf-part="comparative-analysis">
                                <ComparativeAnalysis 
                                    node={node} overallAverage={overallAverage} technicalAverage={technicalAverage}
                                    behavioralAverage={behavioralAverage} technicalData={technicalData} otherData={otherData}
                                    corporateData={corporateData} managerialData={managerialData}
                                />
                            </div>

                            <div>
                                <div data-pdf-part="facts-header">
                                    <h3 style={{ color: '#1e293b', borderTop: '2px solid #e2e8f0', paddingTop: '24px', marginTop: '40px', marginBottom: '20px', fontSize: '1.2rem', fontWeight: '700' }}>
                                        🎯 Fatos Irrefutáveis Registados
                                    </h3>
                                </div>
                                <div data-pdf-part="facts-container">
                                    {allFacts.length > 0 ? (
                                        <div style={{ paddingRight: '10px' }}>
                                            {allFacts.map((fact, index) => (
                                                <div key={index} data-pdf-part="fact-item" style={factCardStyle}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                            <span style={competencyTypeStyle(fact.competencyType)}>{fact.competencyType}</span>
                                                            <strong style={{ color: '#1e293b', fontSize: '1.05rem' }}>{fact.competencyName}</strong>
                                                        </div>
                                                        {fact.rating && (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontWeight: '500', color: '#64748b', fontSize: '0.9rem' }}>Avaliação:</span>
                                                                <span style={{ backgroundColor: ratingColors[fact.rating] || '#d1d5db', color: 'white', fontWeight: 'bold', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>{fact.rating}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ borderTop: '2px dashed #e2e8f0', paddingTop: '16px', display: 'grid', gap: '12px' }}>
                                                        <div><strong style={{ color: '#334155', fontSize: '0.9rem' }}>📅 Data: </strong><span style={{ color: '#475569' }}>{fact.date ? new Date(fact.date + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/A'}</span></div>
                                                        <div><strong style={{ color: '#334155', fontSize: '0.9rem' }}>💼 Resumo: </strong><span style={{ color: '#475569' }}>{fact.summary || 'N/A'}</span></div>
                                                        <div><strong style={{ color: '#334155', fontSize: '0.9rem', display: 'block', marginBottom: '6px' }}>📄 Detalhes:</strong><p style={{ margin: 0, fontSize: '0.9em', color: '#475569', whiteSpace: 'pre-wrap', lineHeight: '1.6', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>{fact.details || 'N/A'}</p></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div data-pdf-part="no-facts" style={{ textAlign: 'center', padding: '40px 20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1' }}>
                                            <p style={{ color: '#64748b', margin: 0, fontSize: '1rem' }}>📭 Nenhum fato irrefutável foi registado.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={modalFooterStyle}>
                            {/* INDICADOR DE CARREGAMENTO */}
                            {isGeneratingPdf && (
                                <div style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    backgroundColor: '#ecfeff', 
                                    border: '1px solid #a5f3fc', 
                                    borderRadius: '8px', 
                                    color: '#0e7490', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '12px',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}>
                                    <div style={{ 
                                        width: '18px', 
                                        height: '18px', 
                                        border: '3px solid #06b6d4', 
                                        borderTop: '3px solid transparent', 
                                        borderRadius: '50%', 
                                        animation: 'spin-loader 1s linear infinite' 
                                    }}></div>
                                    <span>Preparando o documento... Isso pode levar alguns segundos.</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <button data-pdf-ignore="true" onClick={handleGeneratePdf} style={secondaryButtonStyle} disabled={isGeneratingPdf || !scriptsLoaded}>
                                    {isGeneratingPdf ? '⏳ Processando...' : scriptsLoaded ? '📄 Baixar PDF Completo' : 'A carregar...'}
                                </button>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    {canEdit && <button data-pdf-ignore="true" onClick={handleSave} style={primaryButtonStyle}>✓ Salvar Descrição</button>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};
    const PDIModal = ({ isOpen, onClose, node, dicionarioCompetencias, canEdit, onSave, token, handleFetchError }) => {
        const MAX_PDI_ACTIONS = 5;
        const [pdiActions, setPdiActions] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [isSaving, setIsSaving] = useState(false);
        const [availableCompetencies, setAvailableCompetencies] = useState([]);
        const [successionData, setSuccessionData] = useState({
            role: '',
            time: '',
            actions: '',
            feedback: ''
        });

        useEffect(() => {
            if (isOpen && node?.data?.userId) {
                const competenciasTecnicas = (node.data?.competenciasTecnicas || []).map(c => ({ ...c, tipo: 'Técnica' }));
                const outrasCompetencias = (node.data?.outrasCompetencias || []).map(c => ({ ...c, tipo: 'Outra' }));
                const corporativas = (dicionarioCompetencias?.corporativas || []).map(c => ({ ...c, tipo: 'Corporativa' }));
                const gerenciais = node.data?.isGerencial ? (dicionarioCompetencias?.gerenciais || []).map(c => ({ ...c, tipo: 'Gerencial' })) : [];

                const allCompetencies = [...competenciasTecnicas, ...outrasCompetencias, ...corporativas, ...gerenciais];
                const uniqueCompetencies = [...new Map(allCompetencies.map(item => [item.nome, item])).values()].filter(comp => comp && comp.nome);
                setAvailableCompetencies(uniqueCompetencies);

                setIsLoading(true);
                fetch(`${process.env.REACT_APP_API_URL}/users/${node.data.userId}/pdi-actions`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                    .then(handleFetchError)
                    .then(data => {
                        let actions = [];
                        
                        // Verifica se a resposta é um objeto (novo formato) ou array (legado)
                        if (data && !Array.isArray(data) && data.actions) {
                            actions = data.actions;
                            if (data.succession) {
                                setSuccessionData(data.succession);
                            }
                        } else if (Array.isArray(data)) {
                            actions = data;
                        }

                        if (actions && actions.length > 0) {
                            const formattedActions = actions.map(action => ({
                                id: action.id,
                                competenciaNome: action.competencia,
                                acao: action.acao,
                                deadline: action.deadline ? action.deadline.split('T')[0] : '',
                                prioridade: action.prioridade,
                                evidencia: action.evidencia
                            }));
                            setPdiActions(formattedActions);
                        } else {
                            setPdiActions([{ id: Date.now(), competenciaNome: '', acao: '', deadline: '', prioridade: 'Média', evidencia: '' }]);
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao buscar PDI:', err);
                        setPdiActions([{ id: Date.now(), competenciaNome: '', acao: '', deadline: '', prioridade: 'Média', evidencia: '' }]);
                    })
                    .finally(() => setIsLoading(false));
            }
        }, [isOpen, node, dicionarioCompetencias, token, handleFetchError]);

        const handleAddRow = () => {
            if (pdiActions.length < MAX_PDI_ACTIONS && canEdit) {
                setPdiActions([...pdiActions, { id: Date.now(), competenciaNome: '', acao: '', deadline: '', prioridade: 'Média', evidencia: '' }]);
            }
        };

        const handleRemoveRow = (idToRemove) => {
            if (pdiActions.length > 1 && canEdit) {
                setPdiActions(pdiActions.filter(action => action.id !== idToRemove));
            }
        };

        const handleInputChange = (id, field, value) => {
            setPdiActions(pdiActions.map(action => action.id === id ? { ...action, [field]: value } : action));
        };

        const handleSave = async () => {
            if (!canEdit) return;
            setIsSaving(true);
            const validActions = pdiActions.filter(a => a.competenciaNome && a.acao);
            
            // Envia objeto com ações e dados de sucessão
            // O backend deve estar preparado para receber este formato ou o array antigo
            await onSave({ 
                actions: validActions, 
                succession: successionData 
            });
            
            setIsSaving(false);
        };

        if (!isOpen || !node) return null;

        // ===== ESTILOS PROFISSIONAIS =====
        const professionalModalContentStyle = {
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
            padding: '0',
            borderRadius: '16px',
            width: '95%',
            maxWidth: '1100px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            margin: 0,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        };

        const modalHeaderStyle = {
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            padding: '28px 32px',
            borderBottom: '3px solid #22d3ee',
            color: 'white',
            position: 'relative'
        };

        const modalBodyStyle = {
            padding: '32px',
            overflowY: 'auto',
            flexGrow: 1,
            backgroundColor: '#ffffff'
        };

        const modalFooterStyle = {
            padding: '20px 32px',
            borderTop: '2px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap'
        };

        const pdiCardStyle = {
            background: '#fff',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'transform 0.2s, box-shadow 0.2s'
        };

        const prioridadeColors = {
            'Baixa': { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
            'Média': { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
            'Alta': { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' }
        };

        const competencyTypeStyle = (tipo) => {
            const styles = {
                'Técnica': { backgroundColor: '#ede9fe', color: '#5b21b6' },
                'Outra': { backgroundColor: '#fce7f3', color: '#9f1239' },
                'Corporativa': { backgroundColor: '#d1fae5', color: '#065f46' },
                'Gerencial': { backgroundColor: '#fef9c3', color: '#854d0e' }
            };
            return styles[tipo] || {};
        };

        return (
            <ModalPortal>
                <div style={modalOverlayStyle}>
                    <div style={professionalModalContentStyle}>
                        {/* Header */}
                        <div style={modalHeaderStyle}>
                            <button
                                onClick={onClose}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    width: '36px',
                                    height: '36px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    transition: 'background 0.2s',
                                    zIndex: 10
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >
                                <XIcon style={{ width: '20px', height: '20px' }} />
                            </button>
                            
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px' }}>
                                    🎯 Plano de Desenvolvimento Individual (PDI)
                                </h2>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    gap: '16px',
                                    paddingRight: '50px'
                                }}>
                                    <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>
                                        Colaborador: <strong>{node.data.name}</strong>
                                    </p>
                                    <div style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '9999px',
                                        padding: '6px 14px',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {pdiActions.length} / {MAX_PDI_ACTIONS} Ações
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Body */}
                        <div style={modalBodyStyle}>
                            {isLoading ? (
                                <p style={{ textAlign: 'center', color: '#475569', padding: '40px', fontSize: '1rem' }}>
                                    🔄 A carregar PDI...
                                </p>
                            ) : (
                                <>
                                    {pdiActions.map((action, index) => {
                                        const competency = availableCompetencies.find(c => c.nome === action.competenciaNome);
                                        const prioridadeStyle = prioridadeColors[action.prioridade] || prioridadeColors['Média'];

                                        return (
                                            <div
                                                key={action.id}
                                                style={pdiCardStyle}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.08)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                                                }}
                                            >
                                                {/* Badge de número */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-12px',
                                                    left: '24px',
                                                    backgroundColor: '#0891b2',
                                                    color: 'white',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold',
                                                    fontSize: '1rem',
                                                    boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)'
                                                }}>
                                                    {index + 1}
                                                </div>

                                                {/* Botão de remover */}
                                                {canEdit && pdiActions.length > 1 && (
                                                    <button
                                                        onClick={() => handleRemoveRow(action.id)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '16px',
                                                            right: '16px',
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            border: '2px solid #fecaca',
                                                            borderRadius: '8px',
                                                            width: '32px',
                                                            height: '32px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#fecaca';
                                                            e.currentTarget.style.transform = 'scale(1.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#fee2e2';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                        title="Remover Ação"
                                                    >
                                                        <DeleteIcon />
                                                    </button>
                                                )}

                                                {/* Competência */}
                                                <div style={{ marginBottom: '20px', marginTop: '8px' }}>
                                                    <label style={{ ...formLabelStyle, marginBottom: '10px', display: 'block' }}>
                                                        🎯 Competência a Desenvolver
                                                    </label>
                                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                        <select
                                                            style={{ ...inputStyle, flexGrow: 1 }}
                                                            value={action.competenciaNome}
                                                            onChange={(e) => handleInputChange(action.id, 'competenciaNome', e.target.value)}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value="">Selecione uma competência...</option>
                                                            {availableCompetencies.map(comp => (
                                                                <option key={`${comp.nome}-${comp.tipo}`} value={comp.nome}>
                                                                    {comp.nome} ({comp.tipo})
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {competency && (
                                                            <span style={{
                                                                ...competencyTypeStyle(competency.tipo),
                                                                padding: '6px 12px',
                                                                borderRadius: '9999px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '600',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {competency.tipo}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Ação */}
                                                <div style={{ marginBottom: '20px' }}>
                                                    <label style={{ ...formLabelStyle, marginBottom: '10px', display: 'block' }}>
                                                        📝 Ação de Desenvolvimento
                                                    </label>
                                                    <textarea
                                                        style={{ ...textareaStyle, minHeight: '100px' }}
                                                        placeholder="Ex: Fazer curso online de comunicação assertiva na plataforma X..."
                                                        value={action.acao}
                                                        onChange={(e) => handleInputChange(action.id, 'acao', e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>

                                                {/* Grid: Deadline + Prioridade */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                                    <div>
                                                        <label style={{ ...formLabelStyle, marginBottom: '10px', display: 'block' }}>
                                                            📅 Deadline
                                                        </label>
                                                        <input
                                                            type="date"
                                                            style={inputStyle}
                                                            value={action.deadline}
                                                            onChange={(e) => handleInputChange(action.id, 'deadline', e.target.value)}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ ...formLabelStyle, marginBottom: '10px', display: 'block' }}>
                                                            ⚡ Prioridade
                                                        </label>
                                                        <select
                                                            style={{
                                                                ...inputStyle,
                                                                backgroundColor: prioridadeStyle.bg,
                                                                color: prioridadeStyle.color,
                                                                borderColor: prioridadeStyle.border,
                                                                fontWeight: '600'
                                                            }}
                                                            value={action.prioridade}
                                                            onChange={(e) => handleInputChange(action.id, 'prioridade', e.target.value)}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value="Baixa">Baixa</option>
                                                            <option value="Média">Média</option>
                                                            <option value="Alta">Alta</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Evidência */}
                                                <div>
                                                    <label style={{ ...formLabelStyle, marginBottom: '10px', display: 'block' }}>
                                                        ✅ Evidência da Conclusão
                                                    </label>
                                                    <textarea
                                                        style={{ ...textareaStyle, minHeight: '80px' }}
                                                        placeholder="Ex: Certificado de conclusão, link para o projeto, feedback do gestor..."
                                                        value={action.evidencia}
                                                        onChange={(e) => handleInputChange(action.id, 'evidencia', e.target.value)}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}

                            {/* --- CARD SUCESSÓRIO --- */}
                            <div style={{
                                background: '#fff',
                                border: '2px solid #8b5cf6', // Roxo para destacar
                                borderRadius: '12px',
                                padding: '24px',
                                marginBottom: '20px',
                                position: 'relative',
                                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
                                marginTop: '32px'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '8px',
                                    background: 'linear-gradient(90deg, #8b5cf6 0%, #d946ef 100%)',
                                    borderTopLeftRadius: '10px',
                                    borderTopRightRadius: '10px'
                                }}></div>
                                
                                <h3 style={{ marginTop: '12px', marginBottom: '24px', color: '#4c1d95', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem' }}>
                                    🚀 Plano Sucessório
                                </h3>

                                {/* Cargo de Sucessão */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ ...formLabelStyle, color: '#5b21b6' }}>Cargo de Sucessão</label>
                                    <input 
                                        type="text" 
                                        style={{...inputStyle, borderColor: '#ddd6fe'}} 
                                        value={successionData.role || ''}
                                        onChange={(e) => setSuccessionData(prev => ({...prev, role: e.target.value}))}
                                        placeholder="Para qual cargo este colaborador está sendo preparado?"
                                        disabled={!canEdit}
                                    />
                                </div>

                                {/* Tempo estimado */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ ...formLabelStyle, color: '#5b21b6' }}>Tempo estimado de preparação</label>
                                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
                                        {['6 meses', '1 ano', '2 anos', 'mais de 3 anos'].map((timeOption) => (
                                            <label key={timeOption} style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px', 
                                                cursor: canEdit ? 'pointer' : 'default',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                backgroundColor: successionData.time === timeOption ? '#f3e8ff' : 'transparent',
                                                border: successionData.time === timeOption ? '1px solid #8b5cf6' : '1px solid #e5e7eb',
                                                transition: 'all 0.2s'
                                            }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={successionData.time === timeOption}
                                                    onChange={() => canEdit && setSuccessionData(prev => ({...prev, time: timeOption}))}
                                                    style={{ accentColor: '#8b5cf6', width: '16px', height: '16px' }}
                                                    disabled={!canEdit}
                                                />
                                                <span style={{ color: successionData.time === timeOption ? '#4c1d95' : '#374151', fontWeight: successionData.time === timeOption ? '600' : '400' }}>{timeOption}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Principais ações */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ ...formLabelStyle, color: '#5b21b6' }}>Principais ações de preparação de sucessão</label>
                                    <textarea 
                                        style={{...textareaStyle, minHeight: '80px', borderColor: '#ddd6fe'}} 
                                        value={successionData.actions || ''}
                                        onChange={(e) => setSuccessionData(prev => ({...prev, actions: e.target.value}))}
                                        placeholder="Quais ações específicas são necessárias para preparar este sucessor?"
                                        disabled={!canEdit}
                                    />
                                </div>

                                {/* Feedback */}
                                <div>
                                    <label style={{ ...formLabelStyle, color: '#5b21b6' }}>Feedback do processo</label>
                                    <textarea 
                                        style={{...textareaStyle, minHeight: '80px', borderColor: '#ddd6fe'}} 
                                        value={successionData.feedback || ''}
                                        onChange={(e) => setSuccessionData(prev => ({...prev, feedback: e.target.value}))}
                                        placeholder="Feedback sobre o andamento da preparação..."
                                        disabled={!canEdit}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={modalFooterStyle}>
                            {canEdit && (
                                <button
                                    onClick={handleAddRow}
                                    disabled={pdiActions.length >= MAX_PDI_ACTIONS || isLoading}
                                    style={{
                                        ...ghostButtonStyle,
                                        opacity: pdiActions.length >= MAX_PDI_ACTIONS ? 0.5 : 1
                                    }}
                                    title={pdiActions.length >= MAX_PDI_ACTIONS ? `Limite de ${MAX_PDI_ACTIONS} ações atingido` : 'Adicionar nova ação'}
                                >
                                    + Adicionar Ação
                                </button>
                            )}
                            <div style={{ display: 'flex', gap: '12px' }}>
                                {canEdit && (
                                    <button
                                        onClick={handleSave}
                                        style={{
                                            ...primaryButtonStyle,
                                            backgroundColor: '#16a34a',
                                            padding: '12px 32px',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}
                                        disabled={isLoading || isSaving}
                                    >
                                        {isSaving ? '⏳ A Salvar...' : '✓ Salvar PDI'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </ModalPortal>
        );
    };



    const AddVacancyModal = ({
        isOpen,
        onClose,
        onConfirm,
        createDirectly = false,
        isCreating = false,
        existingAreas,
        parentNode,
        canEdit, 
        revisionContext,
        isGerenteViewer = false,
        managerAreaId = null, 
        dicionarioCompetencias,
        onCreateArea,
        onUpdateArea,
        token,
        handleFetchError,
        showConfirmation,
        areas,
        categories
    }) => {
        const getInitialJobData = () => ({
            cargo: '',
            descricaoSumaria: '',
            atividades: '',
            competenciasTecnicasObrigatorias: '',
            competenciasTecnicasDesejaveis: '',
            competenciasTecnicas: [], // Array de objetos { nome, descricao }
            outrasCompetencias: [], // Array de objetos { nome, descricao }
            isGerencial: false,
            justificativa: ''
        });

        const [areaId, setAreaId] = useState('');
        const [jobData, setJobData] = useState(getInitialJobData);
        const [numberOfCards, setNumberOfCards] = useState(1);
        const [areaView, setAreaView] = useState('list');
        const [editableArea, setEditableArea] = useState(null);
        const [newAreaName, setNewAreaName] = useState('');
        const [newAreaColor, setNewAreaColor] = useState('#ffffff');
        
        // Estados para templates
        const [jobDescriptionTemplates, setJobDescriptionTemplates] = useState([]);
        const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
        const [selectedTemplate, setSelectedTemplate] = useState(null);
        const [isSaving, setIsSaving] = useState(false);
        const [isPromptOpen, setIsPromptOpen] = useState(false);
        const justificativaRef = useRef(null);
        const [promptConfig, setPromptConfig] = useState({ title: '', message: '', onConfirm: () => {} });
        
        // Estado para controle de abas
        const [activeTab, setActiveTab] = useState(0);

        const resetToParentOrInitial = useCallback(() => {
            if (parentNode) {
                const parentArea = existingAreas.find(a => a.name === parentNode.data.areaName);
                setAreaId(parentArea?.id || '');
                setJobData({
                    cargo: parentNode.data.cargo || '',
                    descricaoSumaria: parentNode.data.descricaoSumaria || '',
                    atividades: parentNode.data.atividades || '',
                    competenciasTecnicasObrigatorias: parentNode.data.competenciasTecnicasObrigatorias || '',
                    competenciasTecnicasDesejaveis: parentNode.data.competenciasTecnicasDesejaveis || '',
                    competenciasTecnicas: parentNode.data.competenciasTecnicas || [],
                    outrasCompetencias: parentNode.data.outrasCompetencias || [],
                    isGerencial: parentNode.data.isGerencial || false,
                    justificativa: ''
                });
                setNumberOfCards(1);
                 } else if (isGerenteViewer && managerAreaId) {
      // ADICIONAR ESTE BLOCO
      // Se for gerente visualizador, pré-seta a área dele
      setAreaId(managerAreaId.toString());
      setJobData(getInitialJobData());
      setNumberOfCards(1);
            } else {
                setAreaId('');
                setJobData(getInitialJobData());
                setNumberOfCards(1);
            }
        }, [parentNode, existingAreas, isGerenteViewer, managerAreaId]);

        useEffect(() => {
            if (isOpen) {
                setAreaView('list');
                setEditableArea(null);
                resetToParentOrInitial();
                setSelectedTemplate(null);
                setActiveTab(0); // Reset para a primeira aba
                
                // Buscar templates
                setIsLoadingTemplates(true);
                fetch(`${process.env.REACT_APP_API_URL}/job-description-templates`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(handleFetchError)
                .then(data => setJobDescriptionTemplates(data || []))
                .catch(error => console.error('Erro ao buscar modelos de JD:', error))
                .finally(() => setIsLoadingTemplates(false));
            }
        }, [isOpen, parentNode, existingAreas, resetToParentOrInitial, token, handleFetchError]);

        if (!isOpen) return null;

        const handleTemplateChange = (e) => {
            const templateId = e.target.value;
            const template = jobDescriptionTemplates.find(t => t.id.toString() === templateId);
            
            if (template) {
                setJobData({
                    ...getInitialJobData(),
                    ...template.jobData,
                    // Garante que arrays sejam sempre arrays
                    competenciasTecnicas: Array.isArray(template.jobData.competenciasTecnicas) ? template.jobData.competenciasTecnicas : [],
                    outrasCompetencias: Array.isArray(template.jobData.outrasCompetencias) ? template.jobData.outrasCompetencias : [],
                    justificativa: '' // Sempre reseta justificativa
                });
                setSelectedTemplate(template);
            } else {
                resetToParentOrInitial();
                setSelectedTemplate(null);
            }
        };

const handleConfirmClick = () => {
   console.log('🔍 === DEBUG VALIDAÇÃO ===');
  console.log('isGerenteViewer:', isGerenteViewer);
  console.log('jobData:', jobData);
  console.log('jobData.justificativa:', jobData.justificativa);
  console.log('justificativaRef.current:', justificativaRef.current);
  console.log('========================');
  
  // Validação da justificativa para gerentes
  if (isGerenteViewer && (!jobData.justificativa || !jobData.justificativa.trim())) {
    
    if (justificativaRef.current) {
      // Scroll suave até o campo
      justificativaRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      // Adiciona animação após scroll
      setTimeout(() => {
        if (justificativaRef.current) {
          justificativaRef.current.classList.add('blink-red');
        }
      }, 400);
      
      // Foca no campo
      setTimeout(() => {
        if (justificativaRef.current) {
          justificativaRef.current.focus();
        }
      }, 600);
      
      // Remove a classe após animação
      setTimeout(() => {
        if (justificativaRef.current) {
          justificativaRef.current.classList.remove('blink-red');
        }
      }, 3000);
    }
    
    return; // PARA AQUI - NÃO ENVIA
  }
  
  // Continua normalmente se passou na validação
  if (isGerenteViewer) {
    if (!areaId) {
      alert('Por favor, selecione uma área.');
      return;
    }
    onConfirm({ 
            areaId, 
            jobDescription: jobData, // Mapeia jobData para jobDescription
            numberOfCards 
        });
  } else if (parentNode) {
    onConfirm({ 
            areaId, 
            jobDescription: jobData, 
            numberOfCards 
        });
  } else {
   onConfirm({ 
            areaId, 
            jobDescription: jobData, 
            numberOfCards 
        });
  }
};




        const handleFormChange = (e) => {
            const { name, value, type, checked } = e.target;
            setJobData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        };

        const handleGerencialChange = (isGerencial) => {
            setJobData(prev => ({ ...prev, isGerencial }));
        };

        // Funções para gerenciar competências
    const addCompetency = (type, competencyToAdd) => {
        setJobData(prev => ({
            ...prev,
            [type]: [...prev[type], {
                ...competencyToAdd,
                areaId: competencyToAdd.areaId ? competencyToAdd.areaId.toString() : null,
                categoriaId: competencyToAdd.categoriaId ? competencyToAdd.categoriaId.toString() : null
            }]
        }));
    };


        const removeCompetency = (type, index) => {
            setJobData(prev => ({
                ...prev,
                [type]: prev[type].filter((_, i) => i !== index)
            }));
        };

        // Funções para gerenciar áreas
        const handleEditAreaClick = () => {
            const selected = existingAreas.find(a => a.id.toString() === areaId);
            if (selected) {
                setEditableArea(selected);
                setNewAreaName(selected.name);
                setNewAreaColor(selected.color);
                setAreaView('edit');
            }
        };

        const handleSaveArea = () => {
            if (!newAreaName.trim()) {
                console.error('O nome da área não pode estar vazio.');
                return;
            }

            if (areaView === 'edit' && editableArea) {
                onUpdateArea(areaId, editableArea.id, { name: newAreaName, color: newAreaColor });
                setAreaView('list');
            } else if (areaView === 'create') {
                onCreateArea({ name: newAreaName, color: newAreaColor }, (newArea) => {
                    if (newArea && newArea.id) {
                        setAreaId(newArea.id.toString());
                    }
                    setAreaView('list');
                });
            }
        };

        // Funções para gerenciar templates
        const handleSaveAsNewTemplate = (templateName) => {
            if (!templateName) return;
            
            setIsSaving(true);
            fetch(`${process.env.REACT_APP_API_URL}/job-description-templates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: templateName, jobData })
            })
            .then(handleFetchError)
            .then(newTemplate => {
                if (newTemplate) {
                    setJobDescriptionTemplates(prev => [...prev, newTemplate]);
                    setSelectedTemplate(newTemplate);
                    setIsPromptOpen(false);
                }
            })
            .catch(error => console.error('Erro ao salvar novo modelo:', error.message))
            .finally(() => setIsSaving(false));
        };

        const handleUpdateTemplate = () => {
            if (!selectedTemplate) return;
            
            setIsSaving(true);
            fetch(`${process.env.REACT_APP_API_URL}/job-description-templates/${selectedTemplate.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: jobData.cargo || selectedTemplate.name, jobData })
            })
            .then(handleFetchError)
            .then(updatedTemplate => {
                if (updatedTemplate) {
                    setJobDescriptionTemplates(prev => prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t));
                    setSelectedTemplate(updatedTemplate);
                }
            })
            .catch(error => console.error('Erro ao atualizar modelo:', error.message))
            .finally(() => setIsSaving(false));
        };

        const handleDeleteTemplate = () => {
            if (!selectedTemplate) return;
            
            showConfirmation(`Tem a certeza que deseja excluir o modelo "${selectedTemplate.name}"? Esta ação não pode ser desfeita.`, () => {
                setIsSaving(true);
                fetch(`${process.env.REACT_APP_API_URL}/job-description-templates/${selectedTemplate.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(handleFetchError)
                .then(() => {
                    setJobDescriptionTemplates(prev => prev.filter(t => t.id !== selectedTemplate.id));
                    resetToParentOrInitial();
                    setSelectedTemplate(null);
                })
                .catch(error => console.error('Erro ao excluir modelo:', error.message))
                .finally(() => setIsSaving(false));
            });
        };

        const openSaveAsNewPrompt = () => {
            setPromptConfig({
                title: 'Salvar Novo Modelo de Job Description',
                message: 'Insira um nome para este novo modelo. Este nome será usado para o identificar na lista de seleção.',
                placeholder: `Ex: ${jobData.cargo || 'Modelo de Cargo'}`,
                onConfirm: handleSaveAsNewTemplate
            });
            setIsPromptOpen(true);
        };

        // ESTILOS (mantendo os mesmos do código original)
        const professionalModalContentStyle = {
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
            padding: 0,
            borderRadius: '16px',
            width: '95%',
            maxWidth: '1100px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            margin: 0,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        };

        const modalHeaderStyle = {
            background: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
            padding: '28px 32px',
            borderBottom: '3px solid #4ade80',
            color: 'white',
            position: 'relative'
        };

        const modalBodyStyle = {
            padding: '32px',
            overflowY: 'auto',
            flexGrow: 1,
            backgroundColor: '#ffffff'
        };

        const modalFooterStyle = {
            padding: '20px 32px',
            borderTop: '2px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
        };

        const sectionCardStyle = {
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'box-shadow 0.3s ease'
        };

        const sectionHeaderStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '2px solid #f1f5f9'
        };

        const sectionIconStyle = {
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px'
        };

        const improvedInputStyle = {
            ...inputStyle,
            fontSize: '15px',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            transition: 'all 0.2s ease',
            backgroundColor: '#ffffff'
        };

        const improvedTextareaStyle = {
            ...textareaStyle,
            fontSize: '15px',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            transition: 'all 0.2s ease',
            backgroundColor: '#ffffff',
            lineHeight: 1.6
        };

        // Ícones SVG
        const InfoIcon = () => (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
        );

        const RequirementsIcon = () => (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
        );

        const CompetencyIcon = () => (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
        );
    const renderAreaManagement = () => {
            const presetColors = [
                { color: '#ef4444', name: 'Vermelho' },
                { color: '#f97316', name: 'Laranja' },
                { color: '#f59e0b', name: 'Âmbar' },
                { color: '#eab308', name: 'Amarelo' },
                { color: '#84cc16', name: 'Lima' },
                { color: '#22c55e', name: 'Verde' },
                { color: '#10b981', name: 'Esmeralda' },
                { color: '#14b8a6', name: 'Turquesa' },
                { color: '#06b6d4', name: 'Ciano' },
                { color: '#0ea5e9', name: 'Azul Claro' },
                { color: '#3b82f6', name: 'Azul' },
                { color: '#6366f1', name: 'Índigo' },
                { color: '#8b5cf6', name: 'Violeta' },
                { color: '#a855f7', name: 'Roxo' },
                { color: '#d946ef', name: 'Fúcsia' },
                { color: '#ec4899', name: 'Rosa' },
                { color: '#f43f5e', name: 'Rosa Escuro' },
                { color: '#64748b', name: 'Cinza' }
            ];

            const LocationIcon = () => (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                </svg>
            );
    const renderAreaManagement = () => {
            const presetColors = [
                { color: '#ef4444', name: 'Vermelho' },
                { color: '#f97316', name: 'Laranja' },
                { color: '#f59e0b', name: 'Âmbar' },
                { color: '#eab308', name: 'Amarelo' },
                { color: '#84cc16', name: 'Lima' },
                { color: '#22c55e', name: 'Verde' },
                { color: '#10b981', name: 'Esmeralda' },
                { color: '#14b8a6', name: 'Turquesa' },
                { color: '#06b6d4', name: 'Ciano' },
                { color: '#0ea5e9', name: 'Azul Claro' },
                { color: '#3b82f6', name: 'Azul' },
                { color: '#6366f1', name: 'Índigo' },
                { color: '#8b5cf6', name: 'Violeta' },
                { color: '#a855f7', name: 'Roxo' },
                { color: '#d946ef', name: 'Fúcsia' },
                { color: '#ec4899', name: 'Rosa' },
                { color: '#f43f5e', name: 'Rosa Escuro' },
                { color: '#64748b', name: 'Cinza' }
            ];

            const LocationIcon = () => (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                </svg>
            );

            const ColorPresetButton = ({ color, name, isSelected, onClick }) => (
                <div
                    onClick={onClick}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: color,
                        border: isSelected ? `4px solid #1e293b` : '3px solid white',
                        boxShadow: isSelected 
                            ? `0 8px 20px rgba(0, 0, 0, 0.25), 0 0 0 2px ${color}`
                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}>
                        {isSelected && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '24px'
                            }}>✓</div>
                        )}
                    </div>
                    <span style={{fontSize: '0.75rem', color: '#64748b', fontWeight: 500, textAlign: 'center'}}>
                        {name}
                    </span>
                </div>
            );

            return (
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={{...sectionIconStyle, backgroundColor: '#22c55e', color: 'white'}}>
                            <LocationIcon />
                        </div>
                        <div style={{flexGrow: 1}}>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Área do Card</h3>
                            <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b'}}>
                                Selecione ou crie uma área para organizar o card no organograma
                            </p>
                        </div>
                    </div>

                    {areaView === 'list' ? (
                        <div style={{marginBottom: '24px'}}>
                            <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '10px', display: 'block'}}>
                                Selecionar Área
                            </label>
                            <div style={{display: 'flex', gap: '12px', alignItems: 'stretch'}}>
                                <select
                                    style={{...improvedInputStyle, flexGrow: 1}}
                                    value={areaId}
                                    onChange={(e) => setAreaId(e.target.value)}
                                >
                                    <option value="">Selecione uma área</option>
                                    {existingAreas.map(area => (
                                        <option key={area.id} value={area.id}>{area.name}</option>
                                    ))}
                                </select>

                                {!isGerenteViewer && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewAreaName('');
                                                setNewAreaColor('#3b82f6');
                                                setEditableArea(null);
                                                setAreaView('create');
                                            }}
                                            style={{
                                                ...iconButtonStyle,
                                                backgroundColor: '#16a34a',
                                                color: 'white',
                                                border: '2px solid #16a34a',
                                                padding: '0 16px',
                                                minWidth: '44px',
                                                height: '44px'
                                            }}
                                            title="Criar Nova Área"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                            </svg>
                                            <span style={{fontSize: '0.9rem', fontWeight: 600}}>Nova</span>
                                        </button>

                                        {areaId && (
                                            <button
                                                type="button"
                                                onClick={handleEditAreaClick}
                                                style={{
                                                    ...iconButtonStyle,
                                                    backgroundColor: '#FF834F',
                                                    color: 'white',
                                                    border: '2px solid #FF834F',
                                                    padding: '0 16px',
                                                    minWidth: '44px',
                                                    height: '44px'
                                                }}
                                                title="Editar Área Selecionada"
                                            >
                                                <EditIcon />
                                                <span style={{fontSize: '0.9rem', fontWeight: 600}}>Editar</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
                                <button
                                    type="button"
                                    onClick={() => setAreaView('list')}
                                    style={{
                                        ...iconButtonStyle,
                                        padding: '8px',
                                        backgroundColor: '#f1f5f9',
                                        border: '2px solid #e2e8f0'
                                    }}
                                >
                                    <BackIcon />
                                </button>
                                <h4 style={{margin: 0, color: '#1e293b', fontSize: '1.1rem'}}>
                                    {areaView === 'create' ? 'Criar Nova Área' : `Editar Área "${editableArea?.name}"`}
                                </h4>
                            </div>

                            <div style={{marginBottom: '32px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '10px', display: 'block'}}>
                                    Nome da Área
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: Marketing Digital"
                                    value={newAreaName}
                                    onChange={(e) => setNewAreaName(e.target.value)}
                                    style={{...improvedInputStyle, width: '100%', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '24px',
                                padding: '20px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                                border: '2px solid #e2e8f0'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '16px',
                                    backgroundColor: newAreaColor,
                                    border: '4px solid white',
                                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                    flexShrink: 0
                                }} />
                                <div style={{flexGrow: 1}}>
                                    <div style={{fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: '4px'}}>
                                        {newAreaName || 'Nome da Área'}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontFamily: 'monospace',
                                        backgroundColor: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        {newAreaColor.toUpperCase()}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '16px', display: 'block'}}>
                                    Cor da Área
                                </label>
                                <div style={{fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: '12px'}}>
                                    Cores Predefinidas
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '24px'
                                }}>
                                    {presetColors.map(preset => (
                                        <ColorPresetButton
                                            key={preset.color}
                                            color={preset.color}
                                            name={preset.name}
                                            isSelected={newAreaColor === preset.color}
                                            onClick={() => setNewAreaColor(preset.color)}
                                        />
                                    ))}
                                </div>
                                <div style={{fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: '12px'}}>
                                    Ou escolha uma cor personalizada
                                </div>
                                <input
                                    type="color"
                                    value={newAreaColor}
                                    onChange={(e) => setNewAreaColor(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '60px',
                                        border: '3px solid #e2e8f0',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: 'transparent'
                                    }}
                                />
                            </div>

                            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px'}}>
                                <button
                                    type="button"
                                    onClick={() => setAreaView('list')}
                                    style={{
                                        ...ghostButtonStyle,
                                        padding: '12px 24px',
                                        fontSize: '0.95rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveArea}
                                    style={{
                                        ...primaryButtonStyle,
                                        backgroundColor: '#16a34a',
                                        padding: '12px 32px',
                                        fontSize: '0.95rem',
                                        fontWeight: 600
                                    }}
                                    disabled={!newAreaName.trim()}
                                >
                                    {editableArea ? 'Salvar Alterações' : 'Salvar Nova Área'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        };
            const ColorPresetButton = ({ color, name, isSelected, onClick }) => (
                <div
                    onClick={onClick}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: color,
                        border: isSelected ? `4px solid #1e293b` : '3px solid white',
                        boxShadow: isSelected 
                            ? `0 8px 20px rgba(0, 0, 0, 0.25), 0 0 0 2px ${color}`
                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}>
                        {isSelected && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '24px'
                            }}>✓</div>
                        )}
                    </div>
                    <span style={{fontSize: '0.75rem', color: '#64748b', fontWeight: 500, textAlign: 'center'}}>
                        {name}
                    </span>
                </div>
            );

            return (
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={{...sectionIconStyle, backgroundColor: '#22c55e', color: 'white'}}>
                            <LocationIcon />
                        </div>
                        <div style={{flexGrow: 1}}>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Área do Card</h3>
                            <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b'}}>
                                Selecione ou crie uma área para organizar o card no organograma
                            </p>
                        </div>
                    </div>

                    {areaView === 'list' ? (
                        <div style={{marginBottom: '24px'}}>
                            <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '10px', display: 'block'}}>
                                Selecionar Área
                            </label>
                            <div style={{display: 'flex', gap: '12px', alignItems: 'stretch'}}>
                                <select
                                    style={{...improvedInputStyle, flexGrow: 1}}
                                    value={areaId}
                                    onChange={(e) => setAreaId(e.target.value)}
                                    disabled={isGerenteViewer}
                                >
                                    <option value="">Selecione uma área</option>
                                    {existingAreas.map(area => (
                                        <option key={area.id} value={area.id}>{area.name}</option>
                                    ))}
                                </select>

                                {!isGerenteViewer && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewAreaName('');
                                                setNewAreaColor('#3b82f6');
                                                setEditableArea(null);
                                                setAreaView('create');
                                            }}
                                            style={{
                                                ...iconButtonStyle,
                                                backgroundColor: '#16a34a',
                                                color: 'white',
                                                border: '2px solid #16a34a',
                                                padding: '0 16px',
                                                minWidth: '44px',
                                                height: '44px'
                                            }}
                                            title="Criar Nova Área"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                            </svg>
                                            <span style={{fontSize: '0.9rem', fontWeight: 600}}>Nova</span>
                                        </button>

                                        {areaId && (
                                            <button
                                                type="button"
                                                onClick={handleEditAreaClick}
                                                style={{
                                                    ...iconButtonStyle,
                                                    backgroundColor: '#FF834F',
                                                    color: 'white',
                                                    border: '2px solid #FF834F',
                                                    padding: '0 16px',
                                                    minWidth: '44px',
                                                    height: '44px'
                                                }}
                                                title="Editar Área Selecionada"
                                            >
                                                <EditIcon />
                                                <span style={{fontSize: '0.9rem', fontWeight: 600}}>Editar</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
                                <button
                                    type="button"
                                    onClick={() => setAreaView('list')}
                                    style={{
                                        ...iconButtonStyle,
                                        padding: '8px',
                                        backgroundColor: '#f1f5f9',
                                        border: '2px solid #e2e8f0'
                                    }}
                                >
                                    <BackIcon />
                                </button>
                                <h4 style={{margin: 0, color: '#1e293b', fontSize: '1.1rem'}}>
                                    {areaView === 'create' ? 'Criar Nova Área' : `Editar Área "${editableArea?.name}"`}
                                </h4>
                            </div>

                            <div style={{marginBottom: '32px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '10px', display: 'block'}}>
                                    Nome da Área
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: Marketing Digital"
                                    value={newAreaName}
                                    onChange={(e) => setNewAreaName(e.target.value)}
                                    style={{...improvedInputStyle, width: '100%', boxSizing: 'border-box'}}
                                />
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '24px',
                                padding: '20px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                                border: '2px solid #e2e8f0'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '16px',
                                    backgroundColor: newAreaColor,
                                    border: '4px solid white',
                                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                    flexShrink: 0
                                }} />
                                <div style={{flexGrow: 1}}>
                                    <div style={{fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: '4px'}}>
                                        {newAreaName || 'Nome da Área'}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontFamily: 'monospace',
                                        backgroundColor: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        {newAreaColor.toUpperCase()}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '16px', display: 'block'}}>
                                    Cor da Área
                                </label>
                                <div style={{fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: '12px'}}>
                                    Cores Predefinidas
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '24px'
                                }}>
                                    {presetColors.map(preset => (
                                        <ColorPresetButton
                                            key={preset.color}
                                            color={preset.color}
                                            name={preset.name}
                                            isSelected={newAreaColor === preset.color}
                                            onClick={() => setNewAreaColor(preset.color)}
                                        />
                                    ))}
                                </div>
                                <div style={{fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: '12px'}}>
                                    Ou escolha uma cor personalizada
                                </div>
                                <input
                                    type="color"
                                    value={newAreaColor}
                                    onChange={(e) => setNewAreaColor(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '60px',
                                        border: '3px solid #e2e8f0',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: 'transparent'
                                    }}
                                />
                            </div>

                            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px'}}>
                                <button
                                    type="button"
                                    onClick={() => setAreaView('list')}
                                    style={{
                                        ...ghostButtonStyle,
                                        padding: '12px 24px',
                                        fontSize: '0.95rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveArea}
                                    style={{
                                        ...primaryButtonStyle,
                                        backgroundColor: '#16a34a',
                                        padding: '12px 32px',
                                        fontSize: '0.95rem',
                                        fontWeight: 600
                                    }}
                                    disabled={!newAreaName.trim()}
                                >
                                    {editableArea ? 'Salvar Alterações' : 'Salvar Nova Área'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        };
        // Definição das abas
        const tabs = [
            {
                label: 'Informações Gerais',
                content: (
                    <div>
                        {/* Seletor de Template */}
                        {!isGerenteViewer && (
                            <div style={{...sectionCardStyle, background: 'linear-gradient(to right, #fef3c7 0%, #fef9c3 100%)', borderColor: '#fbbf24'}}>
                                <div style={sectionHeaderStyle}>
                                    <div style={{...sectionIconStyle, backgroundColor: '#fbbf24', color: 'white'}}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                    </div>
                                    <div style={{flexGrow: 1}}>
                                        <h3 style={{margin: 0, fontSize: '1.1rem', color: '#78350f'}}>Modelos de Job Description</h3>
                                        <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#92400e'}}>
                                            Reutilize templates existentes ou crie novos para agilizar o processo
                                        </p>
                                    </div>
                                </div>
                                <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                                    <select 
                                        style={{...improvedInputStyle, flexGrow: 1}}
                                        value={selectedTemplate?.id || ''}
                                        onChange={handleTemplateChange}
                                        disabled={isLoadingTemplates || isSaving}
                                    >
                                        <option value="">{isLoadingTemplates ? 'A carregar modelos...' : 'Selecionar um modelo...'}</option>
                                        {jobDescriptionTemplates.map(template => (
                                            <option key={template.id} value={template.id}>{template.name}</option>
                                        ))}
                                    </select>
                                    {selectedTemplate && (
                                        <button
                                            onClick={handleDeleteTemplate}
                                            style={{
                                                ...iconButtonStyle,
                                                backgroundColor: '#fee2e2',
                                                color: '#dc2626',
                                                border: '2px solid #fecaca'
                                            }}
                                            disabled={isSaving}
                                            title={`Excluir modelo "${selectedTemplate.name}"`}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Gestão de Área */}
                        {renderAreaManagement()}

                        {/* Informações do Cargo */}
                        <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                                <div style={{...sectionIconStyle, backgroundColor: '#3b82f6', color: 'white'}}>
                                    <InfoIcon />
                                </div>
                                <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Informações do Cargo</h3>
                            </div>

                        

                            {/* Slider de quantidade de cards - OCULTO PARA GERENTE */}
{!isGerenteViewer && (
  <div style={{
    padding: '24px',
    backgroundColor: '#f0f9ff',
    borderRadius: '12px',
    border: '2px solid #bfdbfe',
    marginBottom: '20px'
  }}>
    <label style={{
      ...formLabelStyle,
      fontSize: '1rem',
      marginBottom: '16px',
      display: 'block',
      color: '#1e40af'
    }}>
      Quantidade de Cards a Criar
    </label>
    
    {/* Display do número */}
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      <div style={{
        fontSize: '3rem',
        fontWeight: 'bold',
        color: '#1e40af',
        lineHeight: 1,
        marginBottom: '8px'
      }}>
        {numberOfCards}
      </div>
      <div style={{
        fontSize: '0.95rem',
        color: '#3b82f6',
        fontWeight: 500
      }}>
        {numberOfCards === 1 ? 'card será criado' : 'cards serão criados'}
      </div>
    </div>
    
    {/* Slider com marcadores */}
    <div style={{ position: 'relative', padding: '0 8px' }}>
      {/* Input range estilizado */}
      <input
        type="range"
        min={1}
        max={10}
        value={numberOfCards}
        onChange={(e) => setNumberOfCards(parseInt(e.target.value))}
        style={{
          width: '100%',
          height: '8px',
          borderRadius: '5px',
          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((numberOfCards - 1) / 9) * 100}%, #e0e7ff ${((numberOfCards - 1) / 9) * 100}%, #e0e7ff 100%)`,
          outline: 'none',
          WebkitAppearance: 'none',
          appearance: 'none',
          cursor: 'pointer'
        }}
        className="custom-slider"
      />
      
      {/* Marcadores numerados */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '12px',
        paddingLeft: '2px',
        paddingRight: '2px'
      }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <div
            key={num}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setNumberOfCards(num)}
          >
            {/* Linha indicadora */}
            <div style={{
              width: '3px',
              height: num === numberOfCards ? '14px' : '8px',
              backgroundColor: num === numberOfCards ? '#1e40af' : '#cbd5e1',
              borderRadius: '2px',
              marginBottom: '6px',
              transition: 'all 0.2s ease'
            }} />
            
            {/* Número */}
            <span style={{
              fontSize: '0.75rem',
              fontWeight: num === numberOfCards ? '700' : '500',
              color: num === numberOfCards ? '#1e40af' : '#94a3b8',
              transition: 'all 0.2s ease'
            }}>
              {num}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
)}


                            {/* Campo de Cargo */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>
                                    Cargo/Função
                                </label>
                                <input
                                    type="text"
                                    name="cargo"
                                    style={improvedInputStyle}
                                    value={jobData.cargo}
                                    onChange={handleFormChange}
                                    placeholder="Ex: Analista de Marketing Digital"
                                />
                            </div>

                            {/* Descrição Sumária */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>
                                    Descrição Sumária
                                </label>
                                <textarea
                                    name="descricaoSumaria"
                                    style={{...improvedTextareaStyle, minHeight: '100px'}}
                                    value={jobData.descricaoSumaria}
                                    onChange={handleFormChange}
                                    placeholder="Descreva resumidamente as responsabilidades e o propósito do cargo..."
                                />
                            </div>

                            {/* Atividades */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{...formLabelStyle, fontSize: '0.95rem', marginBottom: '8px'}}>
                                    Atividades e Responsabilidades
                                </label>
                                <textarea
                                    name="atividades"
                                    style={{...improvedTextareaStyle, minHeight: '180px'}}
                                    value={jobData.atividades}
                                    onChange={handleFormChange}
                                    placeholder="Liste as principais atividades&#10;Descreva tarefas do dia a dia&#10;Inclua responsabilidades específicas..."
                                />
                            </div>

                            {/* Tipo de Vaga */}
                            <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '10px',
                                border: '2px dashed #cbd5e1'
                            }}>
                                <label style={{
                                    ...formLabelStyle,
                                    fontSize: '0.95rem',
                                    marginBottom: '12px',
                                    display: 'block'
                                }}>
                                    Tipo de Vaga
                                </label>
                                <div style={{display: 'flex', gap: '24px'}}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                        <input
                                            type="radio"
                                            name="tipoVaga"
                                            checked={jobData.isGerencial}
                                            onChange={() => handleGerencialChange(true)}
                                            style={{width: '20px', height: '20px', accentColor: '#3b82f6'}}
                                        />
                                        <span style={{fontWeight: 500, color: jobData.isGerencial ? '#1e293b' : '#64748b'}}>
                                            Vaga Gerencial
                                        </span>
                                    </label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                        <input
                                            type="radio"
                                            name="tipoVaga"
                                            checked={!jobData.isGerencial}
                                            onChange={() => handleGerencialChange(false)}
                                            style={{width: '20px', height: '20px', accentColor: '#3b82f6'}}
                                        />
                                        <span style={{fontWeight: 500, color: !jobData.isGerencial ? '#1e293b' : '#64748b'}}>
                                            Vaga Não Gerencial
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            },
            {
                label: 'Requisitos',
                content: (
                    <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                            <div style={{...sectionIconStyle, backgroundColor: '#10b981', color: 'white'}}>
                                <RequirementsIcon />
                            </div>
                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Requisitos da Vaga</h3>
                        </div>

                        <div style={{marginBottom: '24px'}}>
                            <label style={{
                                ...formLabelStyle,
                                fontSize: '0.95rem',
                                marginBottom: '8px',
                                color: '#dc2626'
                            }}>
                                Requisitos Obrigatórios
                            </label>
                            <textarea
                                name="competenciasTecnicasObrigatorias"
                                style={{
                                    ...improvedTextareaStyle,
                                    minHeight: '140px',
                                    borderColor: '#fecaca'
                                }}
                                value={jobData.competenciasTecnicasObrigatorias}
                                onChange={handleFormChange}
                                placeholder="• Formação acadêmica necessária&#10;• Certificações exigidas&#10;• Experiência mínima obrigatória..."
                            />
                        </div>

                        <div>
                            <label style={{
                                ...formLabelStyle,
                                fontSize: '0.95rem',
                                marginBottom: '8px',
                                color: '#16a34a'
                            }}>
                                Requisitos Desejáveis
                            </label>
                            <textarea
                                name="competenciasTecnicasDesejaveis"
                                style={{
                                    ...improvedTextareaStyle,
                                    minHeight: '140px',
                                    borderColor: '#bbf7d0'
                                }}
                                value={jobData.competenciasTecnicasDesejaveis}
                                onChange={handleFormChange}
                                placeholder="• Diferenciais que agregam valor&#10;• Conhecimentos complementares&#10;• Experiências extras..."
                            />
                        </div>
                    </div>
                )
            },
        {  label: 'Competências Técnicas',
        content: (
            <div style={sectionCardStyle}>
                <div style={sectionHeaderStyle}>
                    <div style={{...sectionIconStyle, backgroundColor: '#8b5cf6', color: 'white'}}>
                        <CompetencyIcon />
                    </div>
                    <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Competências Técnicas</h3>
                </div>
                
                <SelectOrCreateCompetencyEditor
                    competencyType="competenciasTecnicas"
                    title="Competência Técnica"
                    competencies={jobData.competenciasTecnicas}
                    canEdit={true}
                    onAddCompetency={addCompetency}
                    onRemoveCompetency={removeCompetency}
                    dictionaryOptions={dicionarioCompetencias?.tecnicas || []}
                    areas={areas || existingAreas || []}
                    categories={categories || []}
                    revisionContext={revisionContext}
                />
            </div>
        )
    },
            {
        label: 'Outras Competências',
        content: (
            <div style={sectionCardStyle}>
                <div style={sectionHeaderStyle}>
                    <div style={{...sectionIconStyle, backgroundColor: '#ec4899', color: 'white'}}>
                        <CompetencyIcon />
                    </div>
                    <h3 style={{margin: 0, fontSize: '1.1rem', color: '#1e293b'}}>Outras Competências</h3>
                </div>
                
                <SelectOrCreateCompetencyEditor
                    competencyType="outrasCompetencias"
                    title="Outra Competência"
                    competencies={jobData.outrasCompetencias}
                    canEdit={true}
                    onAddCompetency={addCompetency}
                    onRemoveCompetency={removeCompetency}
                    dictionaryOptions={dicionarioCompetencias?.outras || []}
                    areas={areas || existingAreas || []}
                    categories={categories || []}
                    revisionContext={revisionContext}
                    
                />
            </div>
        )
    },
    // No componente AddVacancyModal, localize o array 'tabs' e adicione este item:

{
  label: 'Competências Comportamentais',
  content: (
    <>
      {/* COMPETÊNCIAS CORPORATIVAS - SEMPRE MOSTRADAS */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <div style={{
            ...sectionIconStyle,
            backgroundColor: '#14b8a6',
            color: 'white',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
            Competências Corporativas
          </h3>
        </div>
        
        {dicionarioCompetencias?.corporativas?.length > 0 ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {dicionarioCompetencias.corporativas.map((comp) => (
              <div
                key={comp.id}
                style={{
                  background: 'linear-gradient(to right, #f0fdfa, #ccfbf1)',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid #99f6e4',
                }}
              >
                <strong style={{
                  color: '#115e59',
                  fontSize: '1rem',
                  display: 'block',
                  marginBottom: '6px',
                }}>
                  {comp.nome}
                </strong>
                <p style={{
                  margin: 0,
                  fontSize: '0.9em',
                  color: '#134e4a',
                  lineHeight: '1.6',
                }}>
                  {comp.descricao}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{
            color: '#64748b',
            fontSize: '0.95em',
            fontStyle: 'italic',
            textAlign: 'center',
            padding: '20px',
          }}>
            Nenhuma competência corporativa definida.
          </p>
        )}
      </div>

      {/* COMPETÊNCIAS GERENCIAIS - APENAS SE isGerencial === true */}
      {jobData.isGerencial && (
        <div style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{
              ...sectionIconStyle,
              backgroundColor: '#f59e0b',
              color: 'white',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
              Competências Gerenciais
            </h3>
          </div>
          
          {dicionarioCompetencias?.gerenciais?.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {dicionarioCompetencias.gerenciais.map((comp) => (
                <div
                  key={comp.id}
                  style={{
                    background: 'linear-gradient(to right, #fffbeb, #fef3c7)',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #fde68a',
                  }}
                >
                  <strong style={{
                    color: '#78350f',
                    fontSize: '1rem',
                    display: 'block',
                    marginBottom: '6px',
                  }}>
                    {comp.nome}
                  </strong>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9em',
                    color: '#92400e',
                    lineHeight: '1.6',
                  }}>
                    {comp.descricao}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{
              color: '#64748b',
              fontSize: '0.95em',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '20px',
            }}>
              Nenhuma competência gerencial definida.
            </p>
          )}
        </div>
      )}
    </>
  )
}


        ];

        // Renderização de área management (mantendo o código original)
    

        return (
            <ModalPortal>
                <style>{animationStyles}</style>
                <PromptModal

                    isOpen={isPromptOpen}
                    onClose={() => setIsPromptOpen(false)}
                    onConfirm={promptConfig.onConfirm}
                    title={promptConfig.title}
                    message={promptConfig.message}
                    placeholder={promptConfig.placeholder}
                />

                <div style={modalOverlayStyle}>
                    <div style={professionalModalContentStyle}>
                        {/* Header */}
                        <div style={modalHeaderStyle}>
                            <button
                                onClick={onClose}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    width: '36px',
                                    height: '36px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >
                                <XIcon style={{width: '20px', height: '20px'}} />
                            </button>
                            <h2 style={{
                                margin: 0,
                                fontSize: '1.75rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <InboxIcon />
                                {isGerenteViewer ? 'Solicitar Nova Vaga' : 'Criar Nova Vaga'}
                            </h2>
                            <p style={{margin: '8px 0 0', fontSize: '1rem', opacity: 0.9}}>
                                {isGerenteViewer 
                                    ? 'Preencha os detalhes e envie para aprovação administrativa.'
                                    : 'Crie um novo cargo ou vincule ao quadro existente.'}
                            </p>
                            {parentNode && (
                                <p style={{margin: '8px 0 0', fontSize: '1rem', opacity: 0.9}}>
                                    Subordinada a: {parentNode.data.cargo}
                                </p>
                            )}
                        </div>
                            
                        {/* Body com Tabs */}
                        <div style={modalBodyStyle}>
                            {/* Campo de Justificativa - VISÍVEL APENAS PARA GERENTE */}
                            {isGerenteViewer && (
                                <div style={{
                                    ...sectionCardStyle,
                                    backgroundColor: '#fffbeb',
                                    border: '2px solid #fde68a',
                                    marginTop: 0,
                                    marginBottom: '24px',
                                    padding: '24px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '16px',
                                        paddingBottom: '12px',
                                        borderBottom: '2px solid #fde68a'
                                    }}>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '8px',
                                            backgroundColor: '#f59e0b',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '18px',
                                            flexShrink: 0
                                        }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                <polyline points="14 2 14 8 20 8"/>
                                                <line x1="16" y1="13" x2="8" y2="13"/>
                                                <line x1="16" y1="17" x2="8" y2="17"/>
                                                <line x1="10" y1="9" x2="8" y2="9"/>
                                            </svg>
                                        </div>
                                        <div style={{flexGrow: 1}}>
                                            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#78350f', fontWeight: 600}}>
                                                Justificativa da Solicitação
                                            </h3>
                                            <p style={{margin: '4px 0 0', fontSize: '0.85rem', color: '#92400e'}}>
                                                Explique a necessidade desta vaga
                                            </p>
                                        </div>
                                    </div>
                                        <textarea
                                    ref={justificativaRef}
                                    name="justificativa"
                                    style={{
                                        ...improvedTextareaStyle,
                                        minHeight: '120px',
                                        backgroundColor: '#ffffff',
                                        borderColor: '#fde68a',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                    placeholder="Descreva por que esta vaga é necessária, qual impacto ela terá na área/equipe, benefícios esperados..."
                                    value={jobData.justificativa}
                                    onChange={handleFormChange}
                                    />

                                    <small style={{color: '#92400e', fontSize: '0.85rem', marginTop: '8px', display: 'block', fontWeight: 500}}>
                                        Este campo é obrigatório para enviar a solicitação.
                                    </small>
                                </div>
                            )}
                            <TabsComponent tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                        </div>

                        {/* Footer */}
                        <div style={modalFooterStyle}>
                            <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                                {!isGerenteViewer && (
                                    <>
                                        <button
                                            onClick={openSaveAsNewPrompt}
                                            style={{
                                                ...secondaryButtonStyle,
                                                backgroundColor: '#f59e0b',
                                                color: 'white'
                                            }}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'A salvar...' : 'Salvar como Template'}
                                        </button>
                                        {selectedTemplate && (
                                            <button
                                                onClick={handleUpdateTemplate}
                                                style={{
                                                    ...secondaryButtonStyle,
                                                    backgroundColor: '#3b82f6',
                                                    color: 'white'
                                                }}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? 'A atualizar...' : 'Atualizar Template'}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            <button 
                            onClick={handleConfirmClick}
                            style={{
                                ...primaryButtonStyle,
                                backgroundColor: isGerenteViewer ? '#f59e0b' : '#16a34a',
                                padding: '12px 32px',
                                fontSize: '1rem',
                                fontWeight: 600
                            }}
                            disabled={isSaving || isCreating || !jobData.cargo.trim()}
                            >
                            {isCreating ? 'Criando vaga…' : isGerenteViewer 
                                ? 'Enviar para Aprovação' 
                                : (parentNode ? 'Criar e Vincular ao Quadro' : createDirectly ? 'Criar vaga' : 'Criar e Adicionar Sidebar')
                            }
                            </button>
                        </div>
                    </div>
                </div>
            </ModalPortal>
        );
    };

// --- COMPONENTES VISUAIS PARA COMPARAÇÃO (ESTILO FICHA DE AVALIAÇÃO) ---

const RatingBadge = ({ rating }) => {
    const colors = { 1: '#ef4444', 2: '#f97316', 3: '#facc15', 4: '#a3e635', 5: '#22c55e' };
    const labels = { 1: 'Insatisfatório', 2: 'Necessita Melhoria', 3: 'Satisfatório', 4: 'Bom', 5: 'Excelente' };
    
    if (!rating) return <span style={{color: '#94a3b8', fontSize: '0.8rem'}}>Não avaliado</span>;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
                width: '28px', height: '28px', borderRadius: '50%', 
                backgroundColor: colors[rating], color: 'white', fontWeight: 'bold', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' 
            }}>
                {rating}
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors[rating] }}>
                {labels[rating]}
            </span>
        </div>
    );
};

const FactItemView = ({ fact }) => (
    <div style={{ 
        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', 
        padding: '8px', marginBottom: '6px', fontSize: '0.85rem' 
    }}>
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
            <strong style={{color: '#334155'}}>{fact.fact}</strong>
            <span style={{color: '#64748b', fontSize: '0.75rem'}}>{fact.factDate ? new Date(fact.factDate).toLocaleDateString('pt-BR') : ''}</span>
        </div>
        <div style={{color: '#475569', lineHeight: '1.4'}}>{fact.factDescription}</div>
    </div>
);

const EvaluationDiffViewer = ({ oldData, newData, justification }) => {
    // Unir todas as chaves de competências para garantir que mostramos tudo
    const allKeys = Array.from(new Set([
        ...Object.keys(oldData || {}), 
        ...Object.keys(newData || {})
    ])).sort();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* 1. JUSTIFICATIVA EM DESTAQUE */}
            <div style={{ 
                backgroundColor: '#fffbeb', border: '1px solid #fcd34d', 
                borderRadius: '8px', padding: '16px', display: 'flex', gap: '12px' 
            }}>
                <div style={{ fontSize: '1.5rem' }}>📢</div>
                <div>
                    <h4 style={{ margin: '0 0 4px 0', color: '#92400e', fontSize: '1rem' }}>Justificativa do Gestor</h4>
                    <p style={{ margin: 0, color: '#b45309', lineHeight: '1.5' }}>
                        {justification || "Nenhuma justificativa fornecida."}
                    </p>
                </div>
            </div>

            {/* 2. LISTA DE COMPARAÇÃO */}
            {allKeys.map(key => {
                const oldItem = oldData?.[key];
                const newItem = newData?.[key];

                // Detectar mudanças
                const ratingChanged = oldItem?.rating !== newItem?.rating;
                const factsChanged = JSON.stringify(oldItem?.facts) !== JSON.stringify(newItem?.facts);
                const hasChanges = ratingChanged || factsChanged;

                // Se não mudou nada, não mostra (ou mostra colapsado se preferir)
                if (!hasChanges) return null; 

                return (
                    <div key={key} style={{ 
                        border: '1px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden', 
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
                    }}>
                        {/* Header da Competência */}
                        <div style={{ 
                            backgroundColor: '#f1f5f9', padding: '12px 16px', borderBottom: '1px solid #e2e8f0',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <strong style={{ color: '#1e293b', fontSize: '1rem' }}>{key}</strong>
                            <span style={{ 
                                backgroundColor: '#fee2e2', color: '#991b1b', padding: '2px 8px', 
                                borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' 
                            }}>
                                ALTERADO
                            </span>
                        </div>

                        {/* Corpo da Comparação */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                            {/* COLUNA: ATUAL (ANTIGO) */}
                            <div style={{ padding: '16px', borderRight: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
                                <div style={{ 
                                    textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', 
                                    color: '#64748b', marginBottom: '12px', textAlign: 'center' 
                                }}>
                                    Como está agora
                                </div>
                                
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{fontSize: '0.85rem', color:'#94a3b8', marginBottom:'4px'}}>Nota:</div>
                                    <RatingBadge rating={oldItem?.rating} />
                                </div>

                                <div>
                                    <div style={{fontSize: '0.85rem', color:'#94a3b8', marginBottom:'4px'}}>Fatos ({oldItem?.facts?.length || 0}):</div>
                                    {oldItem?.facts?.map((f, i) => <FactItemView key={i} fact={f} />)}
                                </div>
                            </div>

                            {/* COLUNA: PROPOSTA (NOVO) */}
                            <div style={{ padding: '16px', backgroundColor: '#f0fdf4' }}>
                                <div style={{ 
                                    textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', 
                                    color: '#166534', marginBottom: '12px', textAlign: 'center' 
                                }}>
                                    Nova Proposta
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{fontSize: '0.85rem', color:'#166534', marginBottom:'4px'}}>Nota:</div>
                                    <RatingBadge rating={newItem?.rating} />
                                </div>

                                <div>
                                    <div style={{fontSize: '0.85rem', color:'#166534', marginBottom:'4px'}}>Fatos ({newItem?.facts?.length || 0}):</div>
                                    {newItem?.facts?.map((f, i) => <FactItemView key={i} fact={f} />)}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
const PendingRequestsModal = ({
    isOpen,
    onClose,
    requests = [],
    nodes = [], // Precisamos dos nós para pegar o histórico antigo
    isLoading,
    token,
    handleFetchError,
    // Callback genérico do pai (mantido para compatibilidade, mas vamos usar lógica interna para eval)
    onApprove: onApproveGeneric, 
    onReject: onRejectGeneric, 
}) => {
    // Estados internos para o modal de comparação
    const [comparisonOpen, setComparisonOpen] = useState(false);
    const [diffData, setDiffData] = useState({ old: null, updated: null, type: '', requestId: null, justification: '' });
    const [isProcessing, setIsProcessing] = useState(false);

    if (!isOpen) return null;

    // --- LOGICA ESPECÍFICA DE APROVAÇÃO DE JD ---
    const handleApproveJd = async (id) => {
        if(!window.confirm("Confirmar aprovação de Job Description?")) return;
        setIsProcessing(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/organograma/approve-jd-change/${id}`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            await handleFetchError(res);
            alert("Job Description atualizado com sucesso!");
            onClose();
            window.location.reload(); // Refresh para garantir dados frescos
        } catch (e) { alert("Erro ao aprovar: " + e.message); }
        setIsProcessing(false);
    };

    // --- LOGICA ESPECÍFICA DE APROVAÇÃO DE AVALIAÇÃO ---
    const handleApproveEval = async (id) => {
        if(!window.confirm("Confirmar aprovação desta revisão de avaliação? O histórico será alterado.")) return;
        setIsProcessing(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/organograma/approve-evaluation-change/${id}`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            await handleFetchError(res);
            alert("Avaliação corrigida no histórico com sucesso!");
            onClose();
            window.location.reload();
        } catch (e) { alert("Erro ao aprovar avaliação: " + e.message); }
        setIsProcessing(false);
    };

    // --- LOGICA ESPECÍFICA DE REJEIÇÃO DE AVALIAÇÃO ---
    const handleRejectEval = async (id) => {
        if(!window.confirm("Tem certeza que deseja rejeitar esta solicitação?")) return;
        setIsProcessing(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/organograma/reject-evaluation-change/${id}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
            });
            await handleFetchError(res);
            alert("Solicitação rejeitada.");
            onClose(); // Ou apenas remover da lista local se preferir
            window.location.reload();
        } catch (e) { alert("Erro ao rejeitar: " + e.message); }
        setIsProcessing(false);
    };

    // --- PREPARAR DADOS PARA COMPARAÇÃO ---
    const handleOpenComparison = (req) => {
        const currentNode = nodes.find(n => n.id === req.node_id?.toString());
        
        if (req.request_type === 'jd_revision') {
            setDiffData({ 
                old: currentNode?.data, 
                updated: req.new_data, 
                type: 'JD', 
                requestId: req.id,
                justification: req.justification // JD geralmente tem justificativa no payload ou coluna
            });
        } else if (req.request_type === 'eval_revision') {
            // Para avaliação, precisamos pegar o item exato do histórico
            const historyIndex = req.history_index;
            const historyItem = currentNode?.data?.evaluationHistory?.[historyIndex];
            
            setDiffData({ 
                old: historyItem?.evaluations || {}, 
                updated: req.new_evaluation_data, 
                type: 'AVALIAÇÃO', 
                requestId: req.id,
                justification: req.justification // Vem da query SQL que criamos
            });
        }
        setComparisonOpen(true);
    };

    return (
        <>
            <ModalPortal>
                <div style={modalOverlayStyle}>
                    <div style={professionalModalContentStyle}>
                        <div style={{ ...modalHeaderStyle, background: '#050a30', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, color: 'white' }}>📥 Pendências de Gestão</h2>
                            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '20px' }}>✕</button>
                        </div>

                        <div style={modalBodyStyle}>
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                            ) : requests.length === 0 ? (
                                <Typography sx={{ textAlign: 'center', py: 4, color: '#64748b' }}>Nenhuma solicitação pendente.</Typography>
                            ) : (
                                requests.map(req => (
                                    <Paper key={req.id} elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Chip 
                                                label={
                                                    req.request_type === 'jd_revision' ? "ALTERAÇÃO DE JD" : 
                                                    req.request_type === 'eval_revision' ? "REVISÃO DE AVALIAÇÃO" : 
                                                    "NOVA VAGA"
                                                } 
                                                color={req.request_type === 'new_vacancy' ? 'success' : 'warning'} 
                                                size="small" 
                                                sx={{fontWeight: 'bold'}}
                                            />
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(req.created_at).toLocaleDateString('pt-BR')} às {new Date(req.created_at).toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}
                                            </Typography>
                                        </Box>
                                        
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                                            {req.request_type === 'jd_revision' ? req.new_data.cargo : req.node_cargo || req.cargo}
                                        </Typography>
                                        
                                        <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                                            Solicitado por: <strong>{req.requestername}</strong>
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {/* Botão Comparar (Apenas para revisões) */}
                                            {req.request_type !== 'new_vacancy' && (
                                                <Button 
                                                    startIcon={<CompareArrows />} 
                                                    variant="outlined" 
                                                    size="small" 
                                                    onClick={() => handleOpenComparison(req)}
                                                >
                                                    Analisar Detalhes
                                                </Button>
                                            )}
                                            
                                            <Box sx={{ flexGrow: 1 }} />
                                            
                                            {/* Botão Rejeitar */}
                                            <Button 
                                                color="error" 
                                                size="small" 
                                                disabled={isProcessing}
                                                onClick={() => {
                                                    if (req.request_type === 'eval_revision') handleRejectEval(req.id);
                                                    else onRejectGeneric(req.id); // Vagas e outros usam o genérico
                                                }}
                                            >
                                                Rejeitar
                                            </Button>

                                            {/* Botão Aprovar */}
                                            <Button 
                                                variant="contained" 
                                                color="success" 
                                                size="small" 
                                                disabled={isProcessing}
                                                onClick={() => {
                                                    if (req.request_type === 'jd_revision') handleApproveJd(req.id);
                                                    else if (req.request_type === 'eval_revision') handleApproveEval(req.id);
                                                    else onApproveGeneric(req.id); // Vagas usam o genérico
                                                }}
                                            >
                                                Aprovar
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </ModalPortal>

            {/* MODAL DE COMPARAÇÃO INTERNO */}
            <Dialog 
                open={comparisonOpen} 
                onClose={() => setComparisonOpen(false)} 
                maxWidth="lg" 
                fullWidth
                sx={{ zIndex: 9999 }} // Fix do zIndex
            >
                <DialogTitle sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    {diffData.type === 'AVALIAÇÃO' ? '📊 Comparativo de Avaliação' : '📄 Comparativo de Job Description'}
                </DialogTitle>
                
                <DialogContent sx={{ padding: '24px', backgroundColor: '#fff' }}>
                    
                    {diffData.type === 'JD' && (
                        // Aqui você pode manter sua lógica antiga de Diff de JSON ou criar um componente visual para JD também
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 1, maxHeight: '60vh', overflow: 'auto' }}>
                                <Typography color="error" variant="overline" display="block" gutterBottom>Versão Atual</Typography>
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(diffData.old, null, 2)}</pre>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 1, maxHeight: '60vh', overflow: 'auto' }}>
                                <Typography color="success" variant="overline" display="block" gutterBottom>Nova Proposta</Typography>
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(diffData.updated, null, 2)}</pre>
                            </Box>
                        </div>
                    )}

                    {diffData.type === 'AVALIAÇÃO' && (
                        <EvaluationDiffViewer 
                            oldData={diffData.old} 
                            newData={diffData.updated} 
                            justification={diffData.justification}
                        />
                    )}

                </DialogContent>
                
                <DialogActions sx={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                     <Button onClick={() => setComparisonOpen(false)} color="inherit">Fechar Visualização</Button>
                     <Button 
                        variant="contained" 
                        color="success"
                        onClick={() => {
                            setComparisonOpen(false);
                            if (diffData.type === 'AVALIAÇÃO') handleApproveEval(diffData.requestId);
                            if (diffData.type === 'JD') handleApproveJd(diffData.requestId);
                        }}
                     >
                        Aprovar Agora
                     </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};


const OrganogramFlow = () => {
    const NODE_WIDTH = 220;
    const NODE_HEIGHT = 160;

    // 1. Hooks de Base
    const { token, user } = useAuth();
    const currentUserId = user?.id;
    const navigate = useNavigate();
    const { fitView, project } = useReactFlow();
    const reactFlowWrapper = useRef(null);
    const initialFitViewDoneRef = useRef(false);
    const assigningCollaboratorRef = useRef(false);
    const creatingVacanciesRef = useRef(false);
    const [isCreatingVacancies, setIsCreatingVacancies] = useState(false);
    const organogramRequestRef = useRef(0);
    const nodeToLocateRef = useRef(null);

    // 2. Estados de Dados e Organograma
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [layoutDataNodes, setLayoutDataNodes] = useState([]);
    const [layoutDataEdges, setLayoutDataEdges] = useState([]);
    const [areas, setAreas] = useState([]); 
    const [categories, setCategories] = useState([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [dicionarioCompetencias, setDicionarioCompetencias] = useState({ corporativas: [], gerenciais: [] });

    const [viewMode, setViewMode] = useState('organogram');
    const [evaluation360Open, setEvaluation360Open] = useState(false);
    const [evaluation360PersonId, setEvaluation360PersonId] = useState(null);
    const [isOrganogramLoading, setIsOrganogramLoading] = useState(false);
    const [organogramError, setOrganogramError] = useState('');
    const [loadedOrganogramUnitId, setLoadedOrganogramUnitId] = useState('');

    // 3. Estados de UI, Filtros e Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
    const [isGerenteViewer, setIsGerenteViewer] = useState(false);
    const [jdEditMode, setJdEditMode] = useState('normal'); 
    const [allFilterableAreas, setAllFilterableAreas] = useState([]);
    const [selectedFilterAreas, setSelectedFilterAreas] = useState([]);
    const [showDescendants, setShowDescendants] = useState(false);
    const [layoutDirection, setLayoutDirection] = useState('DOWN');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isDraggableHovered, setIsDraggableHovered] = useState(null);
    const [focusedNodeId, setFocusedNodeId] = useState(null);
    const [initialFocusNodeId, setInitialFocusNodeId] = useState(null);
    
    // Estados do Prompt de Justificativa
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [promptConfig, setPromptConfig] = useState({ title: '', message: '', onConfirm: () => {} });

    // 4. Estados de Controle de Modais e Pendências
    const [selectedNode, setSelectedNode] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [sidebarNodes, setSidebarNodes] = useState([]);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isAreaModalOpen, setIsAreaModalForArea] = useState(false);
    const [isJobDescriptionModalOpen, setIsJobDescriptionModalOpen] = useState(false);
    const [isEvaluationSheetModalOpen, setIsEvaluationSheetModalOpen] = useState(false);
    const [isEvaluationResultModalOpen, setIsEvaluationResultModalOpen] = useState(false);
    const [isDisciplinaryHistoryModalOpen, setIsDisciplinaryHistoryModalOpen] = useState(false);
    const [isPDIModalOpen, setIsPDIModalOpen] = useState(false);
    const [isAddVacancyModalOpen, setIsAddVacancyModalOpen] = useState(false);
    const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
    const [isManualsModalOpen, setIsManualsModalOpen] = useState(false);
    const [isExplanatoryVideoModalOpen, setIsExplanatoryVideoModalOpen] = useState(false); 
    const [isManagerEvaluationsModalOpen, setIsManagerEvaluationsModalOpen] = useState(false);
    
    // Novo Estado para o Menu de Ajuda
    const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, message: '', onConfirm: () => {}, onCancel: () => {} });

    // 5. ✨ ESTADOS DE AVALIAÇÃO ELEVADOS (Para o Modo de Revisão funcionar)
    const [step, setStep] = useState('selection'); 
    const [evaluations, setEvaluations] = useState({});
    const [period, setPeriod] = useState('Semestral');
    const [revisionContext, setRevisionContext] = useState(null); // { index, originalData }

    // 6. Estados de Carregamento, Erro e Suporte
    const [company, setCompany] = useState(null);
    const [companyLoading, setCompanyLoading] = useState(true);
    const [companyError, setCompanyError] = useState(null);
    const [units, setUnits] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(true);
    const [unitsError, setUnitsError] = useState(null);
    const [isLoadingAreas, setIsLoadingAreas] = useState(true);
    const [isLoadingCategories, setIsLoadingCategories] = useState(true);
    const [isLoadingPending, setIsLoadingPending] = useState(false);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [shouldOpenEvaluation, setShouldOpenEvaluation] = useState(false);
  
    // 7. Variáveis de URL
    const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const urlUnitId = urlParams.get('unitId');

    // 8. Definições de Permissões
    const canEdit = useMemo(() => user && ['superadmin', 'admin', 'editor'].includes(user.role), [user]);
    const isAdminOrEditor = canEdit;
    const isViewer = user && user.role === 'viewer';
    const canEvaluate = useMemo(() => canEdit || isGerenteViewer, [canEdit, isGerenteViewer]);
    const [availableCompetencies, setAvailableCompetencies] = useState([]);
    const [selectedCompetencies, setSelectedCompetencies] = useState([]);
    useEffect(() => {
    if (selectedNode) {
        const tech = (selectedNode.data?.competenciasTecnicas || []).map(c => ({ ...c, tipo: 'Técnica' }));
        const other = (selectedNode.data?.outrasCompetencias || []).map(c => ({ ...c, tipo: 'Outra' }));
        const corp = (dicionarioCompetencias?.corporativas || []).map(c => ({ ...c, tipo: 'Corporativa' }));
        const mang = selectedNode.data?.isGerencial ? (dicionarioCompetencias?.gerenciais || []).map(c => ({ ...c, tipo: 'Gerencial' })) : [];
        
        setAvailableCompetencies([...tech, ...other, ...corp, ...mang]);
    }
}, [selectedNode, dicionarioCompetencias]);
    const handleFetchError = useCallback(async (response) => {
            if (!response.ok) {
                if (response.status === 401) {
                    throw Object.assign(new Error('Token inválido ou malformado. Por favor, faça login novamente.'), { status: 401 });
                }
                const errorBody = await response.text();
                let errorMessage = `Erro HTTP: ${response.status} ${response.statusText}`;
                try {
                    const errorJson = JSON.parse(errorBody);
                    errorMessage = errorJson.message || JSON.stringify(errorJson);
                } catch (e) {
                    if (errorBody) {
                        errorMessage = errorBody;
                    }
                }
                throw Object.assign(new Error(errorMessage), { status: response.status });
            }
            if (response.status === 204 || response.headers.get("content-length") === "0") {
                return null;
            }
            return response.json();
        }, []);

// --- ESTADOS E LOGICA DE REVISÃO (COLE AQUI) ---

const handleStartEvalRevision = useCallback((entry, index) => {
    if (!entry || !entry.evaluations) return;

    // 1. Pegamos os nomes do que foi avaliado no passado
    const namesInHistory = Object.keys(entry.evaluations);
    
    // 2. Criamos os objetos de competência para o formulário desenhar os campos
    // Buscamos no dicionário para trazer descrição e tipo
    const compsToRender = namesInHistory.map(name => {
        const found = availableCompetencies.find(c => c.nome === name);
        return found || { 
            nome: name, 
            tipo: 'Técnica', 
            descricao: 'Competência do histórico' 
        };
    });

    // 3. ATUALIZAÇÃO DOS ESTADOS GLOBAIS (O que o modal vai ler)
    setSelectedCompetencies(compsToRender); // ✨ Isso faz as competências APARECEREM
    setEvaluations(JSON.parse(JSON.stringify(entry.evaluations))); // ✨ Isso traz NOTAS e FATOS
    setPeriod(entry.period || 'Semestral');
    setRevisionContext({ index: index }); // ✨ Ativa o modo edição para o viewer
    
    setStep('evaluation'); // Pula direto para a aba de notas
    setIsEvaluationSheetModalOpen(true);
}, [availableCompetencies, setSelectedCompetencies, setEvaluations, setRevisionContext, setStep, setIsEvaluationSheetModalOpen]);
// 2. Função consolidada para enviar o pedido ao RH (Substitui as duplicadas)
const handleRequestEvalRevision = useCallback(async (finalEvaluations, index, justificativa) => {
    if (!selectedNode || !token) return;

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/request-evaluation-change`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${token}` 
            },
            body: JSON.stringify({
                nodeId: selectedNode.id,
                historyIndex: index,
                newEvaluationData: finalEvaluations,
                expectedHistoryEntry: selectedNode.data.evaluationHistory?.[index],
                expectedUserId: getRegisteredUserId(selectedNode.data),
                nominalOccupantId: selectedNode.data.nominalOccupantId,
                justification: justificativa,
                unitId: selectedUnitId
            })
        });

        await handleFetchError(response);
        alert("Solicitação de alteração enviada ao RH com sucesso!");
        
        setRevisionContext(null);
        setIsPromptOpen(false);
        setIsEvaluationSheetModalOpen(false);
    } catch (error) {
        alert(`Erro ao enviar solicitação: ${error.message}`);
    }
}, [selectedNode, token, selectedUnitId, handleFetchError]);

    // 10. Handlers de Interface
    const handleCloseJobDescription = useCallback(() => {
        setIsJobDescriptionModalOpen(false);
        setJdEditMode('normal');
    }, []);

    const handleOpenResults = useCallback(() => {
        setIsEvaluationSheetModalOpen(false);
        setTimeout(() => setIsEvaluationResultModalOpen(true), 300);
    }, []);

    // --- EFEITOS DE SISTEMA ---
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (user && user.role === 'viewer' && user.cargo) {
            const cargoLower = user.cargo.toLowerCase();
            if (cargoLower.includes('vaga gerencial') || cargoLower.includes('gerente') || cargoLower.includes('líder')) {
                setIsGerenteViewer(true);
            }
        }
    }, [user]);

    // 11. Funções de Árvore
    const findAllDescendants = useCallback((startNodeId, allEdges) => {
        const descendants = new Set();
        const queue = [startNodeId];
        const visited = new Set([startNodeId]);
        while (queue.length > 0) {
            const currentId = queue.shift();
            const childrenEdges = allEdges.filter(edge => edge.source === currentId);
            childrenEdges.forEach(edge => {
                if (!visited.has(edge.target)) {
                    visited.add(edge.target);
                    descendants.add(edge.target);
                    queue.push(edge.target);
                }
            });
        }
        return descendants;
    }, []);
        const dicionarioCompetenciasMemo = useMemo(() => dicionarioCompetencias, [dicionarioCompetencias]);
        // ESTILOS RESPONSIVOS MOVIDOS PARA DENTRO DO COMPONENTE
    // ===== ESTILOS MELHORADOS PARA O HEADER (VERSÃO ALINHADA) =====
    // ===== ESTILOS OTIMIZADOS (TUDO NA MESMA LINHA) =====
    const headerStyle = {
        padding: isMobile ? '8px 16px' : '8px 16px',  // ✅ Reduzido de 14-16px para 8px
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderBottom: '2px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        flexDirection: isMobile ? 'row' : 'row',  // ✅ Sempre row
        flexShrink: 0,
        gap: isMobile ? '8px' : '8px',  // ✅ Reduzido de 16px/10px para 8px
        minHeight: isMobile ? 'auto' : '44px',  // ✅ Reduzido de 68px para 44px
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        flexWrap: 'wrap',
        overflowX: 'visible',
        position: 'relative',
        zIndex: 1200
    };


    const headerTitleStyle = {
        margin: 0,
        fontSize: isMobile ? '0.95rem' : (isTablet ? '0.85rem' : '0.95rem'),  // ✅ Reduzido
        fontWeight: '700',
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        whiteSpace: 'nowrap',
        flexShrink: 1,
        width: isMobile ? '100%' : 'auto',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };


    const toggleButtonStyle = {
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        border: 'none',
        borderRadius: '8px',
        width: isTablet ? '36px' : '40px',
        height: isTablet ? '36px' : '40px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
        transition: 'all 0.3s ease',
        flexShrink: 0,
        color: 'white'
    };

    const controlsContainerStyle = {
        display: 'flex',
        gap: isMobile ? '12px' : (isTablet ? '6px' : '10px'),
        marginLeft: isMobile ? 0 : 'auto',
        width: isMobile ? '100%' : 'auto',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        flexWrap: 'wrap',
        flexShrink: 1
    };

    const controlsGroupStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '12px' : (isTablet ? '6px' : '8px'),
        flexWrap: 'wrap',
        width: isMobile ? '100%' : 'auto',
        minWidth: 0,
        flexShrink: 1
    };

    const headerSeparatorStyle = {
        width: isMobile ? '100%' : '1px',
        height: isMobile ? '2px' : (isTablet ? '30px' : '34px'),
        background: isMobile 
            ? 'linear-gradient(to right, transparent, #cbd5e1, transparent)' 
            : 'linear-gradient(to bottom, transparent, #cbd5e1, transparent)',
        flexShrink: 0,
        display: isMobile ? 'none' : 'block'
    };





    // ===== ÍCONES PERSONALIZADOS =====
    const OrganogramIcon = () => (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <path d="M10 7h4M17 10v4M7 10v4M10 17h4"/>
        </svg>
    );

    const StyledButton = ({ onClick, children, variant = 'primary', icon, disabled, title }) => {
        const [hover, setHover] = useState(false);
        
        // ✅ Adicione este hook para detectar tamanho
        const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
        
        useEffect(() => {
            const handleResize = () => {
                setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
            };
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        const variants = {
            primary: {
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: 'white',
                hoverBg: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                shadow: '0 4px 12px rgba(22, 163, 74, 0.3)'
            },
            secondary: {
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                hoverBg: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                shadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            },
            ghost: {
                background: 'white',
                color: '#334155',
                hoverBg: '#f8fafc',
                shadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                border: '2px solid #e2e8f0'
            },
            warning: {
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                hoverBg: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                shadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
            }
        };

        const style = variants[variant];

        return (
            <button
                onClick={onClick}
                disabled={disabled}
                title={title}
                style={{
                    padding: icon && !children ? (isTablet ? '9px' : '12px') : (isTablet ? '9px 14px' : '12px 20px'),
                    fontSize: isTablet ? '0.8rem' : '0.95rem',
                    fontWeight: '600',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    border: style.border || 'none',
                    borderRadius: '8px',
                    background: hover && !disabled ? style.hoverBg : style.background,
                    color: disabled ? '#94a3af' : style.color,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',  // ✅ CERTIFIQUE-SE QUE É 'center'
                    gap: '2px',                // ✅ REDUZIDO PARA 2px
                    width: 'auto',              // ✅ MUDE DE '100%' PARA 'auto'
                    boxShadow: hover && !disabled ? style.shadow : '0 2px 4px rgba(0, 0, 0, 0.06)',
                    transform: hover && !disabled ? 'translateY(-2px)' : 'translateY(0)',
                    opacity: disabled ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                    minWidth: icon && !children ? (isTablet ? '38px' : '44px') : 'auto'
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {icon && <span style={{display: 'flex', alignItems: 'center', marginRight: '3px'}}>{icon}</span>}
                {children}
            </button>
        );
    };


    // --- ÍCONE ANIMADO ---
    const AnimatedExclamationIcon = () => (
        <div style={{
            position: 'absolute', top: '-10px', right: '-10px', width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 'bold', fontSize: '16px', animation: 'pulse-glow-vacant 2s infinite ease-in-out', zIndex: 10,
        }}>!</div>
    );
        const handleLayoutToggle = () => {
            const newDirection = layoutDirection === 'DOWN' ? 'RIGHT' : 'DOWN';
            setLayoutDirection(newDirection);
            getLayoutedElements(layoutDataNodes, layoutDataEdges, newDirection).then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
                setNodes(layoutedNodes);
                setEdges(layoutedEdges);
                setLayoutDataNodes(layoutedNodes);
                setLayoutDataEdges(layoutedEdges);
                setTimeout(() => {
                    fitView({ duration: 400 });
                }, 100);
            });
        };

        const showConfirmation = useCallback((message, onConfirm) => {
            setConfirmationModal({
                isOpen: true,
                message,
                onConfirm: () => {
                    onConfirm();
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                },
                onCancel: () => {
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                },
            });
        }, []);

        

        useEffect(() => {
            const loadScript = (src) => {
                return new Promise((resolve, reject) => {
                    if (document.querySelector(`script[src="${src}"]`)) {
                        resolve();
                        return;
                    }
                    const script = document.createElement('script');
                    script.src = src;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error(`Script load error for ${src}`));
                    document.body.appendChild(script);
                });
            };

            Promise.all([
                loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"),
                loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js")
            ]).then(() => {
                setScriptsLoaded(true);
            }).catch(error => {
                console.error("Failed to load PDF scripts:", error);
            });
        }, []);

        useEffect(() => {
            if (!token) {
                setCompanyLoading(false);
                setUnitsLoading(false);
                setIsLoadingAreas(false);     // 👈 ADICIONADO
                setIsLoadingCategories(false); // 👈 ADICIONADO
                return;
            }

            const fetchInitialData = async () => {
                const authHeader = { 'Authorization': `Bearer ${token}` };
                try {
                    setCompanyLoading(true);
                    setUnitsLoading(true);
                    setIsLoadingAreas(true); // 👈 ADICIONADO
                    setIsLoadingCategories(true); // 👈 ADICIONADO
                const [companyData, unitsData, areasData, competenciesData, categoriesData] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/company`, { headers: authHeader }).then(handleFetchError),
                    fetch(`${process.env.REACT_APP_API_URL}/units/stats`, { headers: authHeader }).then(handleFetchError),
                    fetch(`${process.env.REACT_APP_API_URL}/areas`, { headers: authHeader }).then(handleFetchError),
                    fetch(`${process.env.REACT_APP_API_URL}/company/competencies`, { headers: authHeader }).then(handleFetchError),
                    // 👇 ADICIONE ESTE NOVO FETCH
                    fetch(`${process.env.REACT_APP_API_URL}/categories`, { headers: authHeader }).then(handleFetchError)
                ]);

                    setCompany(companyData);
                    setUnits(unitsData || []);
                    setAreas(areasData || []);
                    setDicionarioCompetencias(competenciesData || { corporativas: [], gerenciais: [] });
                    setCategories(categoriesData || []);

                    if (urlUnitId && unitsData && unitsData.some(u => u.id.toString() === urlUnitId)) {
                        setSelectedUnitId(urlUnitId);
                    } else if (!selectedUnitId && unitsData && unitsData.length > 0) {
                        setSelectedUnitId(unitsData[0].id.toString());
                    }
                } catch (error) {
                    console.error("Erro ao carregar dados iniciais:", error);
                    setCompanyError(error.message);
                    setUnitsError(error.message);
                } finally {
                    setCompanyLoading(false);
                    setUnitsLoading(false);
                    setIsLoadingAreas(false); // 👈 MODIFICADO de 'true' para 'false'
                    setIsLoadingCategories(false); // 👈 ADICIONADO
                }
            };

            fetchInitialData();
        }, [token, urlUnitId, handleFetchError, selectedUnitId]);
    const generateNodeLabel = useCallback((nodeData) => {
        // --- Lógica de Status ---
        const isVacant = !hasAssignedCollaborator(nodeData);
        const isPending = nodeData.status === 'pending';

    if (isPending) {
        // Mostra "Vaga Pendente" em laranja
        // Com ícone laranja pulsante
    }

        // --- Textos e Cores ---
        let primaryText;
        let primaryColor;

        if (!isVacant) {
            // Se TEM usuário
            primaryText = getCustomCollaboratorName(nodeData) || nodeData.nome || nodeData.name;
            primaryColor = '#1e293b';
        } else if (isPending) {
            // Se NÃO tem usuário E é pendente
            primaryText = 'Vaga Pendente';
            primaryColor = '#d97706';
        } else {
            // Se NÃO tem usuário E NÃO é pendente (Vaga Aberta)
            primaryText = 'Vaga Aberta';
            primaryColor = '#b45309';
        }

        const secondaryText = nodeData.cargo || 'Defina o Cargo/Função';
        const areaText = nodeData.areaName || 'Definir Área';
        const creationDate = formatDateTime(nodeData.created_at);
        const profilePictureUrl = nodeData.profile_picture_url;

        // --- Estilos ---
        const infoContainerStyle = {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: isVacant ? 'center' : 'left',
            overflow: 'hidden'
        };

        const singleLineTruncatedStyle = {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        };

        const primaryLineStyle = {
            ...singleLineTruncatedStyle,
            fontWeight: 'bold',
            color: primaryColor,
            fontSize: '1em'
        };

        const jobTitleStyle = {
            fontSize: '0.9em',
            color: '#475569',
            whiteSpace: 'normal',
            wordBreak: 'break-word'
        };

        const areaLineStyle = {
            ...singleLineTruncatedStyle,
            fontSize: '0.8em',
            color: '#383838ff',
            fontWeight: 'bold',
            fontStyle: !nodeData.areaName ? 'italic' : 'normal'
        };

        // Ícone Pendente com animação
        const PendingIcon = () => (
            <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#d97706" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#fef3c7',
                    padding: '2px',
                    animation: 'pulse-glow-pending 2s infinite ease-in-out',
                    zIndex: 10
                }}
            >
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
        );

        // --- ESTRUTURA JSX FINAL (LAYOUT CORRETO) ---
        return (
            <div style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
                padding: '5px'
            }}>
                {/* Ícones de status */}
                {isPending ? <PendingIcon /> : (isVacant ? <AnimatedExclamationIcon /> : null)}

                {/* Seção principal: Avatar + Nome + Cargo */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexGrow: 1,
                    marginBottom: '4px',
                    padding: '0 8px',
                    gap: '10px'
                }}>
                    {/* Avatar (só aparece se não for vaga) */}
                    {!isVacant && (
                        <CustomAvatar src={profilePictureUrl} name={primaryText} />
                    )}
                    {/* Info container */}
                    <div style={infoContainerStyle}>
                        {/* Nome/Status */}
                        <div style={primaryLineStyle} title={primaryText}>
                            {primaryText}
                        </div>
                        {/* Cargo */}
                        <div style={jobTitleStyle} title={secondaryText}>
                            {secondaryText}
                        </div>
                    </div>
                </div>

                {/* Área (com bordas) */}
                <div style={{
                    ...areaLineStyle,
                    borderTop: '1px solid #f1f5f9',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 8px',
                    textAlign: 'center',
                    minHeight: '26px'
                }} title={areaText}>
                    {areaText}
                </div>

                {/* Data de criação + Menu (3 pontos) */}
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    marginTop: 'auto',
                    paddingTop: '4px',
                    minHeight: '24px'
                }}>
                    {creationDate && (
                        <span style={{
                            fontSize: '0.7em',
                            color: '#94a3b8',
                            fontStyle: 'italic'
                        }}>
                            Criado em: {creationDate}
                        </span>
                    )}
                    
                    {/* Ícone de 3 pontos (menu de opções) */}
                    <div
                        data-action="open-modal"
                        style={{
                            position: 'absolute',
                            right: '0px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        title="Abrir opções"
                    >
                        <MoreOptionsIcon />
                    </div>
                </div>
            </div>
        );
    }, []);


    const updateNodeAndLayoutData = useCallback((nodeId, updatedData) => {
            const updateFunction = (currentNodes) => currentNodes.map(n => {
      	       	if (n.id === nodeId.toString()) {
                    const patch = { ...(typeof updatedData === 'function' ? updatedData(n.data) : updatedData) };
                    if (patch.userId === undefined && patch.user_id !== undefined) patch.userId = patch.user_id;
                    const newNodeData = normalizeCollaboratorData({ ...n.data, ...patch });
                    
                    // --- Lógica de Estilo Atualizada ---
                    let backgroundColor = newNodeData.color || '#ffffff';
                    let borderColor = '#676767ff'; // Cor padrão (ocupado)
                    
                    if (newNodeData.status === 'pending') {
                        backgroundColor = '#fffbeb'; // Amarelo claro
                        borderColor = '#f59e0b'; // Laranja
                    } else if (!hasAssignedCollaborator(newNodeData)) { // status 'vacant' // status 'vacant'
                        backgroundColor = '#fefce8'; // Seu Amarelo original de Vaga Aberta
                        borderColor = '#facc15'; // Sua Borda original de Vaga Aberta
                    }
                    // --- Fim da Lógica ---

                    return {
          	         	...n,
          	 	       	data: { ...newNodeData, label: generateNodeLabel(newNodeData) },
          	 	       	style: { // Aplicando os estilos
                            ...n.style, // Preserva qualquer estilo anterior (como width/height)
          	 	 	       	backgroundColor: backgroundColor,
                            borderColor: borderColor, 
                            // Mantém os outros estilos originais
                            borderWidth: '1.5px', 
                            borderRadius: '8px', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
        	   	     	},
      	   	     };
      	 	   	}
                return n;
            });
            setLayoutDataNodes(updateFunction);
            setNodes(updateFunction);
            setSelectedNode(current => current ? updateFunction([current])[0] : current);
        }, [generateNodeLabel]); // O array de dependência SÓ precisa de generateNodeLabel

        const handleAreaFilterChange = (newSelectedOptions) => {
            setSelectedFilterAreas(newSelectedOptions);
            setShowDescendants(false);
        };

const fetchOrganogramData = useCallback((unitId) => {
        const requestId = ++organogramRequestRef.current;
        initialFitViewDoneRef.current = false;
        if (!unitId || !token) {
            setIsOrganogramLoading(false);
            setOrganogramError('');
            setLoadedOrganogramUnitId('');
            setNodes([]);
            setEdges([]);
            setLayoutDataNodes([]);
            setLayoutDataEdges([]);
            setAllFilterableAreas([]);
            setSelectedFilterAreas([]);
            return;
        }
        const apiUrl = getOrganogramApiUrl(`/organograma/unit/${encodeURIComponent(unitId)}`);
        setIsOrganogramLoading(true);
        setOrganogramError('');
        return fetch(apiUrl, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(handleFetchError)
            .then(responseData => {
                if (requestId !== organogramRequestRef.current) return;
                const data = responseData == null ? [] : responseData;
                if (!Array.isArray(data)) throw new Error('Resposta inválida ao carregar o organograma.');
                if (data.length === 0) {
                    setIsGerenteViewer(false);
                    setAllFilterableAreas([]);
                    setSelectedFilterAreas([]);
                    setShowDescendants(false);
                    setSelectedNode(null);
                    setFocusedNodeId(null);
                    setInitialFocusNodeId(null);
                    setShouldOpenEvaluation(false);
                    setNodes([]);
                    setEdges([]);
                    setLayoutDataNodes([]);
                    setLayoutDataEdges([]);
                    return;
                };

                let processedData = data;
                let viewerFocusNodeId = null;

                setIsGerenteViewer(false);

                // Lógica de Visualizador (Mantida igual)
                if (isViewer) {
                    if (!currentUserId) {
                        console.error('FILTRO [VISUALIZADOR]: ERRO CRÍTICO! User ID não encontrado.');
                        processedData = [];
                    } else {
                        const viewerUserIdString = currentUserId.toString();
                        const viewerApiNode = data.find(item => item.user_id && item.user_id.toString() === viewerUserIdString);

                        if (!viewerApiNode) {
                            processedData = [];
                        } else {
                            if (viewerApiNode.isGerencial === true) {
                                setIsGerenteViewer(true);
                            }
                            const viewerNodeId = viewerApiNode.id.toString();
                            viewerFocusNodeId = viewerNodeId;

                            const allEdgesForTracing = data
                                .filter(item => item.parent_id !== null)
                                .map(item => ({
                                    id: `e${item.parent_id}-${item.id}`,
                                    source: item.parent_id.toString(),
                                    target: item.id.toString(),
                                }));

                            const descendantIds = findAllDescendants(viewerNodeId, allEdgesForTracing);
                            const allowedNodeIds = new Set(descendantIds);
                            allowedNodeIds.add(viewerNodeId);

                            processedData = data.filter(item => allowedNodeIds.has(item.id.toString()));
                        }
                    }
                }

              const initialNodes = processedData.map(item => {
    const normalizedUserId = getRegisteredUserId(item);

const nodeData = normalizeCollaboratorData({
        name: item.nome_customizado || item.nome || 'Vaga Aberta',
        role: item.cargo,
        userId: normalizedUserId,
        nome_customizado: item.nome_customizado,
        nominalOccupantId: item.nominalOccupantId,

        area: item.areaName,
        nome: item.nome,
        cargo: item.cargo,
        funcao: item.funcao,
        areaName: item.areaName,
        color: item.color,
        unit_id: item.unit_id,
        created_at: item.created_at,
        
        // ✨ CORREÇÃO AQUI: Fallbacks para ler tanto camelCase quanto snake_case
        descricaoSumaria: item.descricaoSumaria || item.descricao_sumaria,
        atividades: item.atividades,
        competenciasTecnicasObrigatorias: item.competenciasTecnicasObrigatorias || item.competencias_tecnicas_obrigatorias,
        competenciasTecnicasDesejaveis: item.competenciasTecnicasDesejaveis || item.competencias_tecnicas_desejaveis,
        competenciasTecnicas: item.competenciasTecnicas || item.competencias_tecnicas || [],
        outrasCompetencias: item.outrasCompetencias || item.outras_competencias || [],
        isGerencial: item.isGerencial !== undefined ? item.isGerencial : item.is_gerencial,
        // ✨ FIM DA CORREÇÃO
        
        profile_picture_url: item.profile_picture_url || item.avatarUrl,
        status: item.status,
        requested_by_user_id: item.requested_by_user_id,
        disciplinaryHistory: item.disciplinaryHistory || { description: '', facts: [] },
        evaluations: item.evaluations || {},
        lastEvaluationDate: item.lastEvaluationDate ?? item.last_evaluation_date,
        nextEvaluationDate: item.nextEvaluationDate ?? item.next_evaluation_date,
        evaluationPeriod: item.evaluationPeriod ?? item.evaluation_period,
        evaluationHistory: item.evaluationHistory ?? item.evaluation_history ?? []
    });

    let backgroundColor = item.color || '#ffffff';
    let borderColor = '#676767ff';
    
    if (item.status === 'pending') {
        backgroundColor = '#fffbeb';
        borderColor = '#f59e0b';
    } else if (!hasAssignedCollaborator(nodeData)) {
        // Vaga realmente vazia, sem usuário e sem nome nominal.
        backgroundColor = '#fefce8';
        borderColor = '#facc15';
    }

    return {
        id: item.id.toString(),
        type: 'default',
        data: { ...nodeData, label: generateNodeLabel(nodeData) },
        position: { x: item.position_x || 0, y: item.position_y || 0 },
        style: {
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            borderWidth: '1.5px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            width: NODE_WIDTH,
            height: NODE_HEIGHT
        },
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
    };
});

                const processedNodeIds = new Set(processedData.map(n => n.id.toString()));
                const initialEdges = processedData
                    .filter(item => item.parent_id !== null && processedNodeIds.has(item.parent_id.toString()))
                    .map(item => ({
                        id: `e${item.parent_id}-${item.id}`,
                        source: item.parent_id.toString(),
                        target: item.id.toString(),
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
                        style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 5' }
                    }));

// Dentro de fetchOrganogramData -> getLayoutedElements(...).then(...)

const layoutPromise = getLayoutedElements(initialNodes, initialEdges).then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
    if (requestId !== organogramRequestRef.current) return;
    setLayoutDataEdges(layoutedEdges);
    setEdges(layoutedEdges);

    // --- LÓGICA DE URL E FOCO AUTOMÁTICO ---
    const urlParams = new URLSearchParams(window.location.search);
    const nodeIdFromUrl = urlParams.get('nodeId');
    const shouldOpenEval = urlParams.get('openEvaluation') === 'true'; // <--- CAPTURA O PARÂMETRO

    let finalNodes = layoutedNodes;

    // 1. Prioridade: Focar no nó solicitado via URL (ex: vindo da Visão Geral)
    if (nodeIdFromUrl && layoutedNodes.some(n => n.id === nodeIdFromUrl)) {
        console.log("Prioridade: Focando no nó solicitado via URL:", nodeIdFromUrl);
        setInitialFocusNodeId(nodeIdFromUrl);
        
        // Se a URL pedir para abrir avaliação, ativa o estado gatilho
        if (shouldOpenEval) {
            setShouldOpenEvaluation(true); 
        }
    } 
    // 2. Se não houver ID na URL, mas for Visualizador, foca no nó dele
    else if (viewerFocusNodeId) {
        console.log("Focando no nó do próprio usuário (Visualizador):", viewerFocusNodeId);
        setInitialFocusNodeId(viewerFocusNodeId);
    }

    setLayoutDataNodes(finalNodes);
    setNodes(finalNodes);

    // Salva posições se não for visualizador
    if (!isViewer) {
        layoutedNodes.forEach(node => {
            fetch(`${process.env.REACT_APP_API_URL}/organograma/position/${node.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ positionX: node.position.x, positionY: node.position.y })
            }).catch(err => console.error("Falha ao salvar a posição:", err));
        });
    }
});

                const areaCounts = data.reduce((acc, node) => { if (node.areaName) { acc[node.areaName] = (acc[node.areaName] || 0) + 1; } return acc; }, {});
                const uniqueAreaNames = [...new Set(data.map(node => node.areaName))].filter(Boolean);
                const areasWithCounts = uniqueAreaNames.map(name => ({ name: name, count: areaCounts[name] || 0 }));
                setAllFilterableAreas(areasWithCounts);
                setSelectedFilterAreas([]);
                return layoutPromise;

            }).catch(error => {
                if (requestId !== organogramRequestRef.current) return;
                console.error(`Erro ao carregar organograma: ${error.message}.`);
                setOrganogramError(error.status === 404
                    ? 'A rota de consulta da equipe não foi encontrada (HTTP 404). Confira a configuração da API.'
                    : error.status === 401 ? 'Sua sessão expirou. Entre novamente para carregar a equipe.'
                    : error.status === 403 ? 'Você não tem permissão para consultar esta unidade.'
                    : 'Não foi possível carregar a equipe. Tente novamente.');
                setNodes([]);
                setEdges([]);
                setLayoutDataNodes([]);
                setLayoutDataEdges([]);
            }).finally(() => {
                if (requestId === organogramRequestRef.current) {
                    setIsOrganogramLoading(false);
                    setLoadedOrganogramUnitId(String(unitId));
                }
            });
    }, [token, generateNodeLabel, NODE_WIDTH, NODE_HEIGHT, handleFetchError, isViewer, currentUserId, findAllDescendants]);
// Localize este useEffect dentro de OrganogramFlow e substitua-o

useEffect(() => {
    if (nodes.length > 0 && fitView) {
        const fitViewWithOptions = (options) => {
            requestAnimationFrame(() => fitView(options));
        };

        if (initialFocusNodeId) {
            const focusTimeout = setTimeout(() => {
                // 1. Dá o zoom no colaborador
                fitViewWithOptions({
                    nodes: [{ id: initialFocusNodeId }],
                    duration: 800,
                    padding: 0.2
                });
                setFocusedNodeId(initialFocusNodeId);

                // 2. Lógica para abrir avaliação automaticamente se solicitado
                if (shouldOpenEvaluation) {
                    const nodeToOpen = nodes.find(n => n.id === initialFocusNodeId);
                    
                    if (nodeToOpen) {
                        // Verifica se tem competências mínimas para abrir
                        const techCount = (nodeToOpen.data?.competenciasTecnicas || []).length;
                        
                        if (techCount >= 3) {
                            setSelectedNode(nodeToOpen); // Define o nó selecionado para o modal
                            setIsEvaluationSheetModalOpen(true); // Abre o modal
                            
                            // Define se vai para seleção, avaliação ou histórico
                            // (O próprio modal EvaluationSheetModal já tem um useEffect que faz isso ao abrir,
                            //  mas podemos forçar aqui se necessário. Geralmente só abrir o modal basta).
                        } else {
                            alert("Este colaborador não possui as 3 competências técnicas mínimas para ser avaliado.");
                        }
                        
                        setShouldOpenEvaluation(false); // Reset para não reabrir em loops
                    }
                }

                setInitialFocusNodeId(null);
                
                // 3. Limpa a URL para que um refresh não reabra o modal acidentalmente
                const nextURL = new URL(window.location);
                nextURL.searchParams.delete('nodeId');
                nextURL.searchParams.delete('openEvaluation');
                window.history.replaceState({}, '', nextURL);
                
                initialFitViewDoneRef.current = true;
            }, 500); // Um pequeno delay garante que o ReactFlow renderizou tudo

            return () => clearTimeout(focusTimeout);
        } else if (!initialFitViewDoneRef.current) {
            const generalTimeout = setTimeout(() => {
                fitViewWithOptions({ duration: 600, padding: 0.1 });
                initialFitViewDoneRef.current = true;
            }, 200);
            return () => clearTimeout(generalTimeout);
        }
    }
}, [nodes, initialFocusNodeId, fitView, shouldOpenEvaluation]); // Adicione shouldOpenEvaluation nas dependências

        useEffect(() => {
            fetchOrganogramData(selectedUnitId);
            return () => { organogramRequestRef.current += 1; };
        }, [selectedUnitId, fetchOrganogramData]);

    useEffect(() => {
        if (layoutDataNodes.length === 0) {
            return;
        }

        if (selectedFilterAreas.length === 0) {
            const styledNodes = layoutDataNodes.map(node => {
                const isFocused = node.id === focusedNodeId;
                const isVacant = !hasAssignedCollaborator(node.data);
                const baseBorderColor = isVacant ? '#facc15' : '#676767ff';
                return {
                    ...node,
                    style: {
                        ...node.style,
                        borderColor: isFocused ? '#FF834F' : baseBorderColor,
                        borderWidth: isFocused ? '3px' : '1.5px',
                        animation: isFocused ? 'pulse-glow-focus 1.5s infinite ease-in-out' : 'none',
                    },
                };
            });
            setNodes(styledNodes);
            setEdges(layoutDataEdges);
            return;
        }

        const nodesToKeep = new Set();
        // ✅ A função findAllDescendants agora é chamada do escopo do componente

              const filteredRootNodes = layoutDataNodes.filter(n => selectedFilterAreas.includes(n.data.areaName));
        filteredRootNodes.forEach(n => nodesToKeep.add(n.id));

        if (showDescendants) {
            filteredRootNodes.forEach(rootNode => {
                const descendants = findAllDescendants(rootNode.id, layoutDataEdges);
                descendants.forEach(descId => nodesToKeep.add(descId));
            });
        }

        const finalNodesRaw = layoutDataNodes.filter(n => nodesToKeep.has(n.id));
        const finalEdges = layoutDataEdges.filter(e => nodesToKeep.has(e.source) && nodesToKeep.has(e.target));

        getLayoutedElements(finalNodesRaw, finalEdges).then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
            const nodesWithFocus = layoutedNodes.map(node => {
                const isFocused = node.id === focusedNodeId;
                const isVacant = !hasAssignedCollaborator(node.data);
                const baseBorderColor = isVacant ? '#facc15' : '#676767ff';
                return {
                    ...node,
                    style: {
                        ...node.style,
                        borderColor: isFocused ? '#FF834F' : baseBorderColor,
                        borderWidth: isFocused ? '3px' : '1.5px',
                        animation: isFocused ? 'pulse-glow-focus 1.5s infinite ease-in-out' : 'none',
                    },
                };
            });

            setNodes(nodesWithFocus);
            setEdges(layoutedEdges);

            setTimeout(() => {
                fitView({ duration: 800, padding: 0.2 });
            }, 100);
        });
    }, [selectedFilterAreas, showDescendants, layoutDataNodes, layoutDataEdges, fitView, focusedNodeId, findAllDescendants]); // ✅ Adicionado findAllDescendants

    // ✨ ADICIONE ESTE useEffect:
    useEffect(() => {
        if (isGerenteViewer && selectedUnitId) {
            setIsLoadingPending(true);
            fetch(`${process.env.REACT_APP_API_URL}/organograma/pending/${selectedUnitId}`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => setPendingRequests(data || []))
            .catch(err => {
                console.error("Erro ao buscar vagas pendentes:", err);
                setPendingRequests([]);
            })
            .finally(() => setIsLoadingPending(false));
        }
    }, [isGerenteViewer, selectedUnitId, token]);


        const handleNodesChange = useCallback((changes) => {
            if (!canEdit) return;
            setNodes((prevNodes) => applyNodeChanges(changes, prevNodes));
            setLayoutDataNodes((prevLayoutNodes) => applyNodeChanges(changes, prevLayoutNodes));
        }, [canEdit]);

        const handleEdgesChange = useCallback((changes) => {
            if (!canEdit) return;
            setEdges((eds) => applyEdgeChanges(changes, eds));
            setLayoutDataEdges((eds) => applyEdgeChanges(changes, eds));
        }, [canEdit]);

        const handleNodeDragStop = useCallback((event, node) => {
            if (!canEdit) return;
            fetch(`${process.env.REACT_APP_API_URL}/organograma/position/${node.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ positionX: node.position.x, positionY: node.position.y })
            })
            .then(handleFetchError)
            .catch(error => console.error(`Erro ao salvar a posição do nó ${node.id}:`, error.message));
        }, [canEdit, token, handleFetchError]);

        const onLayout = useCallback(() => {
            getLayoutedElements(layoutDataNodes, layoutDataEdges, layoutDirection).then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
                setNodes(layoutedNodes);
                setEdges(layoutedEdges);
                setLayoutDataNodes(layoutedNodes);
                setLayoutDataEdges(layoutedEdges);
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        fitView({ duration: 400, padding: 0.1, minZoom: 0.15 });
                    });
                }, 100);
            });
        }, [layoutDataNodes, layoutDataEdges, fitView, layoutDirection]);

        const onConnect = useCallback((params) => {
            if (!canEdit) return;
            const { source, target } = params;
            if (source === target) return;

            const newEdge = {
                id: `e${source}-${target}`, source, target, type: 'smoothstep', animated: true,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
                style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 5' }
            };

            const updateData = { parent_id: parseInt(source, 10) };

            fetch(`${process.env.REACT_APP_API_URL}/organograma/connect/${target}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(updateData)
            })
            .then(handleFetchError)
            .then(response => {
                if (response && response.node) {
                    const updater = (prevEdges) => [...prevEdges, newEdge];
                    setEdges(updater);
                    setLayoutDataEdges(updater);
                }
            })
            .catch(error => console.error("Erro na API ao conectar nó:", error.message));
        }, [canEdit, token, handleFetchError]);

        const onEdgesDelete = useCallback((edgesToDelete) => {
            if (!canEdit) return;
            edgesToDelete.forEach(edge => {
                fetch(`${process.env.REACT_APP_API_URL}/organograma/disconnect/hierarchical/${edge.target}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(handleFetchError)
                .then(() => {
                    const edgeUpdater = (prevEdges) => prevEdges.filter(e => e.id !== edge.id);
                    setEdges(edgeUpdater);
                    setLayoutDataEdges(edgeUpdater);
                })
                .catch(error => console.error("Erro na API ao desconectar nó:", error.message));
            });
        }, [canEdit, token, handleFetchError]);

        const onEdgeMouseEnter = (evt, edge) => { if (!canEdit) return; setEdges((els) => els.map((e) => (e.id === edge.id ? { ...e, style: { ...e.style, stroke: '#dc2626', strokeWidth: 2.5 }, markerEnd: { ...e.markerEnd, color: '#dc2626' } } : e))); };
        const onEdgeMouseLeave = (evt, edge) => { if (!canEdit) return; setEdges((els) => els.map((e) => (e.id === edge.id ? { ...e, style: { ...e.style, stroke: '#94a3b8', strokeWidth: 1.5 }, markerEnd: { ...e.markerEnd, color: '#64748b' } } : e))); };

        const onEdgeClick = useCallback((event, edgeToDelete) => {
            if (!canEdit) return;
            showConfirmation('Tem a certeza que deseja excluir esta conexão?', () => onEdgesDelete([edgeToDelete]));
            event.stopPropagation();
        }, [canEdit, onEdgesDelete, showConfirmation]);

        const handleOpenAddVacancyModal = () => {
            setIsAddVacancyModalOpen(true);
        };

        const handleConfirmAddVacancy = useCallback(async ({ areaId, jobDescription, numberOfCards }) => {
            if (!canEdit || !token || !selectedUnitId || creatingVacanciesRef.current) return;
            const isFirstVacancy = layoutDataNodes.length === 0;
            const parentNode = focusedNodeId ? layoutDataNodes.find(n => n.id === focusedNodeId) : null;
            const selectedArea = areas.find(a => String(a.id) === String(areaId || ''));

            if (!parentNode && !isFirstVacancy && viewMode !== 'list') {
                const newSidebarEntry = {
                    id: `sidebar-batch-${Date.now()}`,
                    count: numberOfCards,
                    label: jobDescription.cargo || 'Nova Vaga',
                    description: selectedArea ? `Área: ${selectedArea.name}` : 'Área não definida',
                    creationData: {
                        ...jobDescription,
                        areaId: selectedArea?.id,
                        parent_id: null,
                    }
                };
                setSidebarNodes(prev => [...prev, newSidebarEntry]);
                setIsAddVacancyModalOpen(false);
                setFocusedNodeId(null);
                if (!isSidebarOpen) setIsSidebarOpen(true);
                return;
            }

            const count = Number(numberOfCards);
            if (!Number.isInteger(count) || count < 1) {
                alert('Informe uma quantidade válida de vagas.');
                return;
            }
            creatingVacanciesRef.current = true;
            setIsCreatingVacancies(true);
            const creationPromises = [];
            for (let i = 0; i < numberOfCards; i++) {
                const newNodePayload = {
                    ...jobDescription,
                    areaId: selectedArea?.id,
                    unitId: selectedUnitId,
                    parent_id: parentNode?.id || null,
                    positionX: (parentNode?.position.x ?? 80) + (i * (NODE_WIDTH + 40)),
                    positionY: parentNode ? parentNode.position.y + NODE_HEIGHT + 100 : 80
                };

                creationPromises.push(
                    fetch(getOrganogramApiUrl('/organograma'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify(newNodePayload),
                    }).then(handleFetchError)
                );
            }

            try {
                const results = await Promise.all(creationPromises);
                if (results.some(result => !result?.node?.id)) throw new Error('A API não confirmou a criação da vaga.');
                const newNodesFromApi = [];
                const newEdgesFromApi = [];

                results.forEach(data => {
                    if (data && data.node) {
                        const newNodeDataFromApi = data.node;

                        const normalizedUserId = (newNodeDataFromApi.user_id === 0 || !newNodeDataFromApi.user_id) ? null : newNodeDataFromApi.user_id;

                        const areaForNode = areas.find(a => a.id.toString() === (newNodeDataFromApi.area_id || "").toString()) || selectedArea;

                        const nodeData = {
                            ...jobDescription,
                            ...newNodeDataFromApi,
                            unit_id: newNodeDataFromApi.unit_id ?? selectedUnitId,
                            name: normalizedUserId ? newNodeDataFromApi.nome : 'Vaga Aberta',
                            role: newNodeDataFromApi.cargo,
                            userId: normalizedUserId,
                            areaName: areaForNode ? areaForNode.name : null,
                            color: areaForNode ? areaForNode.color : null,
                        };

                        const newNode = {
                            id: newNodeDataFromApi.id.toString(),
                            type: 'default',
                            data: { ...nodeData, label: generateNodeLabel(nodeData) },
                            position: { x: newNodeDataFromApi.position_x, y: newNodeDataFromApi.position_y },
                            width: NODE_WIDTH,
                            height: NODE_HEIGHT,
                            style: {
                                backgroundColor: normalizedUserId === null ? '#fefce8' : (nodeData.color || '#ffffff'),
                                borderColor: normalizedUserId === null ? '#facc15' : '#383838ff',
                                borderWidth: '1.5px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                                width: NODE_WIDTH, height: NODE_HEIGHT,
                            },
                        };
                        newNodesFromApi.push(newNode);

                        if (parentNode) {
                            const newEdge = {
                                id: `e${parentNode.id}-${newNodeDataFromApi.id}`,
                                source: parentNode.id.toString(),
                                target: newNodeDataFromApi.id.toString(),
                                type: 'smoothstep', animated: true,
                                markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
                                style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 5' }
                            };
                            newEdgesFromApi.push(newEdge);
                        }
                    }
                });

                if (newNodesFromApi.length > 0) {
                    const allNewNodes = [...layoutDataNodes, ...newNodesFromApi];
                    const allNewEdges = [...layoutDataEdges, ...newEdgesFromApi];

                    await getLayoutedElements(allNewNodes, allNewEdges).then(({ nodes: layoutedNodes, edges: layoutedEdges }) => {
                        setLayoutDataNodes(layoutedNodes);
                        setNodes(layoutedNodes);
                        setLayoutDataEdges(layoutedEdges);
                        setEdges(layoutedEdges);

                        layoutedNodes.forEach(node => {
                            fetch(`${process.env.REACT_APP_API_URL}/organograma/position/${node.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ positionX: node.position.x, positionY: node.position.y })
                            }).catch(err => console.error("Falha ao salvar a posição do nó após o layout:", err));
                        });
                    });
                }

                setIsAddVacancyModalOpen(false);
                if (isFirstVacancy && newNodesFromApi.length === 1) {
                    const firstNode = newNodesFromApi[0];
                    setSelectedNode(firstNode);
                    setFocusedNodeId(firstNode.id);
                    setActiveDropdown(null);
                    setIsUserModalOpen(true);
                } else {
                    setFocusedNodeId(null);
                    if (newNodesFromApi.length > 0) setViewMode('organogram');
                }
            } catch (error) {
                console.error("Erro ao criar nós diretamente no quadro:", error);
                alert('Não foi possível concluir a criação da vaga. Verifique a conexão e tente novamente.');
            } finally {
                creatingVacanciesRef.current = false;
                setIsCreatingVacancies(false);
            }
        }, [canEdit, viewMode, focusedNodeId, layoutDataNodes, layoutDataEdges, areas, selectedUnitId, token, handleFetchError, generateNodeLabel, NODE_WIDTH, NODE_HEIGHT, isSidebarOpen]);

        const handleDeleteSidebarNode = useCallback((nodeId) => { setSidebarNodes(prev => prev.filter(node => node.id !== nodeId)); }, []);

    const handleNodeClick = useCallback((event, node) => {
        if (event.target.closest('[data-action="open-modal"]')) {
            event.stopPropagation();
            const rect = event.target.closest('[data-action="open-modal"]').getBoundingClientRect();
            setSelectedNode(node);
            setActiveDropdown({
                nodeId: node.id,
                top: rect.bottom + window.scrollY + 5,
                left: rect.right + window.scrollX,
            });
            setFocusedNodeId(node.id);
        } else {
            const isDeselecting = focusedNodeId === node.id;
            const newFocusedId = isDeselecting ? null : node.id;
            setFocusedNodeId(newFocusedId);
            setActiveDropdown(null);

            if (!isDeselecting) {
                fitView({
                    nodes: [{ id: node.id }],
                    duration: 800,
                    padding: 0.2,
                });
            }
        }
    }, [fitView, focusedNodeId]);


        const onPaneClick = useCallback(() => {
            setFocusedNodeId(null);
            setActiveDropdown(null);
        }, []);

    const handleSavePDI = useCallback(async (pdiActionsToSave) => {
        if (!selectedNode || !selectedNode.data.userId || !canEdit) {
            console.error("Não é possível salvar: nenhum utilizador selecionado ou sem permissão.");
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedNode.data.userId}/pdi-actions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(pdiActionsToSave)
            });

            const data = await handleFetchError(response);

            console.log('PDI salvo com sucesso:', data);

            setIsPDIModalOpen(false);
            setSelectedNode(null);
            fetchOrganogramData(selectedUnitId);

        } catch (error) {
            console.error(`Erro ao salvar o PDI: ${error.message}`);
            alert(`Falha ao salvar o PDI: ${error.message}`);
        }
    }, [canEdit, selectedNode, token, handleFetchError, fetchOrganogramData, selectedUnitId]);

        const handleStartFillNode = useCallback(() => {
            setIsUserModalOpen(true);
        }, []);


const handleSelectUser = useCallback(async (selectedUser) => {
    if (!selectedNode || !canEdit || assigningCollaboratorRef.current) return;
    const name = selectedUser.name?.trim();
    if (selectedUser.isCustom && !name) return;
    assigningCollaboratorRef.current = true;
    const nodeId = selectedNode.id;
    const requestBody = selectedUser.isCustom
        ? { userId: null, nome_customizado: name, isCustom: true }
        : { userId: selectedUser.id, nome_customizado: null, isCustom: false };

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/${nodeId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(requestBody),
        }).then(handleFetchError);
        if (!response?.node) throw new Error('A API não confirmou o preenchimento da vaga.');

        const finalNodeData = normalizeCollaboratorData({
            ...selectedNode.data,
            ...response.node,
            name: selectedUser.isCustom ? name : (response.node.nome || name),
            nome: selectedUser.isCustom ? name : (response.node.nome || name),
            nome_customizado: selectedUser.isCustom ? name : null,
            userId: selectedUser.isCustom ? null : (response.node.user_id ?? selectedUser.id),
            profile_picture_url: selectedUser.isCustom ? null : response.node.profile_picture_url,
            created_at: selectedNode.data.created_at,
        });
        updateNodeAndLayoutData(nodeId, finalNodeData);
        setIsUserModalOpen(false);
        setSelectedNode(current => current?.id === nodeId ? null : current);
    } catch (error) {
        console.error('Erro ao preencher vaga:', error);
        alert('Não foi possível salvar o colaborador. Tente novamente.');
    } finally {
        assigningCollaboratorRef.current = false;
    }
}, [canEdit, selectedNode, token, updateNodeAndLayoutData, handleFetchError]);

    const handleVacateNode = useCallback(async () => {
        if (!selectedNode || !canEdit) return;
        const nodeId = selectedNode.id;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/vacate/${nodeId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
            }).then(handleFetchError);
            if (!response?.node) throw new Error('A API não confirmou a remoção do colaborador.');
            updateNodeAndLayoutData(nodeId, normalizeCollaboratorData({
                ...response.node,
                userId: null,
                nome_customizado: null,
                nominalOccupantId: null,
                evaluations: {},
                evaluationHistory: [],
                evaluationPeriod: null,
                lastEvaluationDate: null,
                nextEvaluationDate: null,
            }));
            setSelectedNode(current => current?.id === nodeId ? null : current);
        } catch (error) {
            console.error('Erro ao remover colaborador:', error);
            alert('Não foi possível remover o colaborador. Tente novamente.');
        }
    }, [canEdit, selectedNode, token, updateNodeAndLayoutData, handleFetchError]);

        const handleDeleteNode = useCallback(() => {
            if (!selectedNode || !canEdit) return;
            const unitIdOfDeletedNode = selectedNode.data.unit_id?.toString() || selectedUnitId;
            fetch(`${process.env.REACT_APP_API_URL}/organograma/${selectedNode.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(handleFetchError)
            .then(() => {
                const updater = (prev) => prev.filter(n => n.id !== selectedNode.id);
                setNodes(updater);
                setLayoutDataNodes(updater);
                setLayoutDataEdges(prev => prev.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
                setEdges(prev => prev.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
                setUnits(prevUnits =>
                    prevUnits.map(unit =>
                        unit.id.toString() === unitIdOfDeletedNode
                            ? { ...unit, userCount: Math.max(0, parseInt(unit.userCount, 10) - 1) }
                            : unit
                    )
                );
            })
            .catch(error => console.error("Erro na API ao excluir nó:", error.message))
            .finally(() => { setSelectedNode(null); });
        }, [canEdit, selectedNode, token, handleFetchError, selectedUnitId]);

        const handleOpenAreaColorModal = () => { setIsAreaModalForArea(true); };

    const handleAssignArea = useCallback(({ nodeId, areaId, areaObject }) => {
        if (!canEdit) return;

        const applyAreaToNode = (areaToApply) => {
            if (!areaToApply) return;

            fetch(`${process.env.REACT_APP_API_URL}/organograma/assign-area/${nodeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ areaId: areaToApply.id })
            })
            .then(handleFetchError)
            .then(response => {
                if (response && response.node) {
                    const currentNodeData = nodes.find(n => n.id === response.node.id.toString())?.data;
                    if (!currentNodeData) return;

                    const finalNodeData = {
                        ...currentNodeData,
                        area: areaToApply.name,
                        areaName: areaToApply.name,
                        color: areaToApply.color,
                    };

                    updateNodeAndLayoutData(response.node.id, finalNodeData);
                    setIsAreaModalForArea(false);
                    setSelectedNode(null);
                }
            })
            .catch(error => console.error(`Erro ao atribuir área: ${error.message}`));
        };

        if (areaObject) {
            applyAreaToNode(areaObject);
        } else {
            const assignedArea = areas.find(a => a.id === areaId);
            applyAreaToNode(assignedArea);
        }
    }, [canEdit, token, areas, nodes, handleFetchError, updateNodeAndLayoutData]);

    const handleCreateArea = useCallback(({ nodeId, name, color }) => {
        if (!canEdit) return;
        fetch(`${process.env.REACT_APP_API_URL}/areas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ name, color })
        })
        .then(handleFetchError)
        .then(newArea => {
            if (newArea) {
                setAreas(prevAreas => [...prevAreas, newArea]);
                setAllFilterableAreas(prev => {
                    if (prev.find(p => p.name === newArea.name)) return prev;
                    return [...prev, { name: newArea.name, count: 1 }];
                });
                handleAssignArea({ nodeId, areaId: newArea.id, areaObject: newArea });
            }
        })
        .catch(error => console.error(`Erro ao criar área: ${error.message}`));
    }, [canEdit, token, handleAssignArea, handleFetchError]);

        const handleUpdateArea = useCallback(({ areaId, name, color }) => {
            if (!canEdit) return;
            const oldArea = areas.find(a => a.id === areaId);
            if (!oldArea) return;

            fetch(`${process.env.REACT_APP_API_URL}/areas/${areaId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name, color })
            })
            .then(handleFetchError)
            .then(updatedArea => {
                setAreas(prev => prev.map(a => a.id === areaId ? updatedArea : a));
                const updater = (currentNodes) => currentNodes.map(n => {
                    if (n.data.areaName === oldArea.name) {
                        const updatedNodeData = { ...n.data, area: updatedArea.name, areaName: updatedArea.name, color: updatedArea.color };
                        return {
                            ...n,
                            data: { ...updatedNodeData, label: generateNodeLabel(updatedNodeData) },
                            style: { ...n.style, backgroundColor: !hasAssignedCollaborator(updatedNodeData) ? '#fefce8' : (updatedNodeData.color || '#ffffff') },
                        };
                    }
                    return n;
                });
                setNodes(updater);
                setLayoutDataNodes(updater);
                setAllFilterableAreas(prev => prev.map(area => area.name === oldArea.name ? { ...area, name: updatedArea.name } : area));
                setIsAreaModalForArea(false);
                setSelectedNode(null);
            })
            .catch(error => console.error(`Erro ao atualizar área: ${error.message}`));
        }, [canEdit, token, areas, generateNodeLabel, handleFetchError]);

        const handleDeleteArea = useCallback((areaId) => {
            if (!canEdit) return;
            fetch(`${process.env.REACT_APP_API_URL}/areas/${areaId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(handleFetchError)
            .then(() => {
                const deletedArea = areas.find(a => a.id === areaId);
                if (!deletedArea) return;
                setAreas(prev => prev.filter(a => a.id !== areaId));
                const updater = (currentNodes) => currentNodes.map(n => {
                    if (n.data.areaName === deletedArea.name) {
                        const updatedNodeData = { ...n.data, area: null, areaName: null, color: null };
                        return {
                            ...n,
                            data: { ...updatedNodeData, label: generateNodeLabel(updatedNodeData) },
                            style: { ...n.style, backgroundColor: !hasAssignedCollaborator(updatedNodeData) ? '#fefce8' : '#ffffff' },
                        };
                    }
                    return n;
                });
                setNodes(updater);
                setLayoutDataNodes(updater);
                setAllFilterableAreas(prev => prev.filter(area => area.name !== deletedArea.name));
            })
            .catch(error => console.error(`Erro ao excluir área: ${error.message}`));
        }, [canEdit, token, areas, generateNodeLabel, handleFetchError]);

    const handleCreateAreaForModal = useCallback(({ name, color, callback }) => {
        if (!canEdit) return;
        fetch(`${process.env.REACT_APP_API_URL}/areas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ name, color })
        })
        .then(handleFetchError)
        .then(newArea => {
            if (newArea) {
                setAreas(prevAreas => [...prevAreas, newArea]);
                setAllFilterableAreas(prev => {
                    if (prev.find(p => p.name === newArea.name)) return prev;
                    return [...prev, { name: newArea.name, count: 0 }];
                });
                
                if (callback) {
                    callback(newArea);
                }
            }
        })
        .catch(error => console.error(`Erro ao criar área: ${error.message}`));
    }, [canEdit, token, handleFetchError]);

        const handleOpenJobDescriptionModal = () => setIsJobDescriptionModalOpen(true);
        const handleOpenEvaluationSheetModal = () => setIsEvaluationSheetModalOpen(true);
        const handleOpenEvaluationResultModal = () => setIsEvaluationResultModalOpen(true);
        const handleOpenDisciplinaryHistoryModal = () => setIsDisciplinaryHistoryModalOpen(true);
        const handleOpenPDIModal = () => setIsPDIModalOpen(true);
        
       const handleOpenManagerEvaluations = () => {
    navigate('/visao-geral'); // Redireciona para a página nova
};

        const handleManagerEvaluate = useCallback((nodeId) => {
            const node = nodes.find(n => n.id === nodeId.toString());
            if (node) {
                setSelectedNode(node);
                setIsManagerEvaluationsModalOpen(false);
                setIsEvaluationSheetModalOpen(true);
                fitView({ nodes: [{ id: node.id }], duration: 800, padding: 0.2 });
                setFocusedNodeId(node.id);
            } else {
                console.warn("Nó não encontrado no fluxo atual:", nodeId);
                // Opcional: Recarregar dados ou avisar
            }
        }, [nodes, fitView]);

        const handleManagerViewResult = useCallback((nodeData) => {
            const nodeInFlow = nodes.find(n => n.id === nodeData.id.toString());
            if(nodeInFlow) {
                 setSelectedNode(nodeInFlow);
            } else {
                 // Fallback para garantir que o modal receba dados mesmo se o nó não estiver visível
                 setSelectedNode({ id: nodeData.id.toString(), data: nodeData });
            }
            setIsManagerEvaluationsModalOpen(false);
            setIsEvaluationResultModalOpen(true);
        }, [nodes]);

const handleSaveJobDetails = useCallback(async (jobData) => {
    if (!selectedNode || !canEvaluate) return false;
    const nodeId = selectedNode.id;
    const details = pickJobDescriptionData(jobData);

    try {
        if (jdEditMode === 'request_revision') {
            await fetch(`${process.env.REACT_APP_API_URL}/organograma/request-jd-change`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    nodeId,
                    newData: details,
                    unitId: selectedUnitId,
                    justification: 'Alteração de cargo/competências solicitada via histórico de avaliação.',
                }),
            }).then(handleFetchError);
            alert('Sua solicitação de alteração foi enviada para análise do RH/Administração.');
            setJdEditMode('normal');
        } else {
            const customName = getCustomCollaboratorName(selectedNode.data);
            const requestBody = customName ? { ...details, nome_customizado: customName } : details;
            const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/job-details/${nodeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(requestBody),
            }).then(handleFetchError);
            updateNodeAndLayoutData(nodeId, currentData => mergeJobDescriptionData(currentData, details, response));
        }
        setIsJobDescriptionModalOpen(false);
        return true;
    } catch (error) {
        console.error('Erro ao salvar Job Description:', error);
        alert('Não foi possível salvar o Job Description. Tente novamente.');
        return false;
    }
}, [selectedNode, token, canEvaluate, jdEditMode, selectedUnitId, handleFetchError, updateNodeAndLayoutData]);
// --- FUNÇÃO AUXILIAR DE CÁLCULO DE DATA (MODO REAL ✅) ---
    const calculateNextDate = (period) => {
        const date = new Date();
        switch (period) {
            case 'Semanal': date.setDate(date.getDate() + 7); break;
            
            // 👇 VOLTA AO NORMAL (1 Mês)
            case 'Mensal': date.setMonth(date.getMonth() + 1); break; 
            
            case 'Trimestral': date.setMonth(date.getMonth() + 3); break;
            case 'Semestral': date.setMonth(date.getMonth() + 6); break;
            default: date.setMonth(date.getMonth() + 1);
        }
        return date.toISOString();
    };

// No componente OrganogramFlow



// DENTRO DE OrganogramFlow
// DENTRO DE OrganogramFlow

const handleSaveEvaluation = useCallback(async (payload, isFinal = false) => {
    if (!canEvaluate) throw new Error('Você não tem permissão para avaliar este colaborador.');
    const endpoint = getEvaluationEndpoint(selectedNode);
    const nodeId = selectedNode.id;
    const registeredUserId = getRegisteredUserId(selectedNode.data);
    const nominalOccupantId = selectedNode.data.nominalOccupantId;
    if (registeredUserId === null && !nominalOccupantId) {
        throw new Error('Recarregue o organograma para atualizar o vínculo deste colaborador.');
    }
    const {
        evaluations: submittedEvaluations = {},
        evaluationHistory: providedHistory,
    } = payload;
    const period = payload.period ?? payload.evaluationPeriod ?? selectedNode.data.evaluationPeriod ?? 'Semestral';
    const hasProvidedHistory = Array.isArray(providedHistory);
    const finalizingCycle = isFinal && !hasProvidedHistory;
    const currentEvaluations = {};
    Object.entries(submittedEvaluations || {}).forEach(([compName, evaluation]) => {
        const entry = evaluation || {};
        currentEvaluations[compName] = {
            rating: entry.rating,
            facts: (entry.facts || []).filter(f => f && (f.factDate || f.fact || f.factDescription)),
        };
    });

    const bodyPayload = {
        evaluations: finalizingCycle ? {} : currentEvaluations,
        evaluationPeriod: period,
        isFinal: finalizingCycle,
    };
    if (registeredUserId === null) bodyPayload.nominalOccupantId = nominalOccupantId;
    if (hasProvidedHistory) {
        bodyPayload.evaluationHistory = providedHistory;
        bodyPayload.lastEvaluationDate = payload.lastEvaluationDate ?? selectedNode.data.lastEvaluationDate;
        bodyPayload.nextEvaluationDate = payload.nextEvaluationDate ?? selectedNode.data.nextEvaluationDate;
    } else if (finalizingCycle) {
        const lastDate = new Date().toISOString();
        bodyPayload.lastEvaluationDate = lastDate;
        bodyPayload.nextEvaluationDate = calculateNextDate(period);
        bodyPayload.evaluationHistory = [{
            date: lastDate,
            period,
            evaluatorName: user?.name || 'Gestor',
            evaluations: currentEvaluations,
        }, ...(selectedNode.data.evaluationHistory || [])];
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(bodyPayload),
    }).then(handleFetchError);

    // Só mostra o resultado salvo depois que o backend confirmar a gravação.
    const finalNodeData = {
        evaluations: bodyPayload.evaluations,
        evaluationPeriod: period,
    };
    if (bodyPayload.evaluationHistory) {
        finalNodeData.evaluationHistory = bodyPayload.evaluationHistory;
        finalNodeData.lastEvaluationDate = bodyPayload.lastEvaluationDate;
        finalNodeData.nextEvaluationDate = bodyPayload.nextEvaluationDate;
    }
    const savedData = response?.node || response?.user;
    if (savedData) {
        for (const field of ['evaluations', 'evaluationPeriod', 'evaluationHistory', 'lastEvaluationDate', 'nextEvaluationDate']) {
            if (savedData[field] !== undefined) finalNodeData[field] = savedData[field];
        }
    }
    updateNodeAndLayoutData(nodeId, currentData => {
        const sameOccupant = registeredUserId === null
            ? currentData.nominalOccupantId === nominalOccupantId
            : getRegisteredUserId(currentData) === registeredUserId;
        return sameOccupant ? finalNodeData : {};
    });
    return true;
}, [canEvaluate, selectedNode, token, user, handleFetchError, updateNodeAndLayoutData]);


        const handleSaveDisciplinaryHistory = useCallback((historyData) => {
            if (!selectedNode || !canEdit) return;

            const updatedHistory = {
                ...(selectedNode.data.disciplinaryHistory || {}),
                description: historyData.description,
            };

            updateNodeAndLayoutData(selectedNode.id, { disciplinaryHistory: updatedHistory });
            setIsDisciplinaryHistoryModalOpen(false);
            setSelectedNode(null);

        }, [canEdit, selectedNode, updateNodeAndLayoutData]);

    // 1. Função para o VISUALIZADOR salvar a SOLICITAÇÃO (AGORA CRIA O NÓ PENDENTE)
        const handleConfirmVacancyRequest = useCallback(async ({ areaId, jobDescription }) => {
            const parentNode = focusedNodeId ? layoutDataNodes.find(n => n.id === focusedNodeId) : null;
            const selectedArea = areas.find(a => a.id.toString() === areaId.toString());

            // ✨ PREPARA O PAYLOAD PARA A ROTA /organograma ✨
            const newNodePayload = {
                ...jobDescription, // Contém cargo, competencias, etc.
                areaId: selectedArea?.id,
                unitId: selectedUnitId,
                parent_id: parentNode ? parentNode.id : null,
                positionX: parentNode ? parentNode.position.x : 50, // Posição inicial
                positionY: parentNode ? parentNode.position.y + 200 : 50, // Posição inicial
                
                // ✨ CAMPOS-CHAVE DA NOVA LÓGICA ✨
                status: 'pending', // Define o status como pendente
                requested_by_user_id: user.id 
            };

            try {
                // ✨ CHAMA A ROTA DE CRIAÇÃO DE NÓ PADRÃO ✨
                const data = await fetch(`${process.env.REACT_APP_API_URL}/organograma`, { 
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(newNodePayload),
                }).then(handleFetchError);

                if (data && data.node) {
                    // Adiciona o novo nó pendente ao estado local imediatamente
                    // (copiado da lógica 'onDrop')
                    const newNodeDataFromApi = data.node;
                    const normalizedUserId = null; // É pendente, então não tem usuário
                    const areaForNode = areas.find(a => a.id.toString() === (newNodeDataFromApi.area_id || "").toString()) || selectedArea;

                    const nodeData = {
                        ...jobDescription,
                        ...newNodeDataFromApi,
                        name: 'Vaga Pendente',
                        role: newNodeDataFromApi.cargo,
                        userId: normalizedUserId,
                        areaName: areaForNode ? areaForNode.name : null,
                        color: areaForNode ? areaForNode.color : null,
                        status: 'pending', // Garante o status
                        requested_by_user_id: user.id
                    };

                    const newNode = {
                        id: newNodeDataFromApi.id.toString(),
                        type: 'default',
                        data: { ...nodeData, label: generateNodeLabel(nodeData) },
                        position: { x: newNodeDataFromApi.position_x, y: newNodeDataFromApi.position_y },
                        width: NODE_WIDTH,
                        height: NODE_HEIGHT,
                        style: {
                            backgroundColor: '#fffbeb', // Amarelo claro
                            borderColor: '#f59e0b', // Laranja
                            borderWidth: '1.5px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                            width: NODE_WIDTH, height: NODE_HEIGHT,
                        },
                    };
                    
                    // Adiciona o nó e o edge (se houver pai)
                    setNodes(prev => [...prev, newNode]);
                    setLayoutDataNodes(prev => [...prev, newNode]);

                    if (parentNode) {
                        const newEdge = {
                            id: `e${parentNode.id}-${newNodeDataFromApi.id}`,
                            source: parentNode.id.toString(),
                            target: newNodeDataFromApi.id.toString(),
                            type: 'smoothstep', animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
                            style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 5' }
                        };
                        setEdges(prev => [...prev, newEdge]);
                        setLayoutDataEdges(prev => [...prev, newEdge]);
                    }
                }
                
                setIsAddVacancyModalOpen(false);
                setFocusedNodeId(null);
                alert('Solicitação de vaga enviada! Um card pendente foi criado.');

            } catch (error) {
                console.error("Erro ao solicitar vaga:", error);
                alert(`Falha ao enviar solicitação: ${error.message}.`);
            }
            
        }, [focusedNodeId, layoutDataNodes, areas, selectedUnitId, token, user, handleFetchError, generateNodeLabel]);
        // 2. Função para o ADMIN abrir o modal de pendentes e buscar os dados
        // 2. Função para o ADMIN abrir o modal de pendentes e buscar os DADOS DO ORGANOGRAMA
    // (Linha ~3704)
    // ✨ CORREÇÃO: Este useEffect busca o NÚMERO de pendentes para o Admin/Editor
    useEffect(() => {
        // A condição mudou de 'isGerenteViewer' para 'canEdit'
        // Isso garante que a contagem seja buscada no carregamento da página para Admins/Editores
        if (canEdit && selectedUnitId && token) { 
        	setIsLoadingPending(true);
        	fetch(`${process.env.REACT_APP_API_URL}/organograma/pending/${selectedUnitId}`, { 
        	   headers: { 'Authorization': `Bearer ${token}` }
        	})
      	   .then(handleFetchError) // <-- handleFetchError é usado aqui
      	   .then(data => {
      	 	 	 setPendingRequests(data || []);
      	   })
      	   .catch(err => {
      	 	 	 console.error("Erro ao buscar vagas pendentes:", err);
      	 	 	 setPendingRequests([]);
      	   })
      	   .finally(() => {
      	 	 	 setIsLoadingPending(false);
      	   });
        }
    }, [canEdit, selectedUnitId, token, handleFetchError]); // <-- Dependências corretas


        const handleOpenPendingModal = () => {
            setIsPendingModalOpen(true);
            setIsLoadingPending(true);
            // ✨ ROTA ATUALIZADA ✨
            fetch(`${process.env.REACT_APP_API_URL}/organograma/pending/${selectedUnitId}`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(handleFetchError)
            .then(data => {
                // O backend agora retorna nós do organograma com status 'pending'
                setPendingRequests(data || []);
            })
            .catch(err => {
                console.error("Erro ao buscar vagas pendentes:", err);
                setPendingRequests([]);
            })
            .finally(() => {
                setIsLoadingPending(false);
            });
        };

        // 3. Função para o ADMIN aprovar uma solicitação (muda status)
        const handleApproveRequest = useCallback(async (nodeId) => {
            try {
                // ✨ ROTA ATUALIZADA ✨
                const data = await fetch(`${process.env.REACT_APP_API_URL}/organograma/approve/${nodeId}`, { 
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(handleFetchError);
                
                // Remove da lista local do modal
                setPendingRequests(prev => prev.filter(req => req.id !== nodeId));
                
                // Atualiza o nó no frontend para "Vaga Aberta"
                if (data && data.node) {
                    updateNodeAndLayoutData(data.node.id, { 
                        status: 'vacant', 
                        userId: null, 
                        name: 'Vaga Aberta',
                        requested_by_user_id: null
                    });
                }

            } catch (error) {
                console.error("Erro ao aprovar vaga:", error);
                alert(`Falha ao aprovar: ${error.message}.`);
            }
        }, [token, handleFetchError, updateNodeAndLayoutData]);

        // 4. Função para o ADMIN rejeitar uma solicitação (exclui o nó)
        const handleRejectRequest = useCallback(async (nodeId) => {
            
            // Pergunta antes de excluir o card
            showConfirmation('Tem certeza que deseja rejeitar e excluir este card "Vaga Pendente"?', async () => {
                try {
                    // ✨ ROTA ATUALIZADA (usa a rota de delete padrão) ✨
                    await fetch(`${process.env.REACT_APP_API_URL}/organograma/${nodeId}`, { 
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    }).then(handleFetchError);
                    
                    // Remove da lista local do modal
                    setPendingRequests(prev => prev.filter(req => req.id !== nodeId));
                    
                    // Remove do organograma principal
                    setNodes(prev => prev.filter(n => n.id !== nodeId.toString()));
                    setLayoutDataNodes(prev => prev.filter(n => n.id !== nodeId.toString()));
                    setEdges(prev => prev.filter(e => e.source !== nodeId.toString() && e.target !== nodeId.toString()));
                    setLayoutDataEdges(prev => prev.filter(e => e.source !== nodeId.toString() && e.target !== nodeId.toString()));

                } catch (error) {
                    console.error("Erro ao rejeitar vaga:", error);
                    alert(`Falha ao rejeitar: ${error.message}.`);
                }
            });

        }, [token, handleFetchError, showConfirmation]);
    
        // ✨ FIM DAS NOVAS FUNÇÕES DE API

        const onDragOver = useCallback((event) => { if (!canEdit) return; event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }, [canEdit]);

    const onDrop = useCallback(async (event) => {
        if (!canEdit || !selectedUnitId) return;
        event.preventDefault();

        const reactFlowWrapperCurrent = reactFlowWrapper.current;
        if (!reactFlowWrapperCurrent) return;

        try {
            const sidebarNodeDataString = event.dataTransfer.getData('sidebarNode');
            if (!sidebarNodeDataString) return;
            
            const sidebarNode = JSON.parse(sidebarNodeDataString);
            const { count, creationData } = sidebarNode;

            const reactFlowBounds = reactFlowWrapperCurrent.getBoundingClientRect();
            const position = project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });

            const newNodesFromDrop = [];
            for (let i = 0; i < count; i++) {
                const offsetPosition = { x: position.x + (i * (NODE_WIDTH + 50)), y: position.y };
                const tempId = `temp-${Date.now()}-${i}`;
                const selectedArea = areas.find(a => a.id.toString() === (creationData.areaId || "").toString());
                const nodeData = {
                    ...creationData, unit_id: selectedUnitId, name: 'Vaga Aberta', role: creationData.cargo, userId: null,
                    areaName: selectedArea ? selectedArea.name : null, color: selectedArea ? selectedArea.color : null,
                };
                const newNode = {
                    id: tempId, type: 'default', position: offsetPosition,
                    data: { ...nodeData, label: generateNodeLabel(nodeData) },
                    width: NODE_WIDTH, height: NODE_HEIGHT,
                    style: {
                        backgroundColor: '#fefce8', borderColor: '#facc15', borderWidth: '1.5px',
                        borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        width: NODE_WIDTH, height: NODE_HEIGHT,
                    },
                };
                newNodesFromDrop.push(newNode);
            }

            setNodes(prev => [...prev, ...newNodesFromDrop]);
            setLayoutDataNodes(prev => [...prev, ...newNodesFromDrop]);
            setSidebarNodes(prev => prev.filter(n => n.id !== sidebarNode.id));
            
            const creationPromises = newNodesFromDrop.map(frontendNode => {
                const newNodePayload = {
                    ...frontendNode.data, unitId: selectedUnitId,
                    positionX: frontendNode.position.x, positionY: frontendNode.position.y,
                };
                return fetch(`${process.env.REACT_APP_API_URL}/organograma`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(newNodePayload),
                }).then(handleFetchError);
            });

            const results = await Promise.all(creationPromises);
            
            const updateStateWithRealData = (prevNodes) => {
                let updatedNodes = [...prevNodes];
                results.forEach((data, index) => {
                    if (data && data.node) {
                        const tempId = newNodesFromDrop[index].id;
                        const nodeIndex = updatedNodes.findIndex(n => n.id === tempId);

                        if (nodeIndex !== -1) {
                            const tempNode = updatedNodes[nodeIndex];
                            
                            const finalNodeData = { ...tempNode.data, created_at: data.node.createdAt };
                            
                            updatedNodes[nodeIndex] = {
                                ...tempNode,
                                id: data.node.id.toString(),
                                data: { ...finalNodeData, label: generateNodeLabel(finalNodeData) },
                                style: { ...tempNode.style, borderColor: '#676767ff' },
                            };
                        }
                    }
                });
                return updatedNodes;
            };

            setNodes(updateStateWithRealData);
            setLayoutDataNodes(updateStateWithRealData);
            
            setUnits(prev => prev.map(unit =>
                unit.id.toString() === selectedUnitId
                    ? { ...unit, userCount: parseInt(unit.userCount, 10) + count }
                    : unit
            ));

        } catch (error) {
            console.error("Erro ao criar nós por Drag and Drop:", error);
            alert(`Falha ao criar os cards: ${error.message}.`);
            fetchOrganogramData(selectedUnitId);
        }

    }, [canEdit, selectedUnitId, token, project, areas, handleFetchError, generateNodeLabel, NODE_WIDTH, NODE_HEIGHT, fetchOrganogramData]);


    const onDragStart = (event, node) => {
        event.dataTransfer.setData('application/reactflow', 'sidebarNode');
        event.dataTransfer.setData('sidebarNode', JSON.stringify(node));
        event.dataTransfer.effectAllowed = 'move';
    };

        const handleMoveStart = useCallback(() => {
            if (activeDropdown) {
                setActiveDropdown(null);
            }
        }, [activeDropdown]);

    const listScopeNodes = useMemo(() => {
        const unitNodes = layoutDataNodes.filter(node => String(node.data.unit_id) === String(selectedUnitId));
        if (selectedFilterAreas.length === 0) return unitNodes;
        const roots = unitNodes.filter(node => selectedFilterAreas.includes(node.data.areaName));
        const visibleIds = new Set(roots.map(node => node.id));
        if (showDescendants) roots.forEach(root => {
            findAllDescendants(root.id, layoutDataEdges).forEach(id => visibleIds.add(id));
        });
        return unitNodes.filter(node => visibleIds.has(node.id));
    }, [layoutDataNodes, layoutDataEdges, selectedUnitId, selectedFilterAreas, showDescendants, findAllDescendants]);

    const handleChangeView = useCallback(mode => {
        setActiveDropdown(null);
        setViewMode(mode);
    }, []);

    const handleListActions = useCallback((event, node) => {
        if (!event || !node) { setActiveDropdown(null); return; }
        const rect = event.currentTarget.getBoundingClientRect();
        setSelectedNode(node);
        setFocusedNodeId(node.id);
        setActiveDropdown(current => current?.nodeId === node.id ? null : {
            nodeId: node.id, top: rect.bottom + 5, left: rect.right,
        });
    }, []);

    const handleListDetails = useCallback(node => {
        setSelectedNode(node);
        setFocusedNodeId(node.id);
        setActiveDropdown(null);
        setJdEditMode('normal');
        setIsJobDescriptionModalOpen(true);
    }, []);

    const handleShowInOrganogram = useCallback(node => {
        setActiveDropdown(null);
        if (node) {
            setSelectedNode(node);
            setFocusedNodeId(node.id);
            nodeToLocateRef.current = node.id;
        }
        setViewMode('organogram');
    }, []);

    useEffect(() => {
        if (viewMode !== 'organogram' || !nodeToLocateRef.current) return;
        const nodeId = nodeToLocateRef.current;
        const timer = setTimeout(() => {
            fitView({ nodes: [{ id: nodeId }], duration: 600, padding: 0.3 });
            nodeToLocateRef.current = null;
        }, 200);
        return () => clearTimeout(timer);
    }, [viewMode, fitView]);

    useEffect(() => {
        setActiveDropdown(null);
        setSelectedNode(null);
        setFocusedNodeId(null);
        nodeToLocateRef.current = null;
        setEvaluation360Open(false);
        setEvaluation360PersonId(null);
    }, [selectedUnitId, currentUserId]);

    const isTeamLoading = isOrganogramLoading || loadedOrganogramUnitId !== String(selectedUnitId);
    const selectedUnitName = units.find(unit => String(unit.id) === String(selectedUnitId))?.name || '';
    const evaluation360People = useMemo(() =>
        build360People(layoutDataNodes, layoutDataEdges, selectedUnitId),
        [layoutDataNodes, layoutDataEdges, selectedUnitId]
    );
    const handleOpenEvaluation360 = useCallback(node => {
        if (!canEvaluate || !selectedUnitId || isTeamLoading || organogramError) return;
        setActiveDropdown(null);
        const person = node ? evaluation360People.find(item => item.sourceNodeIds?.includes(String(node.id))) : null;
        setEvaluation360PersonId(person?.id || null);
        setEvaluation360Open(true);
    }, [canEvaluate, selectedUnitId, isTeamLoading, organogramError, evaluation360People]);

        const defaultEdgeOptions = { type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' }, style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 5' } };

    if (companyLoading || unitsLoading || isLoadingAreas || isLoadingCategories) {
            return (
                <div style={{...mainContainerStyle, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc'}}>
                    <h2 style={{fontSize: '1.2rem', fontWeight: '500', color: '#475569'}}>A carregar dados...</h2>
                </div>
            );
        }
        return (
            <>
                <style>{globalResetStyle + peopleListStyles}</style>
                <div style={mainContainerStyle}>
                {canEdit && viewMode === 'organogram' && (
        <div style={{
            ...sidebarStyle,
            width: isSidebarOpen ? (isMobile ? 'min(320px, calc(100vw - 48px))' : '340px') : '0px',
            position: isMobile ? 'absolute' : 'relative',
            top: 0, bottom: 0, left: 0,
            overflow: 'hidden'
        }}>
            {isSidebarOpen && (
                <>
                    {/* Header */}
                    <div style={sidebarHeaderStyle}>
                        <SidebarIcon />
                        <span>Vagas Não Atribuídas</span>
                    </div>

                    {/* Content */}
                    <div style={sidebarContentStyle}>
                        {sidebarNodes.length === 0 ? (
                            <div style={emptyStateStyle}>
                                <EmptyBoxIcon />
                                <div>
                                    <p style={{
                                        margin: '0 0 8px',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        color: '#64748b'
                                    }}>
                                        Nenhuma vaga pendente
                                    </p>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '0.875rem',
                                        color: '#94a3b8',
                                        lineHeight: '1.5'
                                    }}>
                                        Clique em <strong>"+ Vaga"</strong> no header para criar novos cards
                                    </p>
                                </div>
                            </div>
                        ) : (
                            sidebarNodes.map(node => (
                                <div
                                    key={node.id}
                                    style={isDraggableHovered === node.id 
                                        ? {...draggableNodeStyle, ...draggableNodeHoverStyle} 
                                        : draggableNodeStyle}
                                    onDragStart={(event) => onDragStart(event, node)}
                                    onMouseEnter={() => setIsDraggableHovered(node.id)}
                                    onMouseLeave={() => setIsDraggableHovered(null)}
                                    draggable
                                >
                                    {/* Barra colorida lateral */}
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: '4px',
                                        background: 'linear-gradient(to bottom, #3b82f6, #2563eb)',
                                        borderTopLeftRadius: '10px',
                                        borderBottomLeftRadius: '10px'
                                    }}></div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flexGrow: 1,
                                        paddingLeft: '8px'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '1.2rem',
                                            fontWeight: 'bold',
                                            flexShrink: 0,
                                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                                        }}>
                                            {node.count}
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                            minWidth: 0
                                        }}>
                                            <strong style={{
                                                color: '#1e293b',
                                                fontSize: '0.95rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                lineHeight: '1.4'
                                            }}>
                                                {node.label}
                                            </strong>
                                            <small style={{
                                                color: '#64748b',
                                                fontSize: '0.8rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                marginTop: '2px'
                                            }}>
                                                {node.description}
                                            </small>
                                        </div>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteSidebarNode(node.id);
                                        }}
                                        style={{
                                            background: '#fee2e2',
                                            border: '2px solid #fecaca',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            padding: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#dc2626',
                                            transition: 'all 0.2s',
                                            flexShrink: 0
                                        }}
                                        title="Remover da Sidebar"
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fecaca';
                                            e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fee2e2';
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <DeleteIcon />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    )}
                    <div style={layoutPaneStyle}>
                        
    <header style={headerStyle}>
        {/* Toggle + Título */}
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-start',
            flexShrink: 1,
            minWidth: 0 // ✅ Permite encolher
        }}>
            {canEdit && viewMode === 'organogram' && (
                <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    style={toggleButtonStyle}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                    title={isSidebarOpen ? 'Fechar' : 'Abrir'}
                >
                    {isSidebarOpen ? <XIcon style={{width: '20px', height: '20px'}} /> : <MenuIcon />}
                </button>
            )}

            <h1 style={headerTitleStyle}>
                {companyLoading ? (
                    <span style={{color: '#64748b', fontSize: '0.9rem'}}>Carregar...</span>
                ) : companyError ? (
                    <span style={{color: '#dc2626', fontSize: '0.9rem'}}>Erro</span>
                ) : (
                    company?.name || 'Empresa'
                )}
            </h1>
        </div>

       <div style={controlsContainerStyle}>
    {/* Grupo 1: Seletores - OCULTOS para gerentes */}
    {!isGerenteViewer && (
        <div style={controlsGroupStyle}>
            <select
                value={selectedUnitId}
                aria-label="Unidade da equipe"
                onChange={(e) => setSelectedUnitId(e.target.value)}
                style={{
                    ...baseInputStyle,
                    width: isMobile ? 'calc(50% - 6px)' : 'auto',
                    minWidth: isMobile ? 0 : (isTablet ? '120px' : '160px'),
                    maxWidth: isMobile ? '100%' : (isTablet ? '160px' : '200px'),
                    padding: isTablet ? '7px 9px' : '8px 11px',
                    fontSize: isTablet ? '0.75rem' : '0.82rem',
                    borderRadius: '8px',
                    border: '2px solid #e2e8f0',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                    flexShrink: 1,
                    flexGrow: 0,
                    position: 'relative',
                    zIndex: 1250
                }}
                disabled={unitsLoading || !!unitsError}
                title={!unitsLoading && !unitsError && selectedUnitId 
                    ? units.find(u => u.id === selectedUnitId)?.name 
                    : ''}
            >
                {unitsLoading && <option>Carregar...</option>}
                {unitsError && <option>Erro</option>}
                {!unitsLoading && !unitsError && units.map(unit => (
                    <option key={unit.id} value={unit.id}>{unit.name}</option>
                ))}
            </select>

            <div style={{flexShrink: 0, minWidth: isMobile ? 0 : (isTablet ? '180px' : '220px'), width: isMobile ? 'calc(50% - 6px)' : 'auto'}}>
                <MultiSelectFilter
                    options={allFilterableAreas}
                    selectedOptions={selectedFilterAreas}
                    onChange={handleAreaFilterChange}
                />
            </div>
        </div>
    )}

    {selectedFilterAreas.length > 0 && (
        <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            padding: isTablet ? '6px 9px' : '7px 10px',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '2px solid #bfdbfe',
            fontSize: isTablet ? '0.7rem' : '0.78rem',
            fontWeight: '500',
            color: '#1e40af',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start'
        }}>

                    <input
                        type="checkbox"
                        checked={showDescendants}
                        onChange={(e) => setShowDescendants(e.target.checked)}
                        style={{width: '14px', height: '14px', accentColor: '#3b82f6', cursor: 'pointer', flexShrink: 0}}
                    />
                    {isTablet ? 'Subord.' : 'Subordinados'}
                </label>
            )}

            <div style={headerSeparatorStyle}></div>

            {/* Grupo 2: Ações principais */}
            <div style={{
                ...controlsGroupStyle,
                justifyContent: isMobile ? 'space-between' : 'flex-start'
            }}>
                {/* Botão Minhas Avaliações para Gerentes */}
                {/* Trecho dentro do seu Header no Organograma */}
{(user?.isGerencial || 
  (user?.cargo && /gerente|lider|líder|coordena/i.test(user.cargo)) || 
  (user?.jobDescription && user.jobDescription.toLowerCase().includes('gerencial'))) && (
    <StyledButton
        onClick={handleOpenManagerEvaluations} // Agora chama o navigate
        variant="secondary"
        icon={<Users width={16} />}
        title="Minhas Avaliações"
    >
        {isTablet ? 'Avaliações' : 'Minhas Avaliações'}
    </StyledButton>
)}
                {canEvaluate && <StyledButton
                    onClick={() => handleOpenEvaluation360()}
                    disabled={!selectedUnitId || isTeamLoading || Boolean(organogramError)}
                    variant="secondary" icon={<Users width={16} />}
                    title="Criar e acompanhar avaliações 360°">
                    Avaliação 360°
                </StyledButton>}
                {viewMode === 'organogram' && <StyledButton
                    onClick={handleLayoutToggle}
                    variant="ghost"
                    icon={layoutDirection === 'DOWN' ? <LayoutHorizontalIcon/> : <LayoutVerticalIcon/>}
                    title={layoutDirection === 'DOWN' ? 'Layout Horizontal' : 'Layout Vertical'}
                />}
                {canEdit && (
                   <StyledButton
                disabled={!selectedUnitId || isTeamLoading || isCreatingVacancies}
                onClick={handleOpenAddVacancyModal}
                variant="primary"
                icon={<span style={{fontSize: '1rem', fontWeight: 'bold'}}>+</span>}
            >{isTablet ? 'Vaga' : 'Vaga'}</StyledButton>
                )}
    
                {/* ✨ BOTÃO ADICIONADO PARA VISUALIZADOR GERENTE ✨ */}
                {isGerenteViewer && viewMode === 'organogram' && (
                    <StyledButton
                    	onClick={handleOpenAddVacancyModal} // Reutiliza o mesmo modal
                    	variant="primary"
                    	icon={<span style={{fontSize: '1rem', fontWeight: 'bold'}}>+</span>}
                    	// ✨ ADIÇÕES AQUI ✨
                    	disabled={!focusedNodeId}
                    	title={!focusedNodeId ? "Clique em um card para definir o gestor desta vaga" : "Solicitar nova vaga (requer aprovação)"}
                    >
                    	{isTablet ? 'Solicitar' : 'Solicitar Vaga'}
                    </StyledButton>
                )}
                {/* ✨ BOTÃO ADICIONADO PARA PENDÊNCIAS (ADMIN) ✨ */}
                {canEdit && (
                    <StyledButton
                        onClick={handleOpenPendingModal}
                        variant="warning"
                        icon={<InboxIcon />} // Usei o ícone do modal, pode trocar
                        title="Ver solicitações pendentes">
                        {isTablet ? 'Pend.' : 'Pendentes'}
                        {/* Badge de notificação */}
                        {pendingRequests.length > 0 && (
                        <span style={{
        position: 'absolute',
        top: '-10px',
        right: '-10px',
        width: '24px',
        height: '24px',
        backgroundColor: '#dc2626',
        color: 'white',
        borderRadius: '50%',
        display: 'flex',  // ✅ MUDE DE 'none' PARA 'flex'
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '12px',
        zIndex: 10,
        animation: 'pulse-glow-pending 2s infinite ease-in-out'
    }}>
        {pendingRequests.length}
    </span>
                        )}
                    </StyledButton>
                )}

                {viewMode === 'organogram' && <StyledButton
                    onClick={() => onLayout()}
                    variant="secondary"
                    icon={
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                    }>
                    {isTablet ? 'Auto' : 'Organizar'}
                </StyledButton>}

                <StyledButton
                    onClick={() => setIsHelpMenuOpen(true)}
                    variant="warning"
                    icon={<HelpCircleIcon />}
                    title="Central de Ajuda"
                />
            </div>
        </div>
    </header>
    <PeopleViewSwitch value={viewMode} onChange={handleChangeView} unitName={selectedUnitName} hasAreaFilter={selectedFilterAreas.length > 0} />

    {token && selectedUnitId && canEvaluate && <Avaliacao360Workspace
        key={JSON.stringify([company?.id, selectedUnitId, currentUserId, user?.role, isGerenteViewer])}
        open={evaluation360Open}
        onClose={() => setEvaluation360Open(false)}
        people={evaluation360People}
        unitId={selectedUnitId}
        unitName={selectedUnitName}
        companyName={company?.name || company?.nome || 'GPC'}
        initialPersonId={evaluation360PersonId}
        canManage={Boolean(canEvaluate)}
        canAddPerson={Boolean(canEdit) && !isTeamLoading && !organogramError}
        onAddPerson={() => { setEvaluation360Open(false); handleOpenAddVacancyModal(); }}
    />}

    {/* Menu de Seleção de Ajuda */}
    <Dialog open={isHelpMenuOpen} onClose={() => setIsHelpMenuOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', padding: '16px' } }}>
        <h3 style={{ textAlign: 'center', marginTop: 0, color: '#1e293b' }}>Central de Ajuda</h3>
        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '24px' }}>O que você deseja acessar?</p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
                onClick={() => {
                    setIsHelpMenuOpen(false);
                    setIsExplanatoryVideoModalOpen(true);
                }}
                style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#334155'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>▶</div>
                Vídeos Explicativos
            </button>

            <button
                onClick={() => {
                    setIsHelpMenuOpen(false);
                    setIsManualsModalOpen(true);
                }}
                style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#334155'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📄</div>
                Manuais em PDF
            </button>
        </div>
        
        <button onClick={() => setIsHelpMenuOpen(false)} style={{ marginTop: '20px', background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
    </Dialog>

    <ModalPortal>
                        <ManualsModal isOpen={isManualsModalOpen} onClose={() => setIsManualsModalOpen(false)} />
                        <ExplanatoryVideoModal isOpen={isExplanatoryVideoModalOpen} onClose={() => setIsExplanatoryVideoModalOpen(false)} />
                        </ModalPortal>
                        <ModalPortal>
                            <UserSelectionModal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} onSelectUser={handleSelectUser} unitId={selectedUnitId} token={token} />
                        </ModalPortal>
                        {activeDropdown && selectedNode && activeDropdown.nodeId === selectedNode.id && (
                            <NodeActionDropdown
                                node={selectedNode}
                                position={{ top: activeDropdown.top, left: activeDropdown.left }}
                                onClose={() => setActiveDropdown(null)}
                                onFill={handleStartFillNode}
                                onVacate={handleVacateNode}
                                onDelete={handleDeleteNode}
                                onOpenAreaColorModal={handleOpenAreaColorModal}
                                onOpenJobDescriptionModal={handleOpenJobDescriptionModal}
                                onOpenEvaluationSheetModal={handleOpenEvaluationSheetModal}
                                onOpenEvaluation360={() => handleOpenEvaluation360(selectedNode)}
                                onOpenEvaluationResultModal={handleOpenEvaluationResultModal}
                                onOpenDisciplinaryHistoryModal={handleOpenDisciplinaryHistoryModal}
                                onOpenPDIModal={handleOpenPDIModal}
                                onShowConfirmation={showConfirmation}
                                canEdit={canEdit}
                                isGerenteViewer={isGerenteViewer}
                            />
                        )}
                        <ModalPortal>
                            <AreaColorModal isOpen={isAreaModalOpen} onClose={() => setIsAreaModalForArea(false)} node={selectedNode} existingAreas={areas} onAssignArea={handleAssignArea} onCreateArea={handleCreateArea} onUpdateArea={handleUpdateArea} onDeleteArea={handleDeleteArea} onShowConfirmation={showConfirmation} canEdit={canEdit} revisionContext={revisionContext}/>
                        </ModalPortal>
                        <ModalPortal>
                            <ConfirmationModal isOpen={confirmationModal.isOpen} message={confirmationModal.message} onConfirm={confirmationModal.onConfirm} onCancel={confirmationModal.onCancel} />
                        </ModalPortal><AddVacancyModal
  isOpen={isAddVacancyModalOpen}
  onClose={() => setIsAddVacancyModalOpen(false)}
  onConfirm={isGerenteViewer ? handleConfirmVacancyRequest : handleConfirmAddVacancy}
  createDirectly={layoutDataNodes.length === 0 || viewMode === 'list'}
  isCreating={isCreatingVacancies}
  existingAreas={areas}
  parentNode={focusedNodeId ? layoutDataNodes.find(n => n.id === focusedNodeId) : null}
  canEdit={canEdit}
  isGerenteViewer={isGerenteViewer}
  managerAreaId={isGerenteViewer && user?.areaid ? user.areaid : null}  // ADICIONAR ESTA LINHA
  dicionarioCompetencias={dicionarioCompetencias}
  onCreateArea={handleCreateAreaForModal}
  onUpdateArea={handleUpdateArea}
  token={token}
  handleFetchError={handleFetchError}
  showConfirmation={showConfirmation}
  areas={areas}
  categories={categories}
  revisionContext={revisionContext}
/><ModalPortal>
    <PendingRequestsModal
        isOpen={isPendingModalOpen}
        onClose={() => setIsPendingModalOpen(false)}
        requests={pendingRequests}
        nodes={nodes}
        isLoading={isLoadingPending}
        token={token}
        handleFetchError={handleFetchError}
        onApprove={handleApproveRequest}
        onReject={handleRejectRequest}
    /> </ModalPortal>
                    <ModalPortal>
                        <JobDescriptionModal
                                isOpen={isJobDescriptionModalOpen}
                               onClose={() => {
    setIsJobDescriptionModalOpen(false);
    setJdEditMode('normal');
    
}}
                                node={selectedNode}
                                onSave={handleSaveJobDetails}
                                canEdit={canEdit}
                                isGerenteViewer={isGerenteViewer}
                                dicionarioCompetencias={dicionarioCompetencias}
                                token={token}
                                showConfirmation={showConfirmation}
                                handleFetchError={handleFetchError}
                                areas={areas} // <-- *** ADICIONE/CONFIRME ESTA LINHA ***
                                categories={categories}
                                revisionContext={revisionContext}
                            />
                            </ModalPortal>
                           {/* Exemplo de Ficha de Avaliação */}
{/* --- MODAL DE AVALIAÇÃO (Ajustado) --- */}
{/* --- MODAL DA FICHA DE AVALIAÇÃO --- */}
<ModalPortal>
 <EvaluationSheetModal
    isOpen={isEvaluationSheetModalOpen}
    onClose={() => {
        setIsEvaluationSheetModalOpen(false);
        setRevisionContext(null);
    }}
    node={selectedNode}
    dicionarioCompetencias={dicionarioCompetencias}
    canEdit={canEvaluate}
    onSave={handleSaveEvaluation}
    onOpenResults={handleOpenResults}
    isGerenteViewer={isGerenteViewer}
    isAdminOrEditor={isAdminOrEditor}
    setJdEditMode={setJdEditMode}
    setSelectedNode={setSelectedNode}
    setIsEvaluationSheetModalOpen={setIsEvaluationSheetModalOpen}
    setIsJobDescriptionModalOpen={setIsJobDescriptionModalOpen}
    step={step}
    setStep={setStep}
    evaluations={evaluations}
    setEvaluations={setEvaluations}
    period={period}
    setPeriod={setPeriod}
    selectedCompetencies={selectedCompetencies}
    setSelectedCompetencies={setSelectedCompetencies}
    availableCompetencies={availableCompetencies}
    setAvailableCompetencies={setAvailableCompetencies} // ✨ Conecta o fio faltante
    revisionContext={revisionContext}
    setRevisionContext={setRevisionContext}             // ✨ Conecta o fio faltante
    handleRequestEvalRevision={handleRequestEvalRevision}
    handleStartEvalRevision={handleStartEvalRevision}
    promptConfig={promptConfig}                         // ✨ Passa o estado pro filho
    setPromptConfig={setPromptConfig}                   // ✨ Passa a função pro filho
    isPromptOpen={isPromptOpen}                         // ✨ Passa o estado pro filho
    setIsPromptOpen={setIsPromptOpen}                   // ✨ Passa a função pro filho
/>
</ModalPortal>



{/* --- MODAL DE GESTOR (Minhas Avaliações) --- */}
{isManagerEvaluationsModalOpen && (
    <ModalPortal>
        <ManagerEvaluationsModal 
            isOpen={isManagerEvaluationsModalOpen}
            onClose={() => setIsManagerEvaluationsModalOpen(false)} // ✅ Resolve erro do terminal
            onEvaluate={handleManagerEvaluate}
            onViewResult={handleManagerViewResult}
            token={token}
            handleFetchError={handleFetchError}
        />
    </ModalPortal>
)}

                        <ModalPortal>
                        <EvaluationResultModal
                            isOpen={isEvaluationResultModalOpen}
                            onClose={() => setIsEvaluationResultModalOpen(false)}
                            node={selectedNode}
                            dicionarioCompetencias={dicionarioCompetencias}
                        />
                        </ModalPortal>
                        <ModalPortal>
                        <DisciplinaryHistoryModal
                            isOpen={isDisciplinaryHistoryModalOpen}
                            onClose={() => setIsDisciplinaryHistoryModalOpen(false)}
                            node={selectedNode}
                            canEdit={canEdit}
                            onSave={handleSaveDisciplinaryHistory}
                            dicionarioCompetencias={dicionarioCompetencias}
                            scriptsLoaded={scriptsLoaded}
                        />
                        </ModalPortal>

                        <ModalPortal>
                        <PDIModal
                            isOpen={isPDIModalOpen}
                            onClose={() => setIsPDIModalOpen(false)}
                            node={selectedNode}
                            dicionarioCompetencias={dicionarioCompetencias}
                            canEdit={canEdit}
                            onSave={handleSavePDI}
                            token={token}
                            handleFetchError={handleFetchError}
                        />
                        </ModalPortal>


                        <div style={{ position: 'relative', flex: '1 0 280px', minHeight: 280, minWidth: 1 }}>
                        {/* O canvas permanece montado e dimensionado para conservar o zoom. */}
                        <div id="gpc-organogram-panel" role="region" aria-label="Organograma da equipe" aria-hidden={viewMode !== 'organogram'} ref={reactFlowWrapper}
                            style={{ ...reactFlowWrapperStyle, position: 'absolute', inset: 0, visibility: viewMode === 'organogram' ? 'visible' : 'hidden', pointerEvents: viewMode === 'organogram' ? 'auto' : 'none' }}>
                            <div style={reactFlowInnerStyle}>
                                <SizedOrganogramFlow
                                    nodes={nodes}
                                    edges={edges}
                                    onNodesChange={handleNodesChange}
                                    onEdgesChange={handleEdgesChange}
                                    onConnect={onConnect}
                                    onNodeClick={handleNodeClick}
                                    onPaneClick={onPaneClick}
                                    onDrop={onDrop}
                                    onDragOver={onDragOver}
                                    onNodeDragStop={handleNodeDragStop}
                                    defaultEdgeOptions={defaultEdgeOptions}
                                    minZoom={0.05}
                                    onEdgeMouseEnter={onEdgeMouseEnter}
                                    onEdgeMouseLeave={onEdgeMouseLeave}
                                    onEdgeClick={onEdgeClick}
                                    nodesDraggable={canEdit}
                                    nodesConnectable={canEdit}
                                    elementsSelectable={canEdit}
                                    onMoveStart={handleMoveStart}
                                >
                                    <Controls />
                                    <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color="#9ca3af" />
                                </SizedOrganogramFlow>
                            </div>
                            {isTeamLoading && <div className="gpc-people-empty" role="status" style={{ position: 'absolute', inset: 0, zIndex: 5, background: '#f8fafcf5' }}>
                                <span className="gpc-people-loading" aria-hidden="true" /><p>Carregando equipe…</p>
                            </div>}
                            {!isTeamLoading && organogramError && <div role="alert" style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 5, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: 10, padding: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                                <span style={{ flex: 1, fontSize: 13 }}>{organogramError}</span>
                                <button type="button" className="gpc-people-action" onClick={() => fetchOrganogramData(selectedUnitId)}>Tentar novamente</button>
                            </div>}
                            {!isTeamLoading && !organogramError && layoutDataNodes.length === 0 && <div className="gpc-people-empty" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                                <PeopleViewIcon name="people" size={36} />
                                <h3>Esta unidade ainda não tem vagas</h3>
                                <p>{canEdit ? 'Crie a primeira vaga e depois selecione o colaborador ou informe o nome dele.' : 'As vagas e os colaboradores aparecerão aqui quando forem definidos.'}</p>
                                {canEdit && selectedUnitId && <button type="button" className="gpc-people-action" style={{ pointerEvents: 'auto' }} disabled={isCreatingVacancies} onClick={handleOpenAddVacancyModal}>Adicionar primeiro colaborador</button>}
                            </div>}
                        </div>
                        <section id="gpc-collaborators-panel" aria-label="Lista de colaboradores" style={{ position: 'absolute', inset: 0, display: viewMode === 'list' ? 'block' : 'none' }}>
                            <CollaboratorListView
                                visibleNodes={listScopeNodes} allNodes={layoutDataNodes} allEdges={layoutDataEdges}
                                unitId={selectedUnitId} scopeKey={JSON.stringify([selectedUnitId, selectedFilterAreas, showDescendants])}
                                hasAreaFilter={selectedFilterAreas.length > 0} loading={isTeamLoading} error={organogramError}
                                onRetry={() => fetchOrganogramData(selectedUnitId)}
                                onClearFilters={() => { setSelectedFilterAreas([]); setShowDescendants(false); }}
                                onShowOrganogram={handleShowInOrganogram} onOpenActions={handleListActions}
                                onOpenDetails={handleListDetails} activeNodeId={activeDropdown?.nodeId}
                                canEdit={canEdit && Boolean(selectedUnitId)} onCreateVacancy={handleOpenAddVacancyModal}
                            />
                        </section>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // Wrapper para o componente principal do organograma com ReactFlowProvider
    const OrganogramPageWrapper = () => (
        <ReactFlowProvider>
            <OrganogramFlow />
        </ReactFlowProvider>
    );

    export default OrganogramPageWrapper;
